import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Sparkles, History, Search, FileText, AlertCircle, 
  HelpCircle, RefreshCw, Layers, ArrowLeft, Languages,
  Settings, Lock, Shield, ShieldCheck, CheckCircle, CheckCircle2, Plus, Coins, Zap, Building2,
  Monitor, Laptop, Mail, Key, Bot, Globe, Satellite, Flame, Info, ChevronDown, ChevronUp,
  LogOut, UserCheck, Radar, Cpu, Terminal
} from 'lucide-react';
import { ResearchSession, ResearchOptions, ResearchStep, ResearchReport, ReportTemplate } from './types';
import ResearchForm from './components/ResearchForm';
import ResearchProgress from './components/ResearchProgress';
import ReportView from './components/ReportView';
import ArchiveView from './components/ArchiveView';
import TemplateManager from './components/TemplateManager';
import DataAnalyzer from './components/DataAnalyzer';
import SettingsView from './components/SettingsView';
import HubView from './components/HubView';
import { DesktopAppStoreModal } from './components/DesktopAppStoreModal';
import { DeepBrainOsShowcaseModal } from './components/DeepBrainOsShowcaseModal';
import DocumentCorrector from './components/DocumentCorrector';
import VideoRenderer from './components/VideoRenderer';
import ContractAuditor from './components/ContractAuditor';
import EuCounterpartyAuditor from './components/EuCounterpartyAuditor';
import AmlAuditor from './components/AmlAuditor';
import TenderAnalyst from './components/TenderAnalyst';
import TenderPackageGeneratorView from './components/TenderPackageGeneratorView';
import LegalDocCreator from './components/LegalDocCreator';
import CourtCompliance from './components/CourtCompliance';
import DstuOrderConstructor from './components/DstuOrderConstructor';
import EnterpriseBlankDocumentAgentView from './components/EnterpriseBlankDocumentAgentView';
import MilitaryCodifierAgentView from './components/MilitaryCodifierAgentView';
import NcuvkzUniversalConsultantView from './components/NcuvkzUniversalConsultantView';
import { NcuvkzFinMessengerView } from './components/NcuvkzFinMessengerView';
import HrCompliance from './components/HrCompliance';
import CommercialContractAuditor from './components/CommercialContractAuditor';
import { EnterpriseDashboard } from './components/EnterpriseDashboard';
import { WorkspaceSwitcher, WorkspaceMode } from './components/WorkspaceSwitcher';
import PortalLoadingSkeleton from './components/PortalLoadingSkeleton';
const OfficePortalLayout = React.lazy(() => import('./layouts/OfficePortalLayout').then(m => ({ default: m.OfficePortalLayout })));
const SpacePortalLayout = React.lazy(() => import('./layouts/SpacePortalLayout').then(m => ({ default: m.SpacePortalLayout })));
import TaxFinancialAnalyst from './components/TaxFinancialAnalyst';
import MarketIntelligence from './components/MarketIntelligence';
import SalesProductOptimizer from './components/SalesProductOptimizer';
import GrantWriter from './components/GrantWriter';
import CustomsCompliance from './components/CustomsCompliance';
import IpGuard from './components/IpGuard';
import ItAuditor from './components/ItAuditor';
import StartupPitchBuilder from './components/StartupPitchBuilder';
import NewsAnalyst from './components/NewsAnalyst';
import AgriLandConsultant from './components/AgriLandConsultant';
import PublicAccountantKekv from './components/PublicAccountantKekv';
import BudgetDraftingAgentView from './components/BudgetDraftingAgentView';
import EsgEcoAuditor from './components/EsgEcoAuditor';
import SatelliteEcoAnalyst from './components/SatelliteEcoAnalyst';
import SpaceSatAgentView from './components/SpaceSatAgentView';
import SpaceCenterHubView, { SPACE_AGENT_IDS } from './components/SpaceCenterHubView';
import TaxCenterHubView, { TAX_AGENT_IDS } from './components/TaxCenterHubView';
import BusinessCenterHubView, { BUSINESS_AGENT_IDS } from './components/BusinessCenterHubView';
import BusinessAgentView from './components/BusinessAgentView';
import EnergyCenterHubView, { ENERGY_AGENT_IDS } from './components/EnergyCenterHubView';
import EnergyAgentView from './components/EnergyAgentView';
import CircuitSchematicAgentView from './components/CircuitSchematicAgentView';
import TaxAgentView from './components/TaxAgentView';
import TaxPnUnblockerAgentView from './components/TaxPnUnblockerAgentView';
import AgriCenterHubView, { AGRI_AGENT_IDS } from './components/AgriCenterHubView';
import AgroDeepResearchHubView from './components/AgroDeepResearchHubView';
import AgriAgentView from './components/AgriAgentView';
import LogisticsCenterHubView, { LOGISTICS_AGENT_IDS } from './components/LogisticsCenterHubView';
import LogisticsAgentView from './components/LogisticsAgentView';
import FolkHealerAgentView from './components/FolkHealerAgentView';
import CyberCenterHubView, { CYBER_AGENT_IDS } from './components/CyberCenterHubView';
import CyberAgentView from './components/CyberAgentView';
import ConstructionCenterHubView, { CONSTRUCTION_AGENT_IDS } from './components/ConstructionCenterHubView';
import ConstructionAgentView from './components/ConstructionAgentView';
import ScienceCenterHubView, { SCIENCE_AGENT_IDS } from './components/ScienceCenterHubView';
import ScienceAgentView from './components/ScienceAgentView';
import PharmaCenterHubView, { PHARMA_AGENT_IDS } from './components/PharmaCenterHubView';
import PolandCenterHubView, { POLAND_AGENT_IDS } from './components/PolandCenterHubView';
import EuCenterHubView, { EU_AGENT_IDS } from './components/EuCenterHubView';
import UsCenterHubView, { US_AGENT_IDS } from './components/UsCenterHubView';
import FranceCenterHubView, { FRANCE_AGENT_IDS } from './components/FranceCenterHubView';
import GermanyCenterHubView, { GERMANY_AGENT_IDS } from './components/GermanyCenterHubView';
import EcoCenterHubView, { ECO_AGENT_IDS } from './components/EcoCenterHubView';
import CrossBorderCenterHubView, { CROSS_BORDER_AGENT_IDS } from './components/CrossBorderCenterHubView';
import UkraineCenterHubView, { UKRAINE_AGENT_IDS } from './components/UkraineCenterHubView';
import OfficeCenterHubView, { OFFICE_AGENT_IDS } from './components/OfficeCenterHubView';
import OfficeSpreadsheetAgentView from './components/OfficeSpreadsheetAgentView';
import MeetingTranscriberView from './components/MeetingTranscriberView';
import AdminCenterHubView, { ADMIN_AGENT_IDS } from './components/AdminCenterHubView';
import AdminAssistantConsole from './components/AdminAssistantConsole';
import { AdminTrembitaMeshProject } from './components/AdminTrembitaMeshProject';
import EnterpriseBrainConsole from './components/EnterpriseBrainConsole';
import CorporateOfficeInfoModal from './components/CorporateOfficeInfoModal';
import PhdCenterHubView, { PHD_AGENT_IDS } from './components/PhdCenterHubView';
import PhdAgentView from './components/PhdAgentView';
import NonverbalProfilerAgentView from './components/NonverbalProfilerAgentView';
import PharmaAdvisorAgentView from './components/PharmaAdvisorAgentView';
import AdminLeadHarvester from './components/AdminLeadHarvester';
import DocumentTranslator from './components/DocumentTranslator';
import PropertyValuator from './components/PropertyValuator';
import DelovodstvoAuditor from './components/DelovodstvoAuditor';
import MdToDocxConverter from './components/MdToDocxConverter';
import DynamicOnTheFlyAgentView, { DynamicAgentConfig } from './components/DynamicOnTheFlyAgentView';
import { PublicPricingModal } from './components/PublicPricingModal';
import SmartAgentDispatcher from './components/SmartAgentDispatcher';
import InteractiveShowcasePreview from './components/InteractiveShowcasePreview';
import SituationalCenterShowcase from './components/SituationalCenterShowcase';
import { TelegramBotsShowcase } from './components/TelegramBotsShowcase';
import QuickDemoTourModal from './components/QuickDemoTourModal';
import { InnovationHubView } from './components/InnovationHubView';
import LegislationTracker from './components/LegislationTracker';
import agentsConfig from './agentsConfig.json';
import { DEFAULT_TEMPLATES_UK, DEFAULT_TEMPLATES_EN } from './defaultTemplates';
import { trackConversion, trackEvent } from './utils/analytics';
import { formatPriceWithCurrency, Currency } from './utils/i18n';
const LOGIN_I18N = {
  uk: {
    brandTitle: "Звільніть до 15 годин щотижня",
    brandSubtitle: "Експертний аудит договорів, кошторисів та декларацій за 45 секунд",
    quote: "«Повний операційний центр вашої компанії: від аудиту договорів за 45 секунд до розрахунку кошторисів за ДБН та геоаналітики.»",
    valueMetricsTitle: "Чому це вигідно вашому бізнесу:",
    metric1Title: "45 секунд замість 4 год",
    metric1Desc: "Миттєвий аудит договорів, кошторисів та звітів.",
    metric2Title: "Економія до 90%",
    metric2Desc: "40+ спеціалізованих модулів за ціною однієї підписки.",
    metric3Title: "100% Комплаєнс",
    metric3Desc: "Перевірка Санкцій РНБО, ДПС, ДСТУ та ПКМУ №55.",
    metric4Title: "Web & Telegram 24/7",
    metric4Desc: "Робота у чаті Telegram або розширеному веб-інтерфейсі.",
    hubsTitle: "6 Спеціалізованих Операційних Хабів:",
    hub1Title: "Юридичний & Корпоративний Хаб",
    hub1Desc: "Аудит комерційних договорів за 1 хвилину, виявлення пасток, перевірка контрагентів за реєстрами Санкцій РНБО & AML, діловодство ДСТУ 4163:2020, судовий та кадровий комплаєнс.",
    hub2Title: "Податково-Бухгалтерський Центр",
    hub2Desc: "Контроль та перевірка декларацій ДПС, розмежування КЕКВ (2210 / 3110) для бюджетних установ, оцінка майна та аналіз фінансового стану.",
    hub3Title: "Будівельно-Кошторисний Центр",
    hub3Desc: "Перетворення дефектних актів на кошториси за актуальними ДБН, авто-підбір шифрів норм, розрахунок людино-годин та експорт у Excel.",
    hub4Title: "Енергетичний Хаб & ESG-Аудит",
    hub4Desc: "Енергоменеджер Підприємства (ISO 50001), проектування ВДЕ (СЕС/ВЕС/Microgrid), аналіз АСКОЕ/ЛУЗОД та оцінка сталого розвитку (ESG).",
    hub5Title: "Космічний & Геопросторовий Центр (GEOINT / ДЗЗ)",
    hub5Desc: "Оцінка воєнних руйнувань за супутниковими знімками, спецконтроль (CTBTO, сейсміка), RTK/GNSS навігація та метрологія UTC(UA).",
    hub6Title: "Deep Intelligence & Бізнес-Консалтинг",
    hub6Desc: "Глибокі веб-дослідження у реальному часі (2026), цілодобовий моніторинг новин з дайджестами у Telegram/Email, B2B sales та HR-скринінг.",
    securityTitle: "Безпека та On-Premise для Enterprise:",
    securityItem1: "Приватні бази знань: Повна ізоляція корпоративних даних у захищеній базі.",
    securityItem2: "On-Premise варіант: Розгортання на власних серверах компанії чи держустанови.",
    securitySystemActive: "СИСТЕМА БЕЗПЕКИ: АКТИВНА",
    version: "ВЕРСІЯ: 2.5.0",
    loginCardTitlePrefix: "Вхід до",
    loginCardTitleHighlight: "Екосистеми Deep Research Hub",
    loginCardSubtitle: "АВТОРИЗАЦІЯ КОРИСТУВАЧА",
    loginNotice: "Для роботи з вашим Цифровим двійником та доступу до аналітичних інструментів і модулів, будь ласка, оберіть зручний спосіб входу:",
    offerCheckboxPrefix: "Я ознайомлений(-а) та згоден(-а) з умовами",
    offerLink: "Публічної оферти",
    offerCheckboxSuffix: "та даю згоду на обробку моїх персональних даних.",
    pricingBtn: "Модулі та розцінки",
    googleLoginBtn: "Увійти через Google (1 клік)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Останній вхід",
    loginTabGoogle: "⚡ Google (1 клік)",
    loginTabEmail: "✉️ Вхід за Email кодом",
    loginTabGuest: "🚀 Без реєстрації (Гість)",
    emailInputLabel: "Робочий або особистий Email",
    emailInputPlaceholder: "director@company.com або name@gmail.com",
    sendCodeBtn: "Отримати 6-значний код входу",
    sendingCode: "Надсилаємо код...",
    codeSentNotice: "Код надіслано на вашу пошту",
    codeSentDesc: "Перевірте поштову скриньку (включно зі Спамом) та введіть 6 цифр:",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Увійти в систему",
    verifyingCode: "Перевіряємо код...",
    changeEmailBtn: "← Змінити пошту",
    resendCodeBtn: "Надіслати код повторно",
    inAppBrowserNoticeTitle: "📱 Відкрито у мобільному додатку (Telegram / Viber)",
    inAppBrowserNoticeDesc: "Google блокує вхід усередині месенджерів. Скористайтеся входом за Email кодом або запустіть Гостьовий режим у 1 клік.",
    guestAccessTitle: "Миттєвий гостьовий запуск",
    guestAccessDesc: "Спробуйте будь-який модуль прямо зараз без введення пошти та паролів (1 безкоштовний повний запит).",
    guestAccessBtn: "🚀 Увійти як Гість (1 клік)",
    trustBadge1: "🔒 100% Конфіденційність: Комерційні дані ізольовані",
    trustBadge2: "⚡ Без банківської карти • 1-й запуск безкоштовно",
    trustBadge3: "🛡️ GDPR (EU) & ISO 27001 Банківський рівень захисту",
    freeTestTitle: "Безкоштовний тестовий доступ:",
    freeTestDesc: "Кожен новий користувач отримує можливість зробити по 1 безкоштовному запиту до кожного спеціалізованого модуля. Після використання цього тесту ви можете придбати місячну підписку на пакет запитів або поповнити баланс кабінету для оплати разових запусків (Pay-per-request).",
    syncTipTitle: "Для ознайомлення:",
    syncTipDesc: "Платформа автоматично синхронізує ваші завантажені статути з Telegram-ботом, формує регламентні To-Do календарі та нагадує про звіти.",
    howToStartTitle: "Як почати?",
    howToStartStep1: "Зареєструйте компанію у веб-кабінеті за 2 хвилини.",
    howToStartStep2: "Завантажте базові документи (статут, реквізити).",
    howToStartStep3: "Підключіть Telegram-бота — і ваш Цифровий двійник готовий до роботи!",
    offerModalTitle: "Договір публічної оферти",
    offerModalAcceptBtn: "Прийняти та погодитись",
    offerModalCloseBtn: "Закрити",
    pricingModalBtnText: "Тарифи"
  },
  en: {
    brandTitle: "Save up to 15 Hours Weekly",
    brandSubtitle: "Instant Expert Audits of Contracts, Cost Estimates & Compliance in 45 Seconds",
    quote: "“Full operational center for your enterprise: from contract audits in 45 seconds to construction cost estimates and geospatial analytics.”",
    valueMetricsTitle: "Why it delivers immense value to your business:",
    metric1Title: "45 sec instead of 4 hrs",
    metric1Desc: "Instant audit of contracts, estimates and analytical reports.",
    metric2Title: "Save up to 90%",
    metric2Desc: "40+ specialized modules for the cost of a single subscription.",
    metric3Title: "100% Compliance",
    metric3Desc: "Sanctions screening, Tax regulations, ISO & Government norms.",
    metric4Title: "Web & Telegram 24/7",
    metric4Desc: "Seamless operation in Telegram chat or advanced Web UI.",
    hubsTitle: "6 Specialized Operational Hubs:",
    hub1Title: "Legal & Corporate Hub",
    hub1Desc: "Commercial contract audit in 1 minute, trap clause detection, Sanctions & AML counterparty checks, office records, judicial & HR compliance.",
    hub2Title: "Tax & Accounting Center",
    hub2Desc: "Tax declaration review, budget expenditure categorization, asset evaluation and comprehensive financial health analytics.",
    hub3Title: "Construction & Cost-Estimation Hub",
    hub3Desc: "Transform defect acts into construction cost estimates, automated rate code matching, man-hour calculation and Excel export.",
    hub4Title: "Energy Hub & ESG Audit",
    hub4Desc: "Enterprise Energy Manager (ISO 50001), renewable energy design (Solar/Wind/Microgrid), smart telemetry and ESG sustainability audit.",
    hub5Title: "Space & Geospatial Center (GEOINT)",
    hub5Desc: "Satellite damage assessment, CTBTO/seismic monitoring, precision RTK/GNSS navigation and UTC time metrology.",
    hub6Title: "Deep Intelligence & Business Consulting",
    hub6Desc: "Real-time deep web research (2026), 24/7 automated news intelligence via Telegram/Email, B2B sales discovery and candidate screening.",
    securityTitle: "Enterprise Security & On-Premise:",
    securityItem1: "Private knowledge bases: Complete corporate data isolation in dedicated encrypted store.",
    securityItem2: "On-Premise deployment: Available for private company or government internal servers.",
    securitySystemActive: "SECURITY SYSTEM: ACTIVE",
    version: "VERSION: 2.5.0",
    loginCardTitlePrefix: "Sign in to",
    loginCardTitleHighlight: "Deep Research Hub Ecosystem",
    loginCardSubtitle: "USER AUTHENTICATION",
    loginNotice: "To access your Enterprise Digital Twin and specialized AI analytical modules, choose your preferred sign-in method:",
    offerCheckboxPrefix: "I have read and agree to the terms of the",
    offerLink: "Public Offer",
    offerCheckboxSuffix: "and consent to personal data processing.",
    pricingBtn: "Modules & Pricing",
    googleLoginBtn: "Sign in with Google (1-Click)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Last used",
    loginTabGoogle: "⚡ Google (1-Click)",
    loginTabEmail: "✉️ Email Code (Passwordless)",
    loginTabGuest: "🚀 Instant Guest Access",
    emailInputLabel: "Work or personal Email",
    emailInputPlaceholder: "director@company.com or name@gmail.com",
    sendCodeBtn: "Get 6-digit Verification Code",
    sendingCode: "Sending code...",
    codeSentNotice: "Verification code sent to your email",
    codeSentDesc: "Check your inbox (including Spam folder) and enter 6 digits below:",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Verify & Sign In",
    verifyingCode: "Verifying code...",
    changeEmailBtn: "← Change Email",
    resendCodeBtn: "Resend code",
    inAppBrowserNoticeTitle: "📱 Mobile In-App Browser detected (Telegram / Viber)",
    inAppBrowserNoticeDesc: "Google OAuth blocks logins in in-app browsers. Please use Email OTP code below or start instant Guest Mode in 1 click.",
    guestAccessTitle: "Instant Guest Trial",
    guestAccessDesc: "Test any specialized AI module right now without entering email or passwords (1 free full run).",
    guestAccessBtn: "🚀 Enter as Guest (1-Click)",
    trustBadge1: "🔒 100% Confidentiality: Enterprise Data Isolation",
    trustBadge2: "⚡ No credit card required • 1st run free",
    trustBadge3: "🛡️ GDPR & ISO 27001 Compliant",
    freeTestTitle: "Free Trial Access:",
    freeTestDesc: "Every new user gets 1 free trial request for each specialized module. Afterwards, you can subscribe to a monthly package or top up balance for pay-per-request single runs.",
    syncTipTitle: "Quick Note:",
    syncTipDesc: "The platform automatically synchronizes your uploaded corporate documents with the Telegram bot, schedules To-Do calendars, and delivers reports.",
    howToStartTitle: "How to get started?",
    howToStartStep1: "Register your enterprise profile in 2 minutes.",
    howToStartStep2: "Upload core documents (articles of association, company data).",
    howToStartStep3: "Connect the Telegram bot — and your Digital Twin is ready!",
    offerModalTitle: "Public Offer Agreement",
    offerModalAcceptBtn: "Accept & Agree",
    offerModalCloseBtn: "Close",
    pricingModalBtnText: "Pricing"
  },
  fr: {
    brandTitle: "Jumeau Numérique d'Entreprise",
    brandSubtitle: "Écosystème de Hubs Sectoriels Spécialisés",
    quote: "« Le centre opérationnel complet de votre entreprise : de l'audit de contrats en 45 secondes aux estimations de coûts de construction et à la géo-analytique. »",
    valueMetricsTitle: "Pourquoi c'est hautement rentable pour votre entreprise :",
    metric1Title: "45 sec au lieu de 4 h",
    metric1Desc: "Audit instantané des contrats, devis et rapports analytiques.",
    metric2Title: "Jusqu'à 90% d'économie",
    metric2Desc: "Plus de 40 modules spécialisés au tarif d'un abonnement unique.",
    metric3Title: "100% de Conformité",
    metric3Desc: "Vérification des sanctions, réglementations fiscales, ISO et marchés publics.",
    metric4Title: "Web & Telegram 24/7",
    metric4Desc: "Utilisation fluide sur Telegram ou dans l'interface Web avancée.",
    hubsTitle: "Hubs Opérationnels Spécialisés :",
    hub1Title: "Hub Juridique & Corporate",
    hub1Desc: "Audit des contrats commerciaux en 1 minute, détection des clauses pièges, vérification des sanctions et AML, conformité sociale et RGPD.",
    hub2Title: "Centre Fiscal & Comptable",
    hub2Desc: "Contrôle des déclarations fiscales (Factur-X / TVA / IS), catégorisation budgétaire, valorisation d'actifs et analyse financière.",
    hub3Title: "Centre BTP & Estimation des Coûts",
    hub3Desc: "Conversion des constats de désordres en devis BTP, conformité CCAG/RE2020, calcul des heures et export Excel.",
    hub4Title: "Hub Énergie & Audit ESG",
    hub4Desc: "Gestion de l'énergie (ISO 50001), conception EnR (Solaire/Éolien/Microgrid), télérelève et Bilan Carbone ADEME (ESG).",
    hub5Title: "Centre Spatial & Géospatial (GEOINT)",
    hub5Desc: "Évaluation des dommages par satellite, surveillance sismique CTBTO, navigation de précision RTK/GNSS et métrologie temporelle.",
    hub6Title: "Deep Intelligence & Conseil Stratégique",
    hub6Desc: "Recherche approfondie sur le web en temps réel (2026), veille informationnelle 24/7 par Telegram/Email, prospection B2B et screening RH.",
    securityTitle: "Sécurité d'Entreprise & Déploiement On-Premise :",
    securityItem1: "Bases de connaissances privées : Isolation complète des données d'entreprise dans un stockage chiffré dédié.",
    securityItem2: "Déploiement On-Premise : Installation possible sur vos propres serveurs d'entreprise ou d'institution publique.",
    securitySystemActive: "SYSTÈME DE SÉCURITÉ : ACTIF",
    version: "VERSION : 2.5.0",
    loginCardTitlePrefix: "Connexion à",
    loginCardTitleHighlight: "l'Écosystème Deep Research Hub",
    loginCardSubtitle: "AUTHENTIFICATION UTILISATEUR",
    loginNotice: "Pour accéder à votre Jumeau Numérique d'Entreprise, veuillez choisir votre méthode d'accès :",
    offerCheckboxPrefix: "J'ai pris connaissance et j'accepte les conditions de l'",
    offerLink: "Offre Publique & CGU",
    offerCheckboxSuffix: "et je consens au traitement de mes données personnelles.",
    pricingBtn: "Modules & Tarifs",
    googleLoginBtn: "Se connecter avec Google (1 clic)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Dernière connexion",
    loginTabGoogle: "⚡ Google (1 clic)",
    loginTabEmail: "✉️ Code par E-mail",
    loginTabGuest: "🚀 Accès Invité (Sans inscription)",
    emailInputLabel: "E-mail professionnel ou personnel",
    emailInputPlaceholder: "direction@entreprise.fr ou nom@gmail.com",
    sendCodeBtn: "Obtenir le code à 6 chiffres",
    sendingCode: "Envoi du code...",
    codeSentNotice: "Code envoyé à votre adresse e-mail",
    codeSentDesc: "Vérifiez votre boîte de réception (y compris les spams) et saisissez les 6 chiffres :",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Vérifier et Se Connecter",
    verifyingCode: "Vérification du code...",
    changeEmailBtn: "← Modifier l'e-mail",
    resendCodeBtn: "Renvoyer le code",
    inAppBrowserNoticeTitle: "📱 Navigateur intégré détecté (Telegram / Viber)",
    inAppBrowserNoticeDesc: "Google bloque la connexion dans les applications. Utilisez le code par e-mail ci-dessous ou le mode Invité en 1 clic.",
    guestAccessTitle: "Essai Invité Instantané",
    guestAccessDesc: "Testez n'importe quel module IA dès maintenant sans saisir d'e-mail ni de mot de passe (1 essai complet gratuit).",
    guestAccessBtn: "🚀 Démarrer en mode Invité (1 clic)",
    trustBadge1: "🔒 Confidentialité 100% : Données isolées et chiffrées",
    trustBadge2: "⚡ Sans carte bancaire • 1er essai 100% gratuit",
    trustBadge3: "🛡️ Conforme RGPD & ISO 27001",
    freeTestTitle: "Accès d'essai gratuit :",
    freeTestDesc: "Chaque nouvel utilisateur bénéficie d'une requête d'essai gratuite par module spécialisé. Vous pouvez ensuite souscrire un forfait mensuel ou recharger votre solde pour des analyses à l'usage (Pay-per-request).",
    syncTipTitle: "Bon à savoir :",
    syncTipDesc: "La plateforme synchronise automatiquement vos statuts et documents avec le bot Telegram, génère les plannings To-Do et envoie les rapports.",
    howToStartTitle: "Comment démarrer ?",
    howToStartStep1: "Enregistrez le profil de votre entreprise en 2 minutes.",
    howToStartStep2: "Téléversez vos documents de base (statuts, coordonnées).",
    howToStartStep3: "Connectez le bot Telegram — et votre Jumeau Numérique est opérationnel !",
    offerModalTitle: "Conditions Générales & Offre Publique",
    offerModalAcceptBtn: "Accepter et continuer",
    offerModalCloseBtn: "Fermer",
    pricingModalBtnText: "Tarifs"
  },
  de: {
    brandTitle: "Digitaler Unternehmenszwilling",
    brandSubtitle: "Ökosystem Spezialisierter Branchen-Hubs",
    quote: "„Das vollständige operative Zentrum Ihres Unternehmens: von der Vertragsprüfung in 45 Sekunden bis zur Baukostenermittlung nach DIN 276 und Geoanalytik.“",
    valueMetricsTitle: "Warum es Ihrem Unternehmen maximalen Mehrwert bietet:",
    metric1Title: "45 Sek. statt 4 Std.",
    metric1Desc: "Sofortige Tiefenprüfung von Verträgen, Kostenkalkulationen und Fachberichten.",
    metric2Title: "Bis zu 90% Ersparnis",
    metric2Desc: "Über 40 spezialisierte Module zum Preis eines einzigen Abonnements.",
    metric3Title: "100% Compliance",
    metric3Desc: "Prüfung nach Sanktionslisten, Steuergesetzen, BGB/HGB und DIN/ISO-Normen.",
    metric4Title: "Web & Telegram 24/7",
    metric4Desc: "Nahtlose Arbeit im Telegram-Chat oder im erweiterten Web-Portal.",
    hubsTitle: "Spezialisierte Operative Hubs:",
    hub1Title: "Rechts- & Corporate-Hub",
    hub1Desc: "Prüfung von Wirtschaftsverträgen in 1 Minute, Aufdeckung von Fallstricken, Sanktions- & AML-Prüfung, Arbeitsrecht und DSGVO-Compliance.",
    hub2Title: "Steuer- & Rechnungslegungszentrum",
    hub2Desc: "Prüfung von Steuererklärungen (XRechnung / USt / KSt), Haushaltsgliederung, Vermögensbewertung und Bilanzanalyse.",
    hub3Title: "Bau- & Kostenermittlungszentrum",
    hub3Desc: "Umwandlung von Bauschadensprotokollen in VOB/B-Kostenermittlungen nach DIN 276, HOAI-Honorare, Leistungsphasen und Excel-Export.",
    hub4Title: "Energie-Hub & ESG-Audit",
    hub4Desc: "Betriebliches Energiemanagement (ISO 50001), Planung erneuerbarer Energien (PV/Wind/BESS), Smart-Metering und ESG-Nachhaltigkeitsaudit.",
    hub5Title: "Raumfahrt & Geodatenzentrum (GEOINT)",
    hub5Desc: "Satellitengestützte Schadensbewertung, seismisches CTBTO-Monitoring, hochpräzise RTK/GNSS-Navigation und UTC-Zeitmetrologie.",
    hub6Title: "Deep Intelligence & Unternehmensberatung",
    hub6Desc: "Echtzeit-Web-Recherche (2026), automatisierte 24/7-Nachrichtenanalysen per Telegram/E-Mail, B2B-Lead-Generierung und HR-Screening.",
    securityTitle: "Unternehmenssicherheit & On-Premise:",
    securityItem1: "Private Wissensdatenbanken: Vollständige Datenisolation in einer dedizierten verschlüsselten Umgebung.",
    securityItem2: "On-Premise-Bereitstellung: Verfügbar für unternehmenseigene Server oder Behördennetzwerke.",
    securitySystemActive: "SICHERHEITSSYSTEM: AKTIV",
    version: "VERSION: 2.5.0",
    loginCardTitlePrefix: "Anmeldung beim",
    loginCardTitleHighlight: "Deep Research Hub Ökosystem",
    loginCardSubtitle: "BENUTZER-AUTHENTIFIZIERUNG",
    loginNotice: "Wählen Sie Ihre bevorzugte Anmeldemethode, um auf Ihren digitalen Unternehmenszwilling zuzugreifen:",
    offerCheckboxPrefix: "Ich habe die Bedingungen des",
    offerLink: "Öffentlichen Angebots & AGB",
    offerCheckboxSuffix: "gelesen und willige in die Verarbeitung meiner personenbezogenen Daten ein.",
    pricingBtn: "Module & Preise",
    googleLoginBtn: "Mit Google anmelden (1-Klick)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Zuletzt verwendet",
    loginTabGoogle: "⚡ Google (1-Klick)",
    loginTabEmail: "✉️ Login mit E-Mail-Code",
    loginTabGuest: "🚀 Gast-Zugang (Ohne Anmeldung)",
    emailInputLabel: "Geschäftliche oder private E-Mail",
    emailInputPlaceholder: "geschaeftsfuehrung@firma.de oder name@gmail.com",
    sendCodeBtn: "6-stelligen Bestätigungscode anfordern",
    sendingCode: "Code wird gesendet...",
    codeSentNotice: "Code wurde an Ihre E-Mail gesendet",
    codeSentDesc: "Überprüfen Sie Ihr Postfach (inkl. Spam-Ordner) und geben Sie die 6 Ziffern ein:",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Bestätigen & Anmelden",
    verifyingCode: "Code wird überprüft...",
    changeEmailBtn: "← E-Mail ändern",
    resendCodeBtn: "Code erneut senden",
    inAppBrowserNoticeTitle: "📱 In-App-Browser erkannt (Telegram / Viber)",
    inAppBrowserNoticeDesc: "Google blockiert Anmeldungen in In-App-Browsern. Bitte nutzen Sie den E-Mail-Code unten oder starten Sie den Gast-Modus mit 1 Klick.",
    guestAccessTitle: "Sofortiger Gast-Testlauf",
    guestAccessDesc: "Testen Sie jedes KI-Modul jetzt sofort ohne E-Mail oder Passwort (1 kostenloser vollständiger Test).",
    guestAccessBtn: "🚀 Als Gast starten (1-Klick)",
    trustBadge1: "🔒 100% Vertraulichkeit: Strikte Datenisolation",
    trustBadge2: "⚡ Keine Kreditkarte erforderlich • 1. Test gratis",
    trustBadge3: "🛡️ DSGVO & ISO 27001 Konform",
    freeTestTitle: "Kostenloser Testzugang:",
    freeTestDesc: "Jeder neue Benutzer erhält 1 kostenlose Testabfrage für jedes spezialisierte Modul. Anschließend können Sie ein Monatspaket abonnieren oder Ihr Guthaben für Einzelabfragen aufladen (Pay-per-request).",
    syncTipTitle: "Wissenswertes:",
    syncTipDesc: "Die Plattform synchronisiert Ihre hochgeladenen Dokumente automatisch mit dem Telegram-Bot, erstellt Aufgabenkalender und versendet Berichte.",
    howToStartTitle: "Wie starten Sie?",
    howToStartStep1: "Unternehmensprofil in 2 Minuten anlegen.",
    howToStartStep2: "Basisdokumente (Satzung, Registerdaten) hochladen.",
    howToStartStep3: "Telegram-Bot verknüpfen — und Ihr digitaler Zwilling ist startklar!",
    offerModalTitle: "Vertrag über das öffentliche Angebot",
    offerModalAcceptBtn: "Akzeptieren & Fortfahren",
    offerModalCloseBtn: "Schließen",
    pricingModalBtnText: "Preise"
  },
  pl: {
    brandTitle: "Cyfrowy Bliźniak Przedsiębiorstwa",
    brandSubtitle: "Ekosystem Wyspecjalizowanych Hubów Branżowych",
    quote: "„Kompletne centrum operacyjne Twojej firmy: od audytu umów w 45 sekund po kalkulację kosztorysów i geoanalitykę.”",
    valueMetricsTitle: "Dlaczego to przynosi korzyści Twojej firmie:",
    metric1Title: "45 sekund zamiast 4 godz.",
    metric1Desc: "Błyskawiczny audyt umów, kosztorysów i raportów analitycznych.",
    metric2Title: "Oszczędność do 90%",
    metric2Desc: "40+ wyspecjalizowanych modułów w cenie jednej subskrypcji.",
    metric3Title: "100% Zgodności",
    metric3Desc: "Weryfikacja sankcji, przepisów podatkowych, norm i prawa zamówień.",
    metric4Title: "Web & Telegram 24/7",
    metric4Desc: "Wygodna praca na czacie Telegram lub w zaawansowanym portalu www.",
    hubsTitle: "6 Wyspecjalizowanych Hubów Operacyjnych:",
    hub1Title: "Centrum Prawne i Korporacyjne",
    hub1Desc: "Audyt umów handlowych w 1 minutę, wykrywanie pułapek prawnych, weryfikacja kontrahentów w rejestrach sankcji i AML, zgodność z RODO i prawem pracy.",
    hub2Title: "Centrum Podatkowo-Księgowe",
    hub2Desc: "Weryfikacja deklaracji podatkowych (KSeF / VAT / CIT), klasyfikacja budżetowa, wycena majątku i analiza kondycji finansowej.",
    hub3Title: "Centrum Budowlano-Kosztorysowe",
    hub3Desc: "Przekształcanie protokołów szkód w kosztorysy budowlane, dobór stawek KNR, kalkulacja roboczogodzin i eksport do Excela.",
    hub4Title: "Hub Energetyczny & Audyt ESG",
    hub4Desc: "Zarządzanie energią przedsiębiorstwa (ISO 50001), projektowanie OZE (PV/Wiatr/Microgrid), telemetria licznikowa i audyt zrównoważonego rozwoju (ESG).",
    hub5Title: "Centrum Kosmiczne i Geoprzestrzenne (GEOINT)",
    hub5Desc: "Ocena zniszczeń wojennych ze zdjęć satelitarnych, monitoring sejsmiczny CTBTO, nawigacja precyzyjna RTK/GNSS i metrologia czasu.",
    hub6Title: "Deep Intelligence & Doradztwo Biznesowe",
    hub6Desc: "Głęboki wywiad rynkowy w czasie rzeczywistym, monitoring mediów 24/7 z digestami na Telegram/Email, B2B lead generation i screening HR.",
    securityTitle: "Bezpieczeństwo i On-Premise dla Enterprise:",
    securityItem1: "Prywatne bazy wiedzy: Ścisła izolacja danych firmowych w dedykowanej szyfrowanej bazie.",
    securityItem2: "Wdrożenie On-Premise: Możliwość instalacji na własnych serwerach firmy lub instytucji.",
    securitySystemActive: "SYSTEM BEZPIECZEŃSTWA: AKTYWNY",
    version: "WERSJA: 2.5.0",
    loginCardTitlePrefix: "Logowanie do",
    loginCardTitleHighlight: "Ekosystemu Deep Research Hub",
    loginCardSubtitle: "AUTORYZACJA UŻYTKOWNIKA",
    loginNotice: "Wybierz dogodną metodę logowania, aby uzyskać dostęp do Cyfrowego Bliźniaka:",
    offerCheckboxPrefix: "Zapoznałem(-am) się i akceptuję warunki",
    offerLink: "Regulaminu Ofertowego",
    offerCheckboxSuffix: "oraz wyrażam zgodę na przetwarzanie danych osobowych.",
    pricingBtn: "Moduły i Cennik",
    googleLoginBtn: "Zaloguj się przez Google (1 klik)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Ostatnio używane",
    loginTabGoogle: "⚡ Google (1 klik)",
    loginTabEmail: "✉️ Zaloguj kodem E-mail",
    loginTabGuest: "🚀 Dostęp jako Gość (Bez rejestracji)",
    emailInputLabel: "Służbowy lub prywatny E-mail",
    emailInputPlaceholder: "dyrektor@firma.pl lub imie@gmail.com",
    sendCodeBtn: "Odbierz 6-cyfrowy kod logowania",
    sendingCode: "Wysyłanie kodu...",
    codeSentNotice: "Kod został wysłany na Twój adres e-mail",
    codeSentDesc: "Sprawdź skrzynkę odbiorczą (w tym folder Spam) i wpisz 6 cyfr poniżej:",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Zaloguj do systemu",
    verifyingCode: "Weryfikacja kodu...",
    changeEmailBtn: "← Zmień E-mail",
    resendCodeBtn: "Wyślij kod ponownie",
    inAppBrowserNoticeTitle: "📱 Wykryto przeglądarkę aplikacji (Telegram / Viber)",
    inAppBrowserNoticeDesc: "Google blokuje logowanie wewnątrz komunikatorów. Skorzystaj z logowania kodem E-mail poniżej lub uruchom tryb Gościa w 1 kliknięcie.",
    guestAccessTitle: "Błyskawiczny start jako Gość",
    guestAccessDesc: "Wypróbuj dowolny moduł AI już teraz bez wpisywania e-maila i haseł (1 bezpłatne pełne zapytanie).",
    guestAccessBtn: "🚀 Wejdź jako Gość (1 klik)",
    trustBadge1: "🔒 100% Poufności: Ścisła izolacja danych firmowych",
    trustBadge2: "⚡ Bez karty płatniczej • 1. uruchomienie gratis",
    trustBadge3: "🛡️ Zgodność z RODO i ISO 27001",
    freeTestTitle: "Bezpłatny dostęp testowy:",
    freeTestDesc: "Każdy nowy użytkownik otrzymuje po 1 bezpłatnym zapytaniu testowym do każdego modułu. Następnie możesz wykupić subskrypcję lub doładować saldo na pojedyncze zapytania (Pay-per-request).",
    syncTipTitle: "Warto wiedzieć:",
    syncTipDesc: "Platforma automatycznie synchronizuje przesłane dokumenty z botem Telegram, tworzy kalendarze zadań i wysyła powiadomienia o raportach.",
    howToStartTitle: "Jak zacząć?",
    howToStartStep1: "Zarejestruj profil firmy w 2 minuty.",
    howToStartStep2: "Prześlij podstawowe dokumenty (statut, dane rejestrowe).",
    howToStartStep3: "Połącz bota Telegram — Twój Cyfrowy Bliźniak jest gotowy!",
    offerModalTitle: "Regulamin i Umowa Ofertowa",
    offerModalAcceptBtn: "Zaakceptuj i zgódź się",
    offerModalCloseBtn: "Zamknij",
    pricingModalBtnText: "Cennik"
  },
  it: {
    brandTitle: "Gemello Digitale d'Impresa",
    brandSubtitle: "Ecosistema di Hub Settoriali Specializzati",
    quote: "«Il centro operativo completo della tua azienda: dall'audit dei contratti in 45 secondi ai preventivi edili e alla geoanalitica.»",
    valueMetricsTitle: "Perché è un vantaggio strategico per la tua azienda:",
    metric1Title: "45 sec invece di 4 ore",
    metric1Desc: "Audit istantaneo di contratti, preventivi e relazioni analitiche.",
    metric2Title: "Risparmio fino al 90%",
    metric2Desc: "40+ moduli specializzati al prezzo di un unico abbonamento.",
    metric3Title: "100% Conformità",
    metric3Desc: "Verifica sanzioni, normative fiscali (SDI), standard ISO e appalti pubblici.",
    metric4Title: "Web & Telegram 24/7",
    metric4Desc: "Operatività fluida tramite chat Telegram o portale web avanzato.",
    hubsTitle: "6 Hub Operativi Specializzati:",
    hub1Title: "Centro Giuridico & Societario",
    hub1Desc: "Audit contratti commerciali in 1 minuto, rilevamento clausole critiche, controlli sanzioni & AML, conformità GDPR e diritto del lavoro.",
    hub2Title: "Centro Fiscale & Contabile",
    hub2Desc: "Revisione dichiarazioni fiscali, fatturazione elettronica SDI / FatturaPA, classificazione di bilancio e analisi finanziaria.",
    hub3Title: "Centro Edilizia & Computi Metrici",
    hub3Desc: "Conversione perizie in computi metrici estimativi, codici di tariffa, calcolo ore-uomo ed esportazione in Excel.",
    hub4Title: "Hub Energetico & Audit ESG",
    hub4Desc: "Energy Manager aziendale (ISO 50001), progettazione FER (Solare/Eolico), telemetria e audit di sostenibilità ESG.",
    hub5Title: "Centro Spaziale & Geospaziale (GEOINT)",
    hub5Desc: "Valutazione danni da satellite, monitoraggio sismico CTBTO, navigazione di precisione RTK/GNSS e metrologia temporale.",
    hub6Title: "Deep Intelligence & Consulenza Direzionale",
    hub6Desc: "Ricerche web approfondite in tempo reale (2026), rassegna stampa 24/7 su Telegram/Email, B2B sales e screening candidati.",
    securityTitle: "Sicurezza Enterprise & On-Premise:",
    securityItem1: "Basi di conoscenza private: Isolamento totale dei dati aziendali in archivio crittografato.",
    securityItem2: "Installazione On-Premise: Disponibile per server dedicati aziendali o istituzionali.",
    securitySystemActive: "SISTEMA DI SICUREZZA: ATTIVO",
    version: "VERSIONE: 2.5.0",
    loginCardTitlePrefix: "Accedi all'",
    loginCardTitleHighlight: "Ecosistema Deep Research Hub",
    loginCardSubtitle: "AUTENTICAZIONE UTENTE",
    loginNotice: "Scegli il metodo di accesso preferito per utilizzare il tuo Gemello Digitale:",
    offerCheckboxPrefix: "Ho letto e accetto i termini dell'",
    offerLink: "Offerta Pubblica e Condizioni Generali",
    offerCheckboxSuffix: "e acconsento al trattamento dei miei dati personali.",
    pricingBtn: "Moduli & Tariffe",
    googleLoginBtn: "Accedi con Google (1 clic)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Ultimo accesso",
    loginTabGoogle: "⚡ Google (1 clic)",
    loginTabEmail: "✉️ Accedi con codice E-mail",
    loginTabGuest: "🚀 Accesso Ospite (Senza registrazione)",
    emailInputLabel: "E-mail aziendale o personale",
    emailInputPlaceholder: "direzione@azienda.it o nome@gmail.com",
    sendCodeBtn: "Ricevi codice di verifica a 6 cifre",
    sendingCode: "Invio codice in corso...",
    codeSentNotice: "Codice inviato alla tua e-mail",
    codeSentDesc: "Controlla la posta in arrivo (inclusa la cartella Spam) e inserisci le 6 cifre:",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Verifica e Accedi",
    verifyingCode: "Verifica codice...",
    changeEmailBtn: "← Modifica e-mail",
    resendCodeBtn: "Invia nuovamente il codice",
    inAppBrowserNoticeTitle: "📱 Rilevato browser in-app (Telegram / Viber)",
    inAppBrowserNoticeDesc: "Google blocca l'accesso nei browser interni. Usa il codice e-mail qui sotto o avvia la modalità Ospite in 1 clic.",
    guestAccessTitle: "Prova Istantanea come Ospite",
    guestAccessDesc: "Testa subito qualsiasi modulo IA senza inserire e-mail o password (1 richiesta completa gratuita).",
    guestAccessBtn: "🚀 Entra come Ospite (1 clic)",
    trustBadge1: "🔒 100% Riservatezza: Dati aziendali isolati e protetti",
    trustBadge2: "⚡ Nessuna carta di credito richiesta • 1° test gratis",
    trustBadge3: "🛡️ Conforme GDPR & ISO 27001",
    freeTestTitle: "Accesso di prova gratuito:",
    freeTestDesc: "Ogni nuovo utente riceve 1 richiesta di prova gratuita per ciascun modulo. Successivamente è possibile sottoscrivere un piano mensile o ricaricare il saldo a consumo (Pay-per-request).",
    syncTipTitle: "Nota informativa:",
    syncTipDesc: "La piattaforma sincronizza automaticamente i documenti aziendali caricati con il bot Telegram, pianifica le scadenze e invia i report.",
    howToStartTitle: "Come iniziare?",
    howToStartStep1: "Registra il profilo aziendale in 2 minuti.",
    howToStartStep2: "Carica i documenti di base (statuto, visura camerale).",
    howToStartStep3: "Collega il bot Telegram — e il tuo Gemello Digitale è pronto!",
    offerModalTitle: "Contratto di Offerta Pubblica",
    offerModalAcceptBtn: "Accetta & Continua",
    offerModalCloseBtn: "Chiudi",
    pricingModalBtnText: "Tariffe"
  },
  es: {
    brandTitle: "Gemelo Digital Empresarial",
    brandSubtitle: "Ecosistema de Hubs Sectoriales Especializados",
    quote: "«El centro operativo integral de su empresa: desde auditorías de contratos en 45 segundos hasta presupuestos de obra y geoanalítica.»",
    valueMetricsTitle: "Por qué genera un valor extraordinario para su empresa:",
    metric1Title: "45 seg en lugar de 4 horas",
    metric1Desc: "Auditoría instantánea de contratos, presupuestos e informes analíticos.",
    metric2Title: "Ahorro de hasta el 90%",
    metric2Desc: "Más de 40 módulos especializados al coste de una única suscripción.",
    metric3Title: "100% Cumplimiento",
    metric3Desc: "Verificación de sanciones, normativa tributaria (AEAT), normas ISO y contratación pública.",
    metric4Title: "Web & Telegram 24/7",
    metric4Desc: "Operatividad ágil mediante chat de Telegram o interfaz web avanzada.",
    hubsTitle: "6 Hubs Operativos Especializados:",
    hub1Title: "Centro Legal & Corporativo",
    hub1Desc: "Auditoría de contratos comerciales en 1 minuto, detección de cláusulas trampa, verificación de sanciones y AML, RGPD y derecho laboral.",
    hub2Title: "Centro Fiscal & Contable",
    hub2Desc: "Revisión de declaraciones tributarias, facturación electrónica FacturaE / Veri*Factu, clasificación presupuestaria y salud financiera.",
    hub3Title: "Centro Construcción & Mediciones",
    hub3Desc: "Transformación de actas de daños en presupuestos de obra, precios unitarios, cálculo de horas-hombre y exportación a Excel.",
    hub4Title: "Hub Energético & Auditoría ESG",
    hub4Desc: "Gestor energético empresarial (ISO 50001), diseño de renovables (Solar/Eólica), telemetría de contadores y sostenibilidad ESG.",
    hub5Title: "Centro Espacial & Geoespacial (GEOINT)",
    hub5Desc: "Evaluación de daños por satélite, monitorización sísmica CTBTO, navegación de precisión RTK/GNSS y metrología horaria.",
    hub6Title: "Deep Intelligence & Consultoría de Negocio",
    hub6Desc: "Investigación web profunda en tiempo real (2026), inteligencia de noticias 24/7 en Telegram/Email, prospección B2B y selección de personal.",
    securityTitle: "Seguridad Empresarial & On-Premise:",
    securityItem1: "Bases de conocimiento privadas: Aislamiento total de los datos corporativos en almacén cifrado.",
    securityItem2: "Despliegue On-Premise: Disponible para servidores internos de empresas o instituciones.",
    securitySystemActive: "SISTEMA DE SEGURIDAD: ACTIVO",
    version: "VERSIÓN: 2.5.0",
    loginCardTitlePrefix: "Acceso al",
    loginCardTitleHighlight: "Ecosistema Deep Research Hub",
    loginCardSubtitle: "AUTENTICACIÓN DE USUARIO",
    loginNotice: "Elija su método de acceso preferido para utilizar su Gemelo Digital:",
    offerCheckboxPrefix: "He leído y acepto los términos de la",
    offerLink: "Oferta Pública y Condiciones de Uso",
    offerCheckboxSuffix: "y consiento el tratamiento de mis datos personales.",
    pricingBtn: "Módulos & Precios",
    googleLoginBtn: "Iniciar sesión con Google (1 clic)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Último acceso",
    loginTabGoogle: "⚡ Google (1 clic)",
    loginTabEmail: "✉️ Código por E-mail",
    loginTabGuest: "🚀 Acceso Invitado (Sin registro)",
    emailInputLabel: "E-mail corporativo o personal",
    emailInputPlaceholder: "direccion@empresa.es o nombre@gmail.com",
    sendCodeBtn: "Obtener código de 6 dígitos",
    sendingCode: "Enviando código...",
    codeSentNotice: "Código enviado a su correo electrónico",
    codeSentDesc: "Revise su bandeja de entrada (incluida la carpeta Spam) e introduzca los 6 dígitos:",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Verificar e Iniciar Sesión",
    verifyingCode: "Verificando código...",
    changeEmailBtn: "← Cambiar e-mail",
    resendCodeBtn: "Reenviar código",
    inAppBrowserNoticeTitle: "📱 Navegador integrado detectado (Telegram / Viber)",
    inAppBrowserNoticeDesc: "Google bloquea el inicio de sesión dentro de aplicaciones. Utilice el código por e-mail abajo o el modo Invitado en 1 clic.",
    guestAccessTitle: "Prueba Instantánea de Invitado",
    guestAccessDesc: "Pruebe cualquier módulo de IA ahora mismo sin ingresar e-mail ni contraseña (1 consulta completa gratuita).",
    guestAccessBtn: "🚀 Iniciar como Invitado (1 clic)",
    trustBadge1: "🔒 100% Confidencialidad: Datos corporativos aislados",
    trustBadge2: "⚡ Sin tarjeta de crédito requerida • 1ª prueba gratis",
    trustBadge3: "🛡️ Cumplimiento RGPD & ISO 27001",
    freeTestTitle: "Acceso de prueba gratuito:",
    freeTestDesc: "Cada nuevo usuario dispone de 1 solicitud de prueba gratuita para cada módulo. Posteriormente puede contratar un paquete mensual o recargar saldo para consultas individuales (Pay-per-request).",
    syncTipTitle: "Información útil:",
    syncTipDesc: "La plataforma sincroniza automáticamente los documentos subidos con el bot de Telegram, crea calendarios de tareas y envía informes.",
    howToStartTitle: "¿Cómo empezar?",
    howToStartStep1: "Registre el perfil de su empresa en 2 minutos.",
    howToStartStep2: "Suba los documentos básicos (estatutos, datos registrales).",
    howToStartStep3: "Conecte el bot de Telegram — ¡y su Gemelo Digital estará listo!",
    offerModalTitle: "Contrato de Oferta Pública",
    offerModalAcceptBtn: "Aceptar & Continuar",
    offerModalCloseBtn: "Cerrar",
    pricingModalBtnText: "Precios"
  },
  pt: {
    brandTitle: "Gémeo Digital Empresarial",
    brandSubtitle: "Ecossistema de Hubs Setoriais Especializados",
    quote: "«O centro operacional completo da sua empresa: da auditoria de contratos em 45 segundos aos orçamentos de obras e geoanalítica.»",
    valueMetricsTitle: "Por que traz um enorme valor ao seu negócio:",
    metric1Title: "45 seg em vez de 4 horas",
    metric1Desc: "Auditoria instantânea de contratos, orçamentos e relatórios analíticos.",
    metric2Title: "Poupança de até 90%",
    metric2Desc: "Mais de 40 módulos especializados pelo preço de uma única subscrição.",
    metric3Title: "100% Conformidade",
    metric3Desc: "Verificação de sanções, conformidade fiscal (AT / SAF-T), normas ISO e contratos públicos.",
    metric4Title: "Web & Telegram 24/7",
    metric4Desc: "Operação prática no chat do Telegram ou na interface web avançada.",
    hubsTitle: "6 Hubs Operacionais Especializados:",
    hub1Title: "Centro Jurídico & Societário",
    hub1Desc: "Auditoria de contratos comerciais em 1 minuto, deteção de cláusulas armadilha, verificação de sanções & AML, RGPD e direito laboral.",
    hub2Title: "Centro Fiscal & Contabilístico",
    hub2Desc: "Revisão de declarações fiscais, faturação eletrónica e SAF-T (PT), classificação orçamental e saúde financeira.",
    hub3Title: "Centro Construção & Orçamentação",
    hub3Desc: "Transformação de autos de danos em orçamentos de construção, preços unitários, cálculo de horas-homem e exportação para Excel.",
    hub4Title: "Hub Energético & Auditoria ESG",
    hub4Desc: "Gestão energética empresarial (ISO 50001), projetos de renováveis (Solar/Eólica), telemetria e sustentabilidade ESG.",
    hub5Title: "Centro Espacial & Geoespacial (GEOINT)",
    hub5Desc: "Avaliação de danos por satélite, monitorização sísmica CTBTO, navegação de precisão RTK/GNSS e metrologia temporal.",
    hub6Title: "Deep Intelligence & Consultoria de Gestão",
    hub6Desc: "Pesquisa web profunda em tempo real (2026), alertas de notícias 24/7 no Telegram/Email, prospeção B2B e triagem de candidatos.",
    securityTitle: "Segurança Empresarial & On-Premise:",
    securityItem1: "Bases de conhecimento privadas: Isolamento total dos dados corporativos em repositório encriptado.",
    securityItem2: "Implementação On-Premise: Disponível para servidores dedicados de empresas ou instituições públicas.",
    securitySystemActive: "SISTEMA DE SEGURANÇA: ATIVO",
    version: "VERSÃO: 2.5.0",
    loginCardTitlePrefix: "Acesso ao",
    loginCardTitleHighlight: "Ecossistema Deep Research Hub",
    loginCardSubtitle: "AUTENTICAÇÃO DE UTILIZADOR",
    loginNotice: "Escolha o seu método de acesso preferido para utilizar o seu Gémeo Digital:",
    offerCheckboxPrefix: "Li e aceito os termos da",
    offerLink: "Oferta Pública e Termos de Utilização",
    offerCheckboxSuffix: "e consinto o tratamento dos meus dados pessoais.",
    pricingBtn: "Módulos & Preços",
    googleLoginBtn: "Iniciar sessão com Google (1 clique)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Último acesso",
    loginTabGoogle: "⚡ Google (1 clique)",
    loginTabEmail: "✉️ Código por E-mail",
    loginTabGuest: "🚀 Acesso Convidado (Sem registo)",
    emailInputLabel: "E-mail profissional ou pessoal",
    emailInputPlaceholder: "direcao@empresa.pt ou nome@gmail.com",
    sendCodeBtn: "Obter código de 6 dígitos",
    sendingCode: "A enviar código...",
    codeSentNotice: "Código enviado para o seu e-mail",
    codeSentDesc: "Verifique a sua caixa de correio (incluindo Spam) e insira os 6 dígitos abaixo:",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Verificar e Entrar",
    verifyingCode: "A verificar código...",
    changeEmailBtn: "← Alterar e-mail",
    resendCodeBtn: "Reenviar código",
    inAppBrowserNoticeTitle: "📱 Navegador in-app detetado (Telegram / Viber)",
    inAppBrowserNoticeDesc: "A Google bloqueia o login dentro de aplicações. Utilize o código por e-mail abaixo ou inicie como Convidado em 1 clique.",
    guestAccessTitle: "Teste Instantâneo de Convidado",
    guestAccessDesc: "Experimente qualquer módulo de IA agora mesmo sem inserir e-mail ou palavras-passe (1 pedido completo grátis).",
    guestAccessBtn: "🚀 Entrar como Convidado (1 clique)",
    trustBadge1: "🔒 100% Confidencialidade: Isolamento de dados corporativos",
    trustBadge2: "⚡ Sem cartão de crédito • 1º teste 100% gratuito",
    trustBadge3: "🛡️ Conforme RGPD & ISO 27001",
    freeTestTitle: "Acesso experimental gratuito:",
    freeTestDesc: "Cada novo utilizador recebe 1 pedido de teste gratuito para cada módulo. Posteriormente, pode subscrever um pacote mensal ou carregar saldo avulso (Pay-per-request).",
    syncTipTitle: "Nota útil:",
    syncTipDesc: "A plataforma sincroniza automaticamente os documentos submetidos com o bot do Telegram, agenda tarefas e entrega relatórios.",
    howToStartTitle: "Como começar?",
    howToStartStep1: "Registe o perfil da empresa em 2 minutos.",
    howToStartStep2: "Submeta os documentos base (estatutos, certidão permanente).",
    howToStartStep3: "Ligue o bot do Telegram — e o seu Gémeo Digital está pronto!",
    offerModalTitle: "Contrato de Oferta Pública",
    offerModalAcceptBtn: "Aceitar & Continuar",
    offerModalCloseBtn: "Fechar",
    pricingModalBtnText: "Preços"
  },
  sv: {
    brandTitle: "Företagets Digitala Tvilling",
    brandSubtitle: "Ekosystem av Specialiserade Branschhubbar",
    quote: "«Företagets kompletta operativa center: från avtalsgranskning på 45 sekunder till byggkalkyler och geoanalys.»",
    valueMetricsTitle: "Varför detta ger enormt affärsvärde till ert företag:",
    metric1Title: "45 sek istället för 4 tim",
    metric1Desc: "Blixtsnabb granskning av avtal, kalkyler och analytiska rapporter.",
    metric2Title: "Spara upp till 90%",
    metric2Desc: "Över 40 specialiserade moduler till priset av en enda prenumeration.",
    metric3Title: "100% Efterlevnad",
    metric3Desc: "Sanktionskontroll, skatteregler (Skatteverket), ISO-standarder och LOU.",
    metric4Title: "Webb & Telegram 24/7",
    metric4Desc: "Smidigt arbetsflöde via Telegram-chatt eller avancerad webbportal.",
    hubsTitle: "6 Specialiserade Operativa Hubbar:",
    hub1Title: "Juridiskt & Bolagsrättsligt Center",
    hub1Desc: "Granskning av kommersiella avtal på 1 minut, upptäckt av riskklausuler, sanktions- & AML-kontroll, GDPR och arbetsrätt.",
    hub2Title: "Skatte- & Redovisningscenter",
    hub2Desc: "Granskning av skattedeklarationer, elektroniska fakturor (PEPPOL / Moms), budgetkategorisering och finansiell hälsoanalys.",
    hub3Title: "Bygg- & Kalkylcenter",
    hub3Desc: "Omvandling av skaderapporter till byggkalkyler enligt AB 04 / ABT 06, beräkning av arbetstimmar och export till Excel.",
    hub4Title: "Energihubb & ESG-Granskning",
    hub4Desc: "Företagets energiledning (ISO 50001), förnybar energi (Sol/Vind/Microgrid), mätartelemetri och ESG-hållbarhetsrevision.",
    hub5Title: "Rymd- & Geospatialt Center (GEOINT)",
    hub5Desc: "Skadebedömning via satellitbilder, seismisk CTBTO-övervakning, RTK/GNSS-precisionsnavigering och tidsmetrologi.",
    hub6Title: "Deep Intelligence & Företagsrådgivning",
    hub6Desc: "Djupgående webbanalys i realtid (2026), 24/7 nyhetsbevakning på Telegram/E-post, B2B-prospektering och kandidatscreening.",
    securityTitle: "Företagssäkerhet & On-Premise:",
    securityItem1: "Privata kunskapsbaser: Total isolering av företagsdata i krypterat datalager.",
    securityItem2: "On-Premise-distribution: Tillgänglig för företags eller myndigheters interna servrar.",
    securitySystemActive: "SÄKERHETSSYSTEM: AKTIVT",
    version: "VERSION: 2.5.0",
    loginCardTitlePrefix: "Logga in på",
    loginCardTitleHighlight: "Deep Research Hub Ekosystem",
    loginCardSubtitle: "ANVÄNDARAUTENTISERING",
    loginNotice: "Välj önskad inloggningsmetod för att använda er Digitala Tvilling:",
    offerCheckboxPrefix: "Jag har läst och godkänner villkoren i",
    offerLink: "Det Allmänna Erbjudandet och Användarvillkoren",
    offerCheckboxSuffix: "och samtycker till behandling av mina personuppgifter.",
    pricingBtn: "Moduler & Priser",
    googleLoginBtn: "Logga in med Google (1 klick)",
    microsoftLoginBtn: "Microsoft 365",
    appleLoginBtn: "Apple ID",
    lastUsedBadge: "Senast använd",
    loginTabGoogle: "⚡ Google (1 klick)",
    loginTabEmail: "✉️ Logga in med E-postkod",
    loginTabGuest: "🚀 Gäståtkomst (Utan registrering)",
    emailInputLabel: "Arbets- eller privat e-post",
    emailInputPlaceholder: "vd@foretag.se eller namn@gmail.com",
    sendCodeBtn: "Hämta 6-siffrig verifieringskod",
    sendingCode: "Skickar kod...",
    codeSentNotice: "Verifieringskod har skickats till din e-post",
    codeSentDesc: "Kontrollera din inkorg (inklusive skräppost) och ange de 6 siffrorna nedan:",
    codeInputPlaceholder: "123456",
    verifyCodeBtn: "Verifiera och Logga in",
    verifyingCode: "Verifierar kod...",
    changeEmailBtn: "← Ändra e-post",
    resendCodeBtn: "Skicka koden igen",
    inAppBrowserNoticeTitle: "📱 In-App-webbläsare identifierad (Telegram / Viber)",
    inAppBrowserNoticeDesc: "Google blockerar inloggningar i inbyggda webbläsare. Använd e-postkoden nedan eller starta gästläget med 1 klick.",
    guestAccessTitle: "Omedelbar testkörning som gäst",
    guestAccessDesc: "Testa valfri AI-modul direkt utan att ange e-post eller lösenord (1 fullständig gratis körning).",
    guestAccessBtn: "🚀 Starta som Gäst (1 klick)",
    trustBadge1: "🔒 100% Konfidentialitet: Isolerade företagsdata",
    trustBadge2: "⚡ Inget kreditkort krävs • 1:a körningen gratis",
    trustBadge3: "🛡️ GDPR & ISO 27001 Efterlevnad",
    freeTestTitle: "Kostnadsfri teståtkomst:",
    freeTestDesc: "Varje ny användare får 1 gratis testförfrågan för varje modul. Därefter kan ni teckna ett månadspaket eller fylla på saldo för enstaka körningar (Pay-per-request).",
    syncTipTitle: "Bra att veta:",
    syncTipDesc: "Plattformen synkroniserar automatiskt uppladdade företagsdokument med Telegram-boten, skapar uppgiftskalendrar och levererar rapporter.",
    howToStartTitle: "Hur kommer du igång?",
    howToStartStep1: "Registrera företagsprofilen på 2 minuter.",
    howToStartStep2: "Ladda upp grundläggande dokument (bolagsordning, registreringsbevis).",
    howToStartStep3: "Koppla Telegram-boten — och er Digitala Tvilling är redo!",
    offerModalTitle: "Avtal om Allmänt Erbjudande",
    offerModalAcceptBtn: "Godkänn & Fortsätt",
    offerModalCloseBtn: "Stäng",
    pricingModalBtnText: "Priser"
  }
};

const getInitialTab = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'office' || tab === 'office-portal') return 'office-portal';
    if (tab === 'space' || tab === 'space-portal') return 'space-portal';
    if (tab) return tab;
    const mode = params.get('mode');
    if (mode === 'research') return 'research';
    const savedWs = localStorage.getItem('deep_research_hub_active_workspace');
    if (savedWs === 'office') return 'office-portal';
    if (savedWs === 'space') return 'space-portal';
  } catch (e) {
    console.error(e);
  }
  return 'hub';
};

export default function App() {
  const [authData, setAuthData] = useState<any>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [acceptedOffer, setAcceptedOffer] = useState(() => {
    try {
      const stored = localStorage.getItem('acceptedOffer');
      return stored !== 'false'; // default true for frictionless 1-click entry
    } catch (e) {
      return true;
    }
  });
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showPublicPricingModal, setShowPublicPricingModal] = useState(false);
  const [showDesktopStoreModal, setShowDesktopStoreModal] = useState(false);
  const [showQuickDemoModal, setShowQuickDemoModal] = useState(false);
  const [sessions, setSessions] = useState<ResearchSession[]>([]);
  const [activeSession, setActiveSession] = useState<ResearchSession | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceMode>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab === 'space' || tab === 'space-portal') return 'space';
      if (tab === 'office' || tab === 'office-portal') return 'office';
      const saved = localStorage.getItem('deep_research_hub_active_workspace') as WorkspaceMode;
      if (saved === 'office' || saved === 'space') return saved;
    } catch (e) {}
    return 'office';
  });

  const handleSwitchWorkspace = (mode: WorkspaceMode) => {
    setActiveWorkspace(mode);
    try {
      localStorage.setItem('deep_research_hub_active_workspace', mode);
    } catch (e) {}
    handleNewResearch();
    if (mode === 'office') {
      setUseEnterpriseContext(true);
      setActiveTab('office-portal');
    } else {
      setUseEnterpriseContext(false);
      setActiveTab('space-portal');
    }
  };
  const [useEnterpriseContext, setUseEnterpriseContext] = useState(false);
  const [showOfficeInfoModal, setShowOfficeInfoModal] = useState(false);
  const [showDeepBrainOsModal, setShowDeepBrainOsModal] = useState(false);
  const [deepBrainOsInitialTab, setDeepBrainOsInitialTab] = useState<'overview' | 'architecture' | 'skills' | 'pricing'>('pricing');
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState<string>('scientific');
  const [pendingAgentNotice, setPendingAgentNotice] = useState<string | null>(null);
  const [globalLanguage, setGlobalLanguage] = useState<Language>(() => {
    try {
      const sessionLang = sessionStorage.getItem('drh_user_chosen_lang_session') as Language;
      if (sessionLang) return sessionLang;
      const manual = localStorage.getItem('drh_manual_lang') as Language;
      if (manual) return manual;
      if (typeof navigator !== 'undefined' && navigator.language) {
        const nav = navigator.language.toLowerCase();
        if (nav.startsWith('de')) return 'de';
        if (nav.startsWith('pl')) return 'pl';
        if (nav.startsWith('fr')) return 'fr';
        if (nav.startsWith('it')) return 'it';
        if (nav.startsWith('es')) return 'es';
        if (nav.startsWith('pt')) return 'pt';
        if (nav.startsWith('sv')) return 'sv';
        if (nav.startsWith('en')) return 'en';
      }
    } catch (e) {}
    return 'uk';
  });
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>(() => {
    try {
      const sessionJur = sessionStorage.getItem('drh_user_chosen_jur_session') as Jurisdiction;
      if (sessionJur) return sessionJur;
      const manual = localStorage.getItem('drh_manual_jurisdiction') as Jurisdiction;
      if (manual) return manual;
    } catch (e) {}
    return 'UA';
  });
  const [currency, setCurrency] = useState<Currency>(() => {
    try {
      const manual = localStorage.getItem('drh_manual_currency') as Currency;
      if (manual) return manual;
    } catch (e) {}
    return 'UAH';
  });
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [activeHubContext, setActiveHubContext] = useState<string | null>(null);
  const [agentLanguageOverride, setAgentLanguageOverride] = useState<Language | null>(null);
  const [activeDynamicAgent, setActiveDynamicAgent] = useState<DynamicAgentConfig | null>(() => {
    try {
      const saved = localStorage.getItem('drh_active_dynamic_agent');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const getEffectiveAgentLanguage = (agentId: string): Language => {
    if (agentLanguageOverride) return agentLanguageOverride;
    if (SPACE_AGENT_IDS.includes(agentId)) return globalLanguage;
    if (POLAND_AGENT_IDS.includes(agentId)) {
      if (globalLanguage === 'uk') return 'uk';
      if (globalLanguage === 'en') return 'en';
      return 'pl';
    }
    if (GERMANY_AGENT_IDS.includes(agentId)) {
      if (agentLanguageOverride) return agentLanguageOverride;
      if (globalLanguage === 'uk') return 'uk';
      if (globalLanguage === 'pl') return 'pl';
      if (globalLanguage === 'fr') return 'fr';
      if (globalLanguage === 'en') return 'en';
      return 'de';
    }
    if (FRANCE_AGENT_IDS.includes(agentId)) {
      if (agentLanguageOverride) return agentLanguageOverride;
      if (globalLanguage === 'uk') return 'uk';
      if (globalLanguage === 'pl') return 'pl';
      if (globalLanguage === 'en') return 'en';
      return 'fr';
    }
    if (EU_AGENT_IDS.includes(agentId)) {
      return globalLanguage;
    }
    return globalLanguage;
  };

  const handleSwitchGlobalLanguage = (lang: Language) => {
    try {
      sessionStorage.setItem('drh_user_chosen_lang_session', lang);
      localStorage.setItem('drh_manual_lang', lang);
    } catch (e) {}
    setGlobalLanguage(lang);
    setAgentLanguageOverride(lang);
    if (lang === 'uk') {
      setJurisdiction('UA');
      setCurrency('UAH');
      if (activeHubContext === 'poland-center-hub' || activeHubContext === 'eu-center-hub' || activeHubContext === 'us-center-hub' || activeHubContext === 'france-center-hub' || activeHubContext === 'germany-center-hub') {
        setActiveHubContext(null);
      }
    } else if (lang === 'pl') {
      setJurisdiction('PL');
      setCurrency('PLN');
    } else if (lang === 'de') {
      setJurisdiction('DE');
      setCurrency('EUR');
    } else if (lang === 'fr') {
      setJurisdiction('FR');
      setCurrency('EUR');
    } else if (lang === 'it') {
      setJurisdiction('IT');
      setCurrency('EUR');
    } else if (lang === 'es') {
      setJurisdiction('ES');
      setCurrency('EUR');
    } else if (lang === 'pt') {
      setJurisdiction('PT');
      setCurrency('EUR');
    } else if (lang === 'sv') {
      setJurisdiction('SE');
      setCurrency('SEK');
    } else if (lang === 'en') {
      setJurisdiction('EU');
      setCurrency('EUR');
    }
  };

  const applyGeoRecommendation = (rec: any, isLoginPage: boolean) => {
    if (!rec) return;
    try {
      const sessionUserChosenLang = sessionStorage.getItem('drh_user_chosen_lang_session');
      const lastCountry = localStorage.getItem('drh_last_country_code');
      const isNewCountry = !!(rec.countryCode && lastCountry && lastCountry !== rec.countryCode);

      // Auto-adapt if:
      // 1) User opened the login page and hasn't manually clicked language in this session
      // 2) Or user connected via VPN / new country (country changed)
      // 3) Or no explicit session language override exists
      const shouldAdapt = !sessionUserChosenLang || isNewCountry || (isLoginPage && !sessionUserChosenLang);

      if (shouldAdapt) {
        if (rec.language) {
          setGlobalLanguage(rec.language);
          setAgentLanguageOverride(rec.language);
          localStorage.setItem('drh_manual_lang', rec.language);
        }
        if (rec.currency) {
          setCurrency(rec.currency);
          localStorage.setItem('drh_manual_currency', rec.currency);
        }
        if (rec.jurisdiction) {
          setJurisdiction(rec.jurisdiction);
          localStorage.setItem('drh_manual_jurisdiction', rec.jurisdiction);
        }
        if (rec.suggestedHub && isLoginPage) {
          setActiveHubContext(rec.suggestedHub);
        }
      }

      if (rec.countryCode) {
        localStorage.setItem('drh_last_country_code', rec.countryCode);
      }
    } catch (e) {
      console.warn('Error applying geo recommendation:', e);
    }
  };

  // 🌍 Geo-IP Auto-Adaptation Engine (PL -> KSeF/Umowy/PLN, DE -> CBAM/XRechnung/EUR, EU -> CBAM/FIDIC/EUR, UA -> ДСТУ/UAH)
  useEffect(() => {
    // Auto-clean stale Google AI Studio prepayment keys depleted in browser localStorage
    try {
      const storedKey = localStorage.getItem('custom_gemini_api_key');
      if (storedKey && storedKey.startsWith('AQ.Ab8RN')) {
        console.warn('[Gemini Auth] Detected depleted AI Studio prepayment key in localStorage. Clearing to use server Google Cloud Console key.');
        localStorage.removeItem('custom_gemini_api_key');
      }
    } catch (_) {}

    try {
      const isLoginPage = !authData || !authData.authenticated;
      fetch('/api/client-geo')
        .then(res => res.json())
        .then(data => {
          if (data?.recommendation) {
            applyGeoRecommendation(data.recommendation, isLoginPage);
          }
        })
        .catch(err => {
          console.warn('Geo-IP auto-adaptation fetch error:', err);
        });
    } catch (e) {
      console.warn('Geo-IP auto-adaptation error:', e);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'poland-center-hub') {
      setGlobalLanguage('pl');
      setJurisdiction('PL');
      setCurrency('PLN');
      setAgentLanguageOverride('pl');
      setActiveHubContext('poland-center-hub');
    } else if (activeTab === 'germany-center-hub') {
      setGlobalLanguage('de');
      setJurisdiction('DE');
      setCurrency('EUR');
      setAgentLanguageOverride('de');
      setActiveHubContext('germany-center-hub');
    } else if (activeTab === 'france-center-hub') {
      setGlobalLanguage('fr');
      setJurisdiction('FR');
      setCurrency('EUR');
      setAgentLanguageOverride('fr');
      setActiveHubContext('france-center-hub');
    } else if (activeTab === 'us-center-hub') {
      setGlobalLanguage('en');
      setJurisdiction('US');
      setCurrency('USD');
      setAgentLanguageOverride('en');
      setActiveHubContext('us-center-hub');
    } else if (activeTab === 'eu-center-hub') {
      setGlobalLanguage('en');
      setJurisdiction('EU');
      setCurrency('EUR');
      setAgentLanguageOverride('en');
      setActiveHubContext('eu-center-hub');
    } else if (activeTab === 'space-center-hub' || SPACE_AGENT_IDS.includes(activeTab)) {
      setActiveHubContext('space-center-hub');
      if (globalLanguage === 'uk') {
        setAgentLanguageOverride('uk');
      }
    } else if (activeTab === 'hub') {
      setActiveHubContext(null);
      setAgentLanguageOverride(null);
    }
  }, [activeTab]);

  const handleLaunchAgentWithContext = (target: string, preferredLang?: any, hubContext?: string, dynamicAgentData?: any) => {
    setUseEnterpriseContext(false);
    if (target === 'dynamic-on-the-fly-agent' || dynamicAgentData) {
      if (dynamicAgentData) {
        setActiveDynamicAgent(dynamicAgentData);
        try {
          localStorage.setItem('drh_active_dynamic_agent', JSON.stringify(dynamicAgentData));
        } catch (e) {}
      }
      setActiveTab('dynamic-on-the-fly-agent');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const isPoland = hubContext === 'poland-center-hub' || (POLAND_AGENT_IDS.includes(target) && globalLanguage !== 'uk');
    const isEU = hubContext === 'eu-center-hub' || (EU_AGENT_IDS.includes(target) && globalLanguage !== 'uk');
    const isGermany = hubContext === 'germany-center-hub' || (GERMANY_AGENT_IDS.includes(target) && globalLanguage !== 'uk');
    const isFrance = hubContext === 'france-center-hub' || (FRANCE_AGENT_IDS.includes(target) && globalLanguage !== 'uk');
    const isUS = hubContext === 'us-center-hub' || (US_AGENT_IDS.includes(target) && globalLanguage !== 'uk');

    if (isGermany) {
      setActiveHubContext('germany-center-hub');
      setGlobalLanguage('de');
      setJurisdiction('DE');
      setCurrency('EUR');
      setAgentLanguageOverride(preferredLang || 'de');
    } else if (isFrance) {
      setActiveHubContext('france-center-hub');
      setGlobalLanguage('fr');
      setJurisdiction('FR');
      setCurrency('EUR');
      setAgentLanguageOverride(preferredLang || 'fr');
    } else if (isUS) {
      setActiveHubContext('us-center-hub');
      setGlobalLanguage('en');
      setJurisdiction('US');
      setCurrency('USD');
      setAgentLanguageOverride('en');
    } else if (isPoland) {
      setActiveHubContext('poland-center-hub');
      setGlobalLanguage('pl');
      setJurisdiction('PL');
      setCurrency('PLN');
      setAgentLanguageOverride('pl');
    } else if (isEU) {
      setActiveHubContext('eu-center-hub');
      setGlobalLanguage('en');
      setJurisdiction('EU');
      setCurrency('EUR');
      setAgentLanguageOverride('en');
    } else {
      if (hubContext) {
        setActiveHubContext(hubContext);
      }
      if (preferredLang) {
        setAgentLanguageOverride(preferredLang);
        setGlobalLanguage(preferredLang);
        if (preferredLang === 'pl') {
          setJurisdiction('PL');
          setCurrency('PLN');
        } else if (preferredLang === 'de') {
          setJurisdiction('DE');
          setCurrency('EUR');
        } else if (preferredLang === 'fr') {
          setJurisdiction('FR');
          setCurrency('EUR');
        } else if (preferredLang === 'en') {
          setJurisdiction('EU');
          setCurrency('EUR');
        } else {
          setJurisdiction('UA');
          setCurrency('UAH');
        }
      } else {
        setAgentLanguageOverride(null);
      }
    }
    setActiveTab(target);
  };

  const handleSelectCurrency = (cur: Currency) => {
    try {
      localStorage.setItem('drh_manual_currency', cur);
    } catch (e) {}
    setCurrency(cur);
    if (cur === 'USD') {
      setJurisdiction('US');
      handleSwitchGlobalLanguage('en');
    } else if (cur === 'EUR') {
      if (jurisdiction === 'DE' || globalLanguage === 'de') {
        setJurisdiction('DE');
        handleSwitchGlobalLanguage('de');
      } else if (jurisdiction === 'FR' || globalLanguage === 'fr') {
        setJurisdiction('FR');
        handleSwitchGlobalLanguage('fr');
      } else if (jurisdiction === 'IT' || globalLanguage === 'it') {
        setJurisdiction('IT');
        handleSwitchGlobalLanguage('it');
      } else if (jurisdiction === 'ES' || globalLanguage === 'es') {
        setJurisdiction('ES');
        handleSwitchGlobalLanguage('es');
      } else if (jurisdiction === 'PT' || globalLanguage === 'pt') {
        setJurisdiction('PT');
        handleSwitchGlobalLanguage('pt');
      } else {
        setJurisdiction('EU');
        handleSwitchGlobalLanguage('en');
      }
    } else if (cur === 'PLN') {
      setJurisdiction('PL');
      handleSwitchGlobalLanguage('pl');
    } else if (cur === 'SEK') {
      setJurisdiction('SE');
      handleSwitchGlobalLanguage('sv');
    } else if (cur === 'UAH') {
      setJurisdiction('UA');
      handleSwitchGlobalLanguage('uk');
    }
  };

  const handleSelectJurisdiction = (jur: Jurisdiction) => {
    try {
      sessionStorage.setItem('drh_user_chosen_lang_session', jur.toLowerCase());
      sessionStorage.setItem('drh_user_chosen_jur_session', jur);
      localStorage.setItem('drh_manual_jurisdiction', jur);
    } catch (e) {}
    setJurisdiction(jur);
    if (jur === 'US') {
      setCurrency('USD');
      handleSwitchGlobalLanguage('en');
    } else if (jur === 'EU') {
      setCurrency('EUR');
      handleSwitchGlobalLanguage('en');
    } else if (jur === 'DE') {
      setCurrency('EUR');
      handleSwitchGlobalLanguage('de');
    } else if (jur === 'FR') {
      setCurrency('EUR');
      handleSwitchGlobalLanguage('fr');
    } else if (jur === 'IT') {
      setCurrency('EUR');
      handleSwitchGlobalLanguage('it');
    } else if (jur === 'ES') {
      setCurrency('EUR');
      handleSwitchGlobalLanguage('es');
    } else if (jur === 'PT') {
      setCurrency('EUR');
      handleSwitchGlobalLanguage('pt');
    } else if (jur === 'SE') {
      setCurrency('SEK');
      handleSwitchGlobalLanguage('sv');
    } else if (jur === 'PL') {
      setCurrency('PLN');
      handleSwitchGlobalLanguage('pl');
    } else {
      setCurrency('UAH');
      handleSwitchGlobalLanguage('uk');
    }
  };

  // Subscription states
  const [subscriptions, setSubscriptions] = useState<Record<string, any>>({});
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState<string | null>(null);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('100');
  const [topUpLoading, setTopUpLoading] = useState(false);

  const loadSubscriptionStatus = async (agentId: string) => {
    if (!authData?.user || authData?.user?.role === 'admin' || authData?.user?.is_enterprise_admin || ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(authData?.user?.email?.toLowerCase() || '')) return;

    // Співробітники підприємства: агенти, призначені адміністратором НЦУВКЗ, є безкоштовними
    if (authData?.user?.enterprise_id) {
      const allowed = Array.isArray(authData.user.allowed_enterprise_agents) ? authData.user.allowed_enterprise_agents : [];
      if (allowed.length > 0 && (allowed.includes(agentId) || allowed.includes(activeTab))) {
        setSubscriptions(prev => ({ 
          ...prev, 
          [agentId]: { allowed: true, isPaid: true, isUnlimited: true, isEnterpriseSponsored: true } 
        }));
        return;
      }
    }

    setSubscriptionLoading(true);
    try {
      const res = await fetch(`/api/subscription/status?agentId=${agentId}`);
      if (res.ok) {
        const data = await res.json();
        setSubscriptions(prev => ({ ...prev, [agentId]: data }));
      }
    } catch (err) {
      console.error(`Failed to load subscription status for ${agentId}`, err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleInitiatePayment = async (agentId: string) => {
    setPaymentLoading(agentId);
    try {
      const res = await fetch('/api/subscription/pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.pageUrl) {
          window.location.href = data.pageUrl;
        } else if (data.success) {
          await loadSubscriptionStatus(agentId);
        }
      } else {
        const data = await res.json();
        alert(data.error || 'Помилка створення рахунку');
      }
    } catch (err) {
      console.error('Payment error', err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setPaymentLoading(null);
    }
  };

  const handleInitiateTopUp = async () => {
    const parsed = parseFloat(topUpAmount);
    if (isNaN(parsed) || parsed <= 0) {
      alert(globalLanguage === 'pl' ? 'Podaj prawidłową kwotę doładowania' : globalLanguage === 'en' ? 'Please enter a valid top-up amount' : 'Вкажіть коректну суму поповнення');
      return;
    }
    setTopUpLoading(true);
    try {
      const res = await fetch('/api/billing/top-up', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parsed, currency })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.pageUrl) {
          window.location.href = data.pageUrl;
        }
      } else {
        const data = await res.json();
        alert(data.error || 'Помилка поповнення');
      }
    } catch (err) {
      console.error('Top-up error', err);
      alert('Помилка з\'єднання з сервером');
    } finally {
      setTopUpLoading(false);
      setShowTopUpModal(false);
    }
  };

  const handleTogglePinAgent = async (agentId: string) => {
    try {
      const res = await fetch('/api/enterprise/my/toggle-pin-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId })
      });
      if (res.ok) {
        const data = await res.json();
        const updatedPinned = data.pinnedAgentIds || [];
        setAuthData((prev: any) => prev && prev.user ? ({
          ...prev,
          user: { ...prev.user, pinned_enterprise_agents: updatedPinned }
        }) : prev);
      }
    } catch (err) {
      console.error('Failed to toggle pin agent:', err);
    }
  };

  const getAgentName = (agentId: string) => {
    const found = (agentsConfig as any[]).find(a => a.id === agentId);
    if (found) {
      return globalLanguage === 'pl' ? (found.namePl || found.nameEn || found.nameUk) : globalLanguage === 'en' ? (found.nameEn || found.nameUk) : found.nameUk;
    }
    switch (agentId) {
      case 'military-codifier': return globalLanguage === 'pl' ? 'Kodyfikator Uzbrojenia MON' : globalLanguage === 'en' ? 'MoD Military Codifier' : 'Fast-Track Кодифікатор ОВТ (МОУ / Brave1)';
      case 'research': return 'Deep Research';
      case 'space-center-hub': return globalLanguage === 'pl' ? 'Centrum Kosmiczne & Teledetekcja' : globalLanguage === 'en' ? 'Space Center & Earth Observation' : 'Космічний Центр, Навігаційне Поле & ДЗЗ';
      case 'tax-center-hub': return globalLanguage === 'pl' ? 'Centrum Podatkowo-Księgowe' : globalLanguage === 'en' ? 'Tax & Accounting Center' : 'Податковий Центр & Декларації ДПС';
      case 'business-center-hub': return globalLanguage === 'pl' ? 'Centrum Prawne & Biznesowe' : globalLanguage === 'en' ? 'Business & Legal Center' : 'Бізнес, Юриспруденція & Консалтинг';
      case 'energy-center-hub': return globalLanguage === 'pl' ? 'Centrum Audytu Energetycznego & ESG' : globalLanguage === 'en' ? 'Energy Audit & ESG Center' : 'Центр Енергоаудиту & Енергоменеджменту';
      case 'agri-center-hub': return globalLanguage === 'pl' ? 'Centrum Rolnicze & EGiB' : globalLanguage === 'en' ? 'Agribusiness & Land Hub' : 'Агропромисловий & Земельний Центр';
      case 'logistics-center-hub': return globalLanguage === 'pl' ? 'Logistyka Międzynarodowa & Cło' : globalLanguage === 'en' ? 'International Logistics & Customs' : 'Міжнародна Логістика & Митниця';
      case 'cyber-center-hub': return globalLanguage === 'pl' ? 'Centrum Cyberbezpieczeństwa' : globalLanguage === 'en' ? 'Cybersecurity & IT Hub' : 'Центр Кібербезпеки, IT & КСЗІ';
      case 'construction-center-hub': return globalLanguage === 'pl' ? 'Centrum Budowlano-Kosztorysowe' : globalLanguage === 'en' ? 'Construction & Estimation Hub' : 'Будівельно-Кошторисний Центр';
      case 'science-center-hub': return globalLanguage === 'pl' ? 'Centrum Naukowe' : globalLanguage === 'en' ? 'Science Center' : 'Науковий Центр';
      case 'pharma-center-hub': return globalLanguage === 'pl' ? 'Centrum Farmaceutyczne' : globalLanguage === 'en' ? 'Pharmaceutical Center' : 'Фармацевтичний Центр';
      case 'eco-center-hub': return globalLanguage === 'pl' ? 'Centrum Ekologii & Green Deal' : globalLanguage === 'en' ? 'Eco & Green Deal Hub' : 'Екологічний Центр & Green Deal';
      case 'ukraine-center-hub': return globalLanguage === 'pl' ? 'Narodowe Centrum Operacyjne Ukrainy' : globalLanguage === 'en' ? 'Ukraine National Enterprise Hub' : 'Національний Операційний Центр України';
      case 'poland-center-hub': return globalLanguage === 'pl' ? 'Centrum Operacyjne Polska' : globalLanguage === 'en' ? 'Poland Operational Hub' : 'Польський Операційний Центр';
      case 'us-center-hub': return globalLanguage === 'pl' ? 'Centrum Operacyjne USA & Compliance' : globalLanguage === 'en' ? 'US Enterprise & Compliance Hub' : 'Операційний Центр США & Комплаєнс';
      case 'eu-center-hub': return globalLanguage === 'pl' ? 'Centrum Regulacyjne & Przetargowe UE' : globalLanguage === 'en' ? 'EU Enterprise & Regulatory Hub' : 'Європейський Операційний Центр';
      case 'cross-border-center-hub': return globalLanguage === 'pl' ? 'Centrum Integracji Transgranicznej' : globalLanguage === 'en' ? 'Cross-Border Law & Interoperability Hub' : 'Центр Транскордонної Інтеграції & Юрисдикцій';
      case 'office-center-hub': return globalLanguage === 'pl' ? 'Centrum Biurowe' : globalLanguage === 'en' ? 'Office Center' : 'Офісний Центр';
      case 'admin-center-hub': return globalLanguage === 'pl' ? 'Centrum Administracji' : globalLanguage === 'en' ? 'Admin Center' : 'Центр Адміністрування';
      case 'phd-center-hub': return globalLanguage === 'pl' ? 'Centrum Doktora Filozofii (PhD)' : globalLanguage === 'en' ? 'PhD Dissertation Hub' : 'Центр Доктора Філософії (PhD)';
      default: return agentId;
    }
  };

  const getCenterInfoForAgent = (agentId: string): { id: string; nameUk: string; nameEn: string } | null => {
    if (PHD_AGENT_IDS.includes(agentId)) {
      return { id: 'phd-center-hub', nameUk: 'Центру Доктора Філософії (PhD)', nameEn: 'PhD Dissertation Hub' };
    }
    if ([
      'office-spreadsheet-agent', 'dstu-order-constructor', 'dstu-pkmu-auditor', 
      'corrector', 'doc-translator', 'md-docx-converter', 'meeting-transcriber',
      'news-analyst', 'data-analyzer', 'video-renderer'
    ].includes(agentId)) {
      return { id: 'office-center-hub', nameUk: 'Офісного Центру', nameEn: 'Office Hub' };
    }

    if ([
      'fop-tax-declarator', 'corporate-income-tax-declarator', 'vat-tax-declarator', 
      'payroll-tax-declarator', 'resource-property-tax-declarator', 'excise-tax-declarator', 
      'special-tax-regimes-declarator', 'local-fees-declarator', 'tax-audit-calendar-agent', 
      'tax-financial-analyst', 'public-accountant-kekv', 'agri-mpz-calculator', 
      'cfo-financial-analyzer', 'budget-program-passport', 'budget-koshtorys-builder', 
      'npsbods-accounting-auditor', 'budget-payroll-auditor', 'dostupni-liky-reimbursement'
    ].includes(agentId)) {
      return { id: 'tax-center-hub', nameUk: 'Податкового Центру', nameEn: 'Tax Hub' };
    }

    if ([
      'energy-auditor-certifier', 'iso-50001-energy-manager', 'esco-green-investment-analyst', 
      'solar-wind-microgrid-designer', 'utility-smart-meter-analyst'
    ].includes(agentId)) {
      return { id: 'energy-center-hub', nameUk: 'Центру Енергоаудиту', nameEn: 'Energy Hub' };
    }

    if ([
      'agri-dar-subsidy-matcher', 'agri-npk-soil-analyzer', 'agri-land-lease-auditor', 
      'agri-satellite-yield-forecaster', 'agri-land-consultant'
    ].includes(agentId)) {
      return { id: 'agri-center-hub', nameUk: 'Агро-Центру', nameEn: 'Agri Hub' };
    }

    if ([
      'logistics-hs-code-classifier', 'logistics-equeue-optimizer', 
      'logistics-transfer-pricing-auditor', 'customs-compliance'
    ].includes(agentId)) {
      return { id: 'logistics-center-hub', nameUk: 'Центру Логістики & Митниці', nameEn: 'Logistics Hub' };
    }

    if ([
      'cyber-kszi-auditor', 'cyber-iso27001-nis2-compliance', 
      'cyber-siem-soc-incident-investigator', 'it-auditor'
    ].includes(agentId)) {
      return { id: 'cyber-center-hub', nameUk: 'Центру Кібербезпеки', nameEn: 'Cyber Hub' };
    }

    if ([
      'construction-diam-edessb-auditor', 'construction-war-damage-evaluator', 
      'construction-dpt-urban-planner', 'koshtorisnyk', 'property-valuator'
    ].includes(agentId)) {
      return { id: 'construction-center-hub', nameUk: 'Центру Будівництва', nameEn: 'Construction Hub' };
    }

    if ([
      'deep-research', 'science-grant-matching', 'science-scopus-wos-peer-reviewer', 
      'science-ukrnoivi-patent-auditor', 'science-phd-disseration-compliance', 'esg-eco-auditor'
    ].includes(agentId)) {
      return { id: 'science-center-hub', nameUk: 'Наукового Центру', nameEn: 'Science Hub' };
    }

    if ([
      'pharma-dlu-compatibility-checker', 'pharma-interaction-checker', 
      'pharma-quality-certificate-verifier', 'pharma-licensing-auditor', 
      'pharma-nhsu-reimbursement-auditor', 'pharma-advisor'
    ].includes(agentId)) {
      return { id: 'pharma-center-hub', nameUk: 'Фармацевтичного Центру', nameEn: 'Pharma Hub' };
    }

    if ([
      'solotvyno-geomonitor-orchestrator', 'insar-subsidence-timeseries-analyst',
      'gnss-sat-fusion-geodesist', 'karst-sinkhole-optical-morphometrist',
      'subsidence-trough-geomechanical-predictor', 'mine-infrastructure-structural-auditor',
      'hydro-karst-salt-dissolution-modeler', 'tisza-transboundary-eco-monitor', 'war-ecocide-environmental-damage-evaluator', 'illegal-quarry-amber-mining-skako-sentinel', 'hydrological-drought-groundwater-gnss-modeler', 'coastal-marine-oilspill-sat-inspector', 'industrial-air-emission-skako-auditor', 'landslide-slope-stability-gnss-analyst', 'wildfire-peatland-thermal-gnss-tracker', 'tailings-dam-geohazard-gnss-auditor', 'rad-nuclear-eco-satellite-monitor', 'soil-degradation-land-erosion-analyst', 'natura2000-emerald-biodiversity-monitor', 'waste-landfill-methane-sat-inspector', 'water-basin-eutrophication-sat-analyst', 'eudr-forest-carbon-satellite-auditor', 'eco-space-strategy-architect',
      'sat-image-harvester', 'sat-thematic-processor', 'sat-drought-water-stress-auditor', 'satellite-eco-analyst', 
      'agrisat-monitor', 'urban-sat-analyst', 'maritime-sat-analyst', 
      'disaster-sat-response', 'geoint-defense-analyst', 'sar-radar-analyst', 
      'space-spectrum-consultant', 'newspace-investment-analyst', 'satellite-payload-engineer', 
      'geological-sat-explorer', 'ghg-sat-tracker', 'ncuvkz-mission-control', 
      'ncuvkz-sda-debris-tracker', 'ncuvkz-space-weather-monitor', 'ncuvkz-gnss-geodesy-auditor', 
      'ncuvkz-spacecraft-testing-engineer', 'gcsk-seismic-auditor', 'gcsk-acoustic-infrasound-analyst', 
      'gcsk-radiation-chemical-monitor', 'gcsk-geomagnetic-seismoacoustic-analyst', 
      'gcsk-emergency-situation-notifier', 'knp-gnss-integrity-auditor', 
      'knp-rtk-diff-correction-operator', 'knp-time-frequency-metrologist', 
      'knp-antijamming-antispoofing-analyst', 'knp-defense-navigation-briefing-analyst', 
      'war-damage-sat-auditor'
    ].includes(agentId)) {
      return { id: 'space-center-hub', nameUk: 'Космічного Центру & ДЗЗ', nameEn: 'Space Hub' };
    }

    if (ADMIN_AGENT_IDS.includes(agentId)) {
      return { id: 'admin-center-hub', nameUk: 'Адмін-Центру', nameEn: 'Admin Hub', namePl: 'Centrum Admina' };
    }

    if (ECO_AGENT_IDS.includes(agentId)) {
      return { id: 'eco-center-hub', nameUk: 'Екологічного Центру & Green Deal', nameEn: 'Eco & Green Deal Hub', namePl: 'Centrum Ekologii & Green Deal' };
    }

    if (BUSINESS_AGENT_IDS.includes(agentId)) {
      return { id: 'business-center-hub', nameUk: 'Бізнес-Центру', nameEn: 'Business Hub' };
    }

    if (POLAND_AGENT_IDS.includes(agentId)) {
      return { id: 'poland-center-hub', nameUk: 'Польського Центру', nameEn: 'Poland Hub' };
    }

    if (US_AGENT_IDS.includes(agentId)) {
      return { id: 'us-center-hub', nameUk: 'Центру США', nameEn: 'US Hub' };
    }

    if (EU_AGENT_IDS.includes(agentId)) {
      return { id: 'eu-center-hub', nameUk: 'Європейського Центру', nameEn: 'EU Hub' };
    }

    if (CROSS_BORDER_AGENT_IDS.includes(agentId)) {
      return { id: 'cross-border-center-hub', nameUk: 'Центру Транскордонної Інтеграції', nameEn: 'Cross-Border Hub' };
    }

    return null;
  };

  const getReasonText = (reason?: string) => {
    switch (reason) {
      case 'NOT_PAID': return 'Не оплачено';
      case 'EXPIRED': return 'Термін підписки закінчився (30 днів)';
      case 'REQUESTS_EXPIRED': return 'Термін використання запитів закінчився (30 днів)';
      case 'LIMIT_EXHAUSTED': return 'Вичерпано ліміт запитів';
      case 'INSUFFICIENT_BALANCE': return 'Недостатньо коштів на балансі';
      default: return 'Доступ обмежено';
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return dateStr;
    }
  };

  const renderAgentWithSubscription = (agentId: string, component: React.ReactNode) => {
    if (user?.role === 'admin' || user?.is_enterprise_admin || ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(user?.email?.toLowerCase() || '')) return component;

    // Співробітники підприємства: агенти, призначені адміністратором НЦУВКЗ, є БЕЗКОШТОВНИМИ
    if (user?.enterprise_id) {
      const allowed = Array.isArray(user.allowed_enterprise_agents) ? user.allowed_enterprise_agents : [];
      if (allowed.length > 0 && (allowed.includes(agentId) || allowed.includes(activeTab))) {
        return component;
      }
    }

    const sub = subscriptions[agentId];
    if (subscriptionLoading && !sub) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-12 flex flex-col items-center justify-center gap-4 text-center min-h-[400px]">
          <RefreshCw className="w-8 h-8 text-[#1E3A8A] animate-spin" />
          <p className="text-xs font-mono text-slate-500 uppercase tracking-widest">Перевірка статусу підписки...</p>
        </div>
      );
    }

    if (sub && !sub.allowed) {
      return (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-xl mx-auto text-center space-y-6 shadow-[0_10px_30px_rgba(30,58,138,0.05)] border-t-4 border-t-red-500">
          <div className="mx-auto bg-red-50 w-14 h-14 flex items-center justify-center rounded-2xl border border-red-200">
            <Lock className="w-7 h-7 text-red-500 font-extrabold" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase">
              Доступ до агента <span className="text-red-500 font-mono">Обмежено</span>
            </h2>
            <p className="text-[10px] text-slate-400 font-mono tracking-widest uppercase">Потрібна оплата підписки</p>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-left space-y-2.5 font-mono text-[11px] text-slate-600">
            <div className="flex justify-between border-b border-slate-200 pb-1.5">
              <span className="font-bold">Агент:</span>
              <span className="text-slate-800 font-semibold">{getAgentName(agentId)}</span>
            </div>
            <div className="flex justify-between border-b border-slate-200 pb-1.5">
              <span className="font-bold">Статус підписки:</span>
              <span className="text-red-500 font-semibold uppercase">{getReasonText(sub.reason)}</span>
            </div>
            {sub.isPaid && (
              <>
                <div className="flex justify-between border-b border-slate-200 pb-1.5">
                  <span className="font-bold">Запитів використано:</span>
                  <span className="text-slate-800">{sub.requestsUsed} / {sub.requestsLimit}</span>
                </div>
                <div className="flex justify-between border-b border-slate-200 pb-1.5">
                  <span className="font-bold">Дія запитів закінчується:</span>
                  <span className="text-slate-800">{formatDate(sub.requestsExpiresAt)}</span>
                </div>
                <div className="flex justify-between pb-0.5">
                  <span className="font-bold">Дія підписки закінчується:</span>
                  <span className="text-slate-800">{formatDate(sub.expiresAt)}</span>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            {/* Option A: Month Subscription */}
            <div className="bg-amber-50/40 border border-amber-100 rounded-xl p-4 flex flex-col justify-between space-y-3">
              <div className="space-y-2">
                <h4 className="text-[11px] font-bold text-amber-600 uppercase tracking-wider flex items-center gap-1.5">
                  <span>💎</span> Місячний пакет
                </h4>
                <div className="space-y-1 text-xs">
                  <p className="text-[10px] text-slate-400 font-mono uppercase">
                    {globalLanguage === 'uk' ? 'Ліміт запитів:' : globalLanguage === 'pl' ? 'Limit zapytań:' : 'Request limit:'}{' '}
                    <span className="text-slate-750 font-bold font-sans">
                      {sub.limitRequests} {globalLanguage === 'uk' ? 'зап. / 30 дн.' : globalLanguage === 'pl' ? 'zap. / 30 dni' : 'req. / 30 days'}
                    </span>
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono uppercase">
                    {globalLanguage === 'uk' ? 'Ціна пакета:' : globalLanguage === 'pl' ? 'Cena pakietu:' : 'Package price:'}{' '}
                    <span className="text-slate-750 font-bold font-sans">{formatPriceWithCurrency(sub.price, currency)}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleInitiatePayment(agentId)}
                disabled={paymentLoading === agentId}
                className="w-full py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold text-[10px] uppercase tracking-wider rounded transition-all shadow-xs cursor-pointer flex items-center justify-center gap-1 font-mono"
              >
                {paymentLoading === agentId ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <span>
                    {globalLanguage === 'uk' ? `Оплатити ${formatPriceWithCurrency(sub.price, currency)}` : globalLanguage === 'pl' ? `Opłać ${formatPriceWithCurrency(sub.price, currency)}` : `Pay ${formatPriceWithCurrency(sub.price, currency)}`}
                  </span>
                )}
              </button>
            </div>

            {/* Option B: Pay per request */}
            {sub.singlePrice > 0 && (
              <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-4 flex flex-col justify-between space-y-3">
                <div className="space-y-2">
                  <h4 className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1.5">
                    <span>⚡</span> {globalLanguage === 'uk' ? 'Разовий запуск (Pay-per-request)' : globalLanguage === 'pl' ? 'Pojedyncze uruchomienie (Pay-per-request)' : 'Single Run (Pay-per-request)'}
                  </h4>
                  <div className="space-y-1 text-xs">
                    <p className="text-[10px] text-slate-400 font-mono uppercase">
                      {globalLanguage === 'uk' ? 'Вартість запуску:' : globalLanguage === 'pl' ? 'Koszt zapytania:' : 'Per-request price:'}{' '}
                      <span className="text-slate-750 font-bold font-sans">
                        {formatPriceWithCurrency(sub.singlePrice, currency)} / {globalLanguage === 'uk' ? 'запит' : globalLanguage === 'pl' ? 'zapytanie' : 'req.'}
                      </span>
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono uppercase">
                      {globalLanguage === 'uk' ? 'Ваш баланс:' : globalLanguage === 'pl' ? 'Twój bilans:' : 'Your balance:'}{' '}
                      <span className="text-slate-750 font-bold font-sans">{formatPriceWithCurrency(sub.balance || 0, currency)}</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setTopUpAmount(Math.max(10, sub.singlePrice - (sub.balance || 0)).toFixed(0));
                    setShowTopUpModal(true);
                  }}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[10px] uppercase tracking-wider rounded transition-all shadow-xs cursor-pointer flex items-center justify-center gap-1 font-mono"
                >
                  <Plus className="w-3 h-3" />
                  Поповнити баланс
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-center pt-2">
            <button
              onClick={() => setActiveTab('hub')}
              className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 font-bold text-xs uppercase tracking-wider rounded-lg transition-all cursor-pointer font-mono"
            >
              Повернутися на Головну
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4 w-full">
        {sub && sub.isFreeTrial && (
          <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 font-mono text-[11px] text-amber-850 shadow-xs">
            <div className="flex items-center gap-2">
              <span className="text-base animate-pulse">🎁</span>
              <span>
                {globalLanguage === 'uk' 
                  ? 'Ви використовуєте 1 безкоштовний тестовий запит для цього агента. Наступні запити будуть платними.' 
                  : 'You are using 1 free trial request for this agent. Subsequent requests will be paid.'}
              </span>
            </div>
            <button 
              onClick={() => handleInitiatePayment(agentId)}
              disabled={paymentLoading === agentId}
              className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-200 text-white rounded-lg font-bold uppercase transition-colors text-[10px] tracking-wider self-start sm:self-auto cursor-pointer"
            >
              {paymentLoading === agentId ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                globalLanguage === 'uk' ? 'Придбати підписку' : 'Buy Subscription'
              )}
            </button>
          </div>
        )}
        {component}
      </div>
    );
  };

  // Password-based 2nd Factor Front-end States
  const [passwordInput, setPasswordInput] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const user = authData?.user;

  // Check authentication status on mount
  const checkAuth = async () => {
    try {
      const response = await fetch("/api/user");
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setAuthData(data);
        } else {
          setAuthData(null);
        }
      } else {
        setAuthData(null);
      }
    } catch (err) {
      console.error("Failed to check auth", err);
      setAuthData(null);
    } finally {
      setIsLoadingUser(false);
    }
  };

  // 📱 Mobile In-App Browser (Webview) detection
  const isMobileWebview = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = (navigator.userAgent || navigator.vendor || (window as any).opera || '').toLowerCase();
    return /telegram|fban|fbav|instagram|threads|viber|line|micromessenger|wechat|linkedinapp/i.test(ua);
  }, []);

  // Multi-method login states & last used persistence
  const [lastAuthMethod, setLastAuthMethod] = useState<string | null>(() => {
    try {
      return localStorage.getItem('last_auth_method') || null;
    } catch (e) {
      return null;
    }
  });
  const [showEmailAuth, setShowEmailAuth] = useState(() => {
    try {
      return localStorage.getItem('last_auth_method') === 'email';
    } catch (e) {
      return false;
    }
  });
  const [otpEmail, setOtpEmail] = useState(() => {
    try {
      return localStorage.getItem('last_auth_email') || '';
    } catch (e) {
      return '';
    }
  });
  const [otpCode, setOtpCode] = useState('');
  const [otpStep, setOtpStep] = useState<'enter_email' | 'enter_code'>('enter_email');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSuccessMsg, setOtpSuccessMsg] = useState<string | null>(null);
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);

  useEffect(() => {
    let timer: any;
    if (otpResendCountdown > 0) {
      timer = setTimeout(() => setOtpResendCountdown(prev => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpResendCountdown]);

  const handleSendOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!otpEmail || !otpEmail.includes('@')) {
      setOtpError(
        globalLanguage === 'pl' ? 'Podaj prawidłowy adres e-mail' :
        globalLanguage === 'en' ? 'Please enter a valid email address' :
        globalLanguage === 'de' ? 'Bitte geben Sie eine gültige E-Mail-Adresse ein' :
        globalLanguage === 'fr' ? 'Veuillez saisir une adresse e-mail valide' :
        'Будь ласка, введіть коректну адресу електронної пошти'
      );
      return;
    }

    setOtpLoading(true);
    setOtpError(null);
    setOtpSuccessMsg(null);

    try {
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail.trim(), language: globalLanguage })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setOtpStep('enter_code');
        setOtpSuccessMsg(data.message);
        setOtpResendCountdown(60);
      } else {
        setOtpError(data.error || 'Помилка відправки коду');
      }
    } catch (err: any) {
      setOtpError('Помилка з\'єднання з сервером');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanCode = otpCode.trim();
    if (!cleanCode || cleanCode.length < 6) {
      setOtpError(
        globalLanguage === 'pl' ? 'Wpisz 6-cyfrowy kod' :
        globalLanguage === 'en' ? 'Please enter 6-digit code' :
        globalLanguage === 'de' ? 'Bitte geben Sie den 6-stelligen Code ein' :
        globalLanguage === 'fr' ? 'Veuillez saisir le code à 6 chiffres' :
        'Введіть 6-значний код із листа'
      );
      return;
    }

    setOtpLoading(true);
    setOtpError(null);

    try {
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail.trim(), code: cleanCode })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        try {
          localStorage.setItem('last_auth_method', 'email');
          localStorage.setItem('last_auth_email', otpEmail.trim());
          setLastAuthMethod('email');
        } catch (e) {}
        await checkAuth();
      } else {
        setOtpError(data.error || 'Невірний код перевірки');
      }
    } catch (err: any) {
      setOtpError('Помилка з\'єднання з сервером');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSetupPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput || passwordInput.length < 4) {
      setPasswordError(globalLanguage === 'uk' ? 'Пароль повинен містити не менше 4 символів' : 'Password must be at least 4 characters');
      return;
    }
    if (passwordInput !== confirmPassword) {
      setPasswordError(globalLanguage === 'uk' ? 'Паролі не збігаються' : 'Passwords do not match');
      return;
    }

    setIsVerifyingPassword(true);
    setPasswordError(null);

    try {
      const response = await fetch('/api/auth/2fa/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput })
      });

      if (response.ok) {
        setPasswordInput('');
        setConfirmPassword('');
        await checkAuth();
      } else {
        const data = await response.json();
        setPasswordError(data.error || (globalLanguage === 'uk' ? 'Не вдалося встановити пароль' : 'Failed to set password'));
      }
    } catch (err) {
      console.error(err);
      setPasswordError(globalLanguage === 'uk' ? 'Помилка мережі при встановленні пароля' : 'Network error setting password');
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  const handleVerifyPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput) {
      setPasswordError(globalLanguage === 'uk' ? 'Будь ласка, введіть пароль' : 'Please enter password');
      return;
    }

    setIsVerifyingPassword(true);
    setPasswordError(null);

    try {
      const response = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput })
      });

      if (response.ok) {
        setPasswordInput('');
        await checkAuth();
      } else {
        const data = await response.json();
        setPasswordError(data.error || (globalLanguage === 'uk' ? 'Неправильний пароль' : 'Incorrect password'));
      }
    } catch (err) {
      console.error(err);
      setPasswordError(globalLanguage === 'uk' ? 'Помилка мережі при перевірці пароля' : 'Network error verifying password');
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  useEffect(() => {
    if (authData?.authenticated) {
      try {
        const pendingTab = localStorage.getItem('pending_agent_tab');
        if (pendingTab) {
          localStorage.removeItem('pending_agent_tab');
          setActiveTab(pendingTab as any);
        }
      } catch (e) {
        console.error('Error restoring pending agent tab:', e);
      }
    }
  }, [authData]);

  // Notify admin Telegram when a visitor opens the website / login screen
  useEffect(() => {
    if (!isLoadingUser) {
      try {
        const hasNotifiedSession = sessionStorage.getItem('drh_site_visit_notified');
        if (!hasNotifiedSession) {
          sessionStorage.setItem('drh_site_visit_notified', 'true');
          const currentHostname = typeof window !== 'undefined' ? window.location.hostname : 'deepresearchhub.online';
          const currentHref = typeof window !== 'undefined' ? window.location.href : 'https://deepresearchhub.online/';
          const isLoginPage = !authData || !authData.authenticated;

          fetch('/api/notify-login-visit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              domain: currentHostname,
              fullUrl: currentHref,
              userEmail: authData?.user?.email || null,
              pageType: isLoginPage ? 'Сторінка авторизації / Входу' : 'Головний робочий простір Хабу',
              jurisdiction: jurisdiction || 'UA',
              language: globalLanguage || 'uk',
              referrer: document.referrer || 'direct',
              screenWidth: typeof window !== 'undefined' ? window.innerWidth : null
            })
          })
          .then(res => res.json())
          .then(data => {
            if (data?.recommendation) {
              applyGeoRecommendation(data.recommendation, isLoginPage);
            }
          })
          .catch(() => {});
        }
      } catch (err) {
        console.warn('Error sending site visit notification:', err);
      }
    }
  }, [isLoadingUser, authData, jurisdiction, globalLanguage]);

  const handleLaunchAgentOnLogin = (agentId: string, preferredLang?: any, hubContext?: string, dynamicAgentData?: any) => {
    if (agentId === 'dynamic-on-the-fly-agent' || dynamicAgentData) {
      if (dynamicAgentData) {
        setActiveDynamicAgent(dynamicAgentData);
        try {
          localStorage.setItem('drh_active_dynamic_agent', JSON.stringify(dynamicAgentData));
        } catch (e) {}
      }
      setAcceptedOffer(true);
      setActiveTab('dynamic-on-the-fly-agent');
      setAuthData({ authenticated: true, user: { email: 'guest@deepresearch.hub', name: 'Гість платформи', role: 'guest' } });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      localStorage.setItem('pending_agent_tab', agentId);
      setAcceptedOffer(true);
      localStorage.setItem('acceptedOffer', 'true');
    } catch (err) {
      console.error(err);
    }

    const foundAgent = (agentsConfig as any[]).find((a) => a.id === agentId);
    const agentName = foundAgent ? (globalLanguage === 'uk' ? foundAgent.nameUk : foundAgent.nameEn) : agentId;

    setPendingAgentNotice(
      globalLanguage === 'uk'
        ? `🚀 Для запуску обраного агента «${agentName}» авторизуйтесь через Google. (1 безкоштовний запуск вже включено!)`
        : `🚀 To launch agent "${agentName}", please sign in with Google. (1 free request included!)`
    );

    const loginBox = document.getElementById('login-card-section');
    if (loginBox) {
      loginBox.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    checkAuth();
    
    // Check for payment success parameter in the URL
    try {
      const params = new URLSearchParams(window.location.search);
      const isPaymentSuccess = params.get('payment') === 'success';
      const paymentType = params.get('type');
      
      if (isPaymentSuccess) {
        // Track the conversion in Google Ads
        trackConversion('payment_success', 1.0); 
        trackEvent('payment_success', 'billing', paymentType || 'subscription');
        
        // Remove payment params from URL so reloading doesn't re-trigger conversion
        let newSearch = window.location.search
          .replace(/[?&]payment=success/, '')
          .replace(/[?&]type=[^&]+/, '')
          .replace(/[?&]agentId=[^&]+/, '');
        if (newSearch === '?' || newSearch === '&') {
          newSearch = '';
        }
        const newUrl = window.location.pathname + newSearch;
        window.history.replaceState({}, document.title, newUrl);
      }
    } catch (e) {
      console.error('Error handling payment success parameter', e);
    }
  }, []);

  useEffect(() => {
    const agentTabs = ['research', 'corrector', 'koshtorisnyk', 'video-renderer', 'contract-auditor', 'aml-auditor', 'tender-analyst', 'legal-doc-creator', 'legislation-tracker', 'court-compliance', 'dstu-order-constructor', 'ncuvkz-universal-consultant', 'ncuvkz-fin-messenger', 'hr-compliance', 'commercial-contract-auditor', 'tax-financial-analyst', 'market-intelligence', 'grant-writer', 'customs-compliance', 'ip-guard', 'it-auditor', 'news-analyst', 'agri-land-consultant', 'public-accountant-kekv', 'esg-eco-auditor', 'solotvyno-geomonitor-orchestrator', 'insar-subsidence-timeseries-analyst', 'gnss-sat-fusion-geodesist', 'karst-sinkhole-optical-morphometrist', 'subsidence-trough-geomechanical-predictor', 'mine-infrastructure-structural-auditor', 'hydro-karst-salt-dissolution-modeler', 'tisza-transboundary-eco-monitor', 'war-ecocide-environmental-damage-evaluator', 'illegal-quarry-amber-mining-skako-sentinel', 'hydrological-drought-groundwater-gnss-modeler', 'coastal-marine-oilspill-sat-inspector', 'industrial-air-emission-skako-auditor', 'landslide-slope-stability-gnss-analyst', 'wildfire-peatland-thermal-gnss-tracker', 'tailings-dam-geohazard-gnss-auditor', 'rad-nuclear-eco-satellite-monitor', 'soil-degradation-land-erosion-analyst', 'natura2000-emerald-biodiversity-monitor', 'waste-landfill-methane-sat-inspector', 'water-basin-eutrophication-sat-analyst', 'eudr-forest-carbon-satellite-auditor', 'eco-space-strategy-architect', 'satellite-eco-analyst', 'sat-drought-water-stress-auditor', 'agrisat-monitor', 'urban-sat-analyst', 'maritime-sat-analyst', 'disaster-sat-response', 'geoint-defense-analyst', 'sar-radar-analyst', 'space-spectrum-consultant', 'newspace-investment-analyst', 'satellite-payload-engineer', 'geological-sat-explorer', 'ghg-sat-tracker', 'ncuvkz-mission-control', 'ncuvkz-sda-debris-tracker', 'ncuvkz-space-weather-monitor', 'ncuvkz-gnss-geodesy-auditor', 'ncuvkz-spacecraft-testing-engineer', 'gcsk-seismic-auditor', 'gcsk-acoustic-infrasound-analyst', 'gcsk-radiation-chemical-monitor', 'gcsk-geomagnetic-seismoacoustic-analyst', 'gcsk-emergency-situation-notifier', 'knp-gnss-integrity-auditor', 'knp-rtk-diff-correction-operator', 'knp-time-frequency-metrologist', 'knp-antijamming-antispoofing-analyst', 'knp-defense-navigation-briefing-analyst', 'war-damage-sat-auditor', 'sat-image-harvester', 'doc-translator', 'property-valuator', 'dstu-pkmu-auditor'];
    if (agentTabs.includes(activeTab) && authData?.authenticated) {
      const subKey = (
        activeTab === 'aml-auditor' || 
        activeTab === 'tender-analyst' || 
        activeTab === 'legal-doc-creator' || 
        activeTab === 'legislation-tracker' || 
        activeTab === 'court-compliance' ||
        activeTab === 'dstu-order-constructor' ||
        activeTab === 'ncuvkz-universal-consultant' ||
        activeTab === 'hr-compliance' ||
        activeTab === 'commercial-contract-auditor' ||
        activeTab === 'tax-financial-analyst' ||
        activeTab === 'market-intelligence' ||
        activeTab === 'grant-writer' ||
        activeTab === 'customs-compliance' ||
        activeTab === 'ip-guard' ||
        activeTab === 'it-auditor' ||
        activeTab === 'agri-land-consultant' ||
        activeTab === 'public-accountant-kekv' ||
        activeTab === 'esg-eco-auditor' ||
        activeTab === 'solotvyno-geomonitor-orchestrator' ||
        activeTab === 'insar-subsidence-timeseries-analyst' ||
        activeTab === 'gnss-sat-fusion-geodesist' ||
        activeTab === 'karst-sinkhole-optical-morphometrist' ||
        activeTab === 'subsidence-trough-geomechanical-predictor' ||
        activeTab === 'mine-infrastructure-structural-auditor' ||
        activeTab === 'hydro-karst-salt-dissolution-modeler' ||
        activeTab === 'tisza-transboundary-eco-monitor' ||
        activeTab === 'war-ecocide-environmental-damage-evaluator' ||
        activeTab === 'illegal-quarry-amber-mining-skako-sentinel' ||
        activeTab === 'hydrological-drought-groundwater-gnss-modeler' ||
        activeTab === 'coastal-marine-oilspill-sat-inspector' ||
        activeTab === 'industrial-air-emission-skako-auditor' ||
        activeTab === 'landslide-slope-stability-gnss-analyst' ||
        activeTab === 'wildfire-peatland-thermal-gnss-tracker' ||
        activeTab === 'tailings-dam-geohazard-gnss-auditor' ||
        activeTab === 'rad-nuclear-eco-satellite-monitor' ||
        activeTab === 'soil-degradation-land-erosion-analyst' ||
        activeTab === 'natura2000-emerald-biodiversity-monitor' ||
        activeTab === 'waste-landfill-methane-sat-inspector' ||
        activeTab === 'water-basin-eutrophication-sat-analyst' ||
        activeTab === 'eudr-forest-carbon-satellite-auditor' ||
        activeTab === 'eco-space-strategy-architect' ||
        activeTab === 'satellite-eco-analyst' ||
        activeTab === 'sat-drought-water-stress-auditor' ||
        activeTab === 'agrisat-monitor' ||
        activeTab === 'urban-sat-analyst' ||
        activeTab === 'maritime-sat-analyst' ||
        activeTab === 'disaster-sat-response' ||
        activeTab === 'geoint-defense-analyst' ||
        activeTab === 'sar-radar-analyst' ||
        activeTab === 'space-spectrum-consultant' ||
        activeTab === 'newspace-investment-analyst' ||
        activeTab === 'satellite-payload-engineer' ||
        activeTab === 'geological-sat-explorer' ||
        activeTab === 'ghg-sat-tracker' ||
        activeTab === 'ncuvkz-mission-control' ||
        activeTab === 'ncuvkz-sda-debris-tracker' ||
        activeTab === 'ncuvkz-space-weather-monitor' ||
        activeTab === 'ncuvkz-gnss-geodesy-auditor' ||
        activeTab === 'ncuvkz-spacecraft-testing-engineer' ||
        activeTab === 'gcsk-seismic-auditor' ||
        activeTab === 'gcsk-acoustic-infrasound-analyst' ||
        activeTab === 'gcsk-radiation-chemical-monitor' ||
        activeTab === 'gcsk-geomagnetic-seismoacoustic-analyst' ||
        activeTab === 'gcsk-emergency-situation-notifier' ||
        activeTab === 'knp-gnss-integrity-auditor' ||
        activeTab === 'knp-rtk-diff-correction-operator' ||
        activeTab === 'knp-time-frequency-metrologist' ||
        activeTab === 'knp-antijamming-antispoofing-analyst' ||
        activeTab === 'knp-defense-navigation-briefing-analyst' ||
        activeTab === 'war-damage-sat-auditor' ||
        activeTab === 'sat-image-harvester' ||
        activeTab === 'doc-translator' ||
        activeTab === 'property-valuator' ||
        activeTab === 'md-docx-converter'
      ) ? 'contract-auditor' : activeTab;
      loadSubscriptionStatus(subKey);
    }
  }, [activeTab, authData]);

  // Load templates and sync on language swap
  useEffect(() => {
    try {
      const stored = localStorage.getItem('deep_research_templates');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setTemplates(parsed);
          // Set active template if valid
          if (parsed.find(t => t.id === activeTemplateId)) {
            // Keep active
          } else {
            setActiveTemplateId(parsed[0].id);
          }
          return;
        }
      }
      
      // Default initialization
      const defaults = globalLanguage === 'uk' ? DEFAULT_TEMPLATES_UK : DEFAULT_TEMPLATES_EN;
      setTemplates(defaults);
      setActiveTemplateId(defaults[0].id);
      localStorage.setItem('deep_research_templates', JSON.stringify(defaults));
    } catch (e) {
      console.error('Failed to load templates', e);
    }
  }, [globalLanguage]);

  const saveTemplates = (newTemplates: ReportTemplate[]) => {
    setTemplates(newTemplates);
    try {
      localStorage.setItem('deep_research_templates', JSON.stringify(newTemplates));
    } catch (e) {
      console.error('Failed to save templates', e);
    }
  };

  const handleResetTemplates = () => {
    const defaults = globalLanguage === 'uk' ? DEFAULT_TEMPLATES_UK : DEFAULT_TEMPLATES_EN;
    saveTemplates(defaults);
    setActiveTemplateId(defaults[0].id);
  };

  const handleReportGeneratedFromData = (report: ResearchReport, query: string) => {
    const sessionId = 'session_' + Date.now();
    const completedSess: ResearchSession = {
      id: sessionId,
      query,
      options: {
        depth: 'standard',
        focus: 'general',
        format: 'comprehensive',
        language: globalLanguage,
        templateId: activeTemplateId
      },
      status: 'completed',
      progress: 100,
      steps: [
        {
          id: 'synthesis-step',
          type: 'synthesize',
          title: globalLanguage === 'uk' ? 'Звіт успішно синтезовано' : 'Report Successfully Synthesized',
          description: globalLanguage === 'uk' ? 'Інтегровано таблиці, інтерактивні діаграми та аналітику.' : 'Tables, diagrams and statistics integrated.',
          timestamp: new Date().toLocaleTimeString(),
          status: 'completed'
        }
      ],
      report,
      createdAt: new Date().toISOString()
    };

    setActiveSession(completedSess);
    setSessions(history => {
      const updatedHistory = [completedSess, ...history];
      saveSessionsToLocal(updatedHistory);
      return updatedHistory;
    });

    // Automatically switch back to research view to view the report doc!
    setActiveTab('research');
  };

  // Load past sessions from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('deep_research_sessions');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setSessions(parsed);
        }
      }
    } catch (e) {
      console.error('Failed to load past sessions from localStorage', e);
    }
  }, []);

  // Save sessions to localStorage when updated
  const saveSessionsToLocal = (newSessions: ResearchSession[]) => {
    try {
      localStorage.setItem('deep_research_sessions', JSON.stringify(newSessions));
    } catch (e) {
      console.error('Failed to save sessions to localStorage', e);
    }
  };

  const handleStartResearch = async (query: string, options: ResearchOptions) => {
    if (isSearching) return;

    setGlobalError(null);
    setIsSearching(true);

    const sessionId = 'session_' + Date.now();
    const initialSession: ResearchSession = {
      id: sessionId,
      query,
      options,
      status: 'planning',
      progress: 5,
      steps: [],
      report: null,
      createdAt: new Date().toISOString()
    };

    setActiveSession(initialSession);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const reqHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (customKey) {
        reqHeaders['x-api-key'] = customKey;
      }

      const response = await fetch('/api/research', {
        method: 'POST',
        headers: reqHeaders,
        body: JSON.stringify({ 
          query, 
          options: { ...options, useEnterpriseContext } 
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to initiate Deep Research on the server. Make sure GEMINI_API_KEY is configured.');
      }

      // Track conversion & event in Google Ads
      trackConversion('start_research');
      trackEvent('start_research', 'research', query);

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Server returned an unreadable progress stream.');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the last partial line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          try {
            const rawJSON = trimmed.substring(6);
            const { event, data } = JSON.parse(rawJSON);

            if (event === 'status') {
              setActiveSession(prev => {
                if (!prev) return null;
                return {
                  ...prev,
                  status: data.status,
                  progress: data.progress
                };
              });
            } else if (event === 'step') {
              const stepData = data as ResearchStep;
              setActiveSession(prev => {
                if (!prev) return null;
                const updatedSteps = [...prev.steps];
                const existingIdx = updatedSteps.findIndex(s => s.id === stepData.id);
                
                if (existingIdx > -1) {
                  updatedSteps[existingIdx] = { ...updatedSteps[existingIdx], ...stepData };
                } else {
                  updatedSteps.push(stepData);
                }

                return {
                  ...prev,
                  steps: updatedSteps
                };
              });
            } else if (event === 'completed') {
              const finalReport = data as ResearchReport;
              
              setActiveSession(prev => {
                if (!prev) return null;
                const completedSess: ResearchSession = {
                  ...prev,
                  status: 'completed',
                  progress: 100,
                  report: finalReport
                };

                // Add to history list immediately
                setSessions(history => {
                  const filtered = history.filter(h => h.id !== prev.id);
                  const updatedHistory = [completedSess, ...filtered];
                  saveSessionsToLocal(updatedHistory);
                  return updatedHistory;
                });

                return completedSess;
              });
            } else if (event === 'error') {
              const errorPayload = data;
              throw new Error(errorPayload.message || 'An error occurred during search.');
            }
          } catch (jsonErr) {
            console.error('Error parsing streaming chunk:', jsonErr, line);
          }
        }
      }

    } catch (err: any) {
      console.error('Deep Research failure:', err);
      const errMsg = err.message || 'An unexpected server error occurred.';
      setGlobalError(errMsg);
      
      setActiveSession(prev => {
        if (!prev) return null;
        const failedSess: ResearchSession = {
          ...prev,
          status: 'failed',
          progress: 0,
          error: errMsg
        };

        return failedSess;
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSession = (id: string) => {
    const found = sessions.find(s => s.id === id);
    if (found) {
      setActiveSession(found);
      setGlobalError(null);
    }
  };

  const handleDeleteSession = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = sessions.filter(s => s.id !== id);
    setSessions(updated);
    saveSessionsToLocal(updated);
    if (activeSession?.id === id) {
      setActiveSession(null);
    }
  };

  const handleClearAllSessions = () => {
    const confirmation = window.confirm(
      globalLanguage === 'uk' 
        ? 'Ви дійсно бажаєте видалити всі звіти з історії?' 
        : 'Are you sure you want to delete all saved reports?'
    );
    if (confirmation) {
      setSessions([]);
      localStorage.removeItem('deep_research_sessions');
      setActiveSession(null);
    }
  };

  const handleNewResearch = () => {
    setActiveSession(null);
    setGlobalError(null);
  };

  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center font-sans">
        <div className="flex flex-col items-center gap-4 text-center">
          <RefreshCw className="w-8 h-8 text-[#1E3A8A] animate-spin" />
          <p className="text-xs font-mono text-slate-500 uppercase tracking-widest">Завантаження авторизації...</p>
        </div>
      </div>
    );
  }

  if (!authData || !authData.authenticated) {
    const t = LOGIN_I18N[globalLanguage] || LOGIN_I18N.uk;

    return (
      <div className="min-h-screen bg-[#FAFBFC] flex flex-col items-center justify-start sm:justify-center font-sans px-2 sm:px-4 py-3 sm:py-8 space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
        <div className="max-w-6xl w-full space-y-4 sm:space-y-6">
          
          {/* Top Navigation Bar with Brand, Jurisdiction, Language, Currency & Pricing on Login Screen */}
          <header className="bg-white/95 backdrop-blur-md border border-slate-200/90 rounded-2xl shadow-sm px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-3 sm:gap-4 flex-wrap w-full">
            <div className="flex items-center gap-3">
              <div className="bg-[#1E3A8A] w-9 h-9 flex items-center justify-center rounded-xl shadow-md text-white font-black text-xs shrink-0">
                DR
              </div>
              <div>
                <span className="text-xs sm:text-sm font-black text-slate-800 tracking-tight uppercase block leading-tight">
                  DEEP RESEARCH HUB
                </span>
                <span className="text-[9px] text-indigo-600 font-mono font-bold tracking-wider uppercase block">
                  AI Enterprise Ecosystem
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {/* Jurisdiction Selector */}
              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-mono font-bold flex-wrap">
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('UA')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'UA' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: Ukraine (ПКУ, ДСТУ, ДБН, Prozorro)"
                >
                  🇺🇦 UA
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('PL')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'PL' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: Poland (Kodeks cywilny, KSeF, CIT, TED)"
                >
                  🇵🇱 PL
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('DE')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'DE' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: Germany (BGB, HGB, XRechnung, VOB/B, LkSG)"
                >
                  🇩🇪 DE
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('FR')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'FR' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: France (Code de commerce, Factur-X, URSSAF, RE2020)"
                >
                  🇫🇷 FR
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('IT')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'IT' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: Italy (Codice Civile, SDI / FatturaPA, Codice Appalti)"
                >
                  🇮🇹 IT
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('ES')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'ES' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: Spain (Código Civil, FacturaE / Veri*Factu, LCSP)"
                >
                  🇪🇸 ES
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('PT')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'PT' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: Portugal (Código Comercial, SAF-T (PT), CCP)"
                >
                  🇵🇹 PT
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('SE')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'SE' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: Sweden (AB 04 / ABT 06, PEPPOL, Skatteverket, LOU)"
                >
                  🇸🇪 SE
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('EU')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'EU' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: European Union (EU Law, FIDIC, GDPR, TED)"
                >
                  🇪🇺 EU
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectJurisdiction('US')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    jurisdiction === 'US' ? 'bg-[#1E3A8A] text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Jurisdiction: United States (DGCL, FAR, IRS, OFAC, SOC 2)"
                >
                  🇺🇸 US
                </button>
              </div>

              {/* Language Switcher */}
              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-mono font-bold flex-wrap">
                <Languages className="w-3.5 h-3.5 text-indigo-600 ml-1 mr-0.5" />
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('uk')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'uk' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Українська мова"
                >
                  UA
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('de')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'de' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Deutsche Sprache"
                >
                  DE
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('fr')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'fr' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Langue Française"
                >
                  FR
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('pl')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'pl' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Język Polski"
                >
                  PL
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('en')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'en' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="English Language"
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('it')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'it' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Lingua Italiana"
                >
                  IT
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('es')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'es' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Idioma Español"
                >
                  ES
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('pt')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'pt' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Língua Portuguesa"
                >
                  PT
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchGlobalLanguage('sv')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    globalLanguage === 'sv' ? 'bg-indigo-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Svenska Språket"
                >
                  SV
                </button>
              </div>

              {/* Direct Currency Switcher */}
              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-mono font-bold flex-wrap" title="Вибір валюти / Currency / Waluta">
                <Coins className="w-3.5 h-3.5 text-emerald-600 ml-1 mr-0.5" />
                <button
                  type="button"
                  onClick={() => handleSelectCurrency('UAH')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    currency === 'UAH' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Гривня (₴ UAH)"
                >
                  ₴ UAH
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectCurrency('USD')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    currency === 'USD' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="US Dollar ($ USD)"
                >
                  $ USD
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectCurrency('EUR')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    currency === 'EUR' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Euro (€ EUR)"
                >
                  € EUR
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectCurrency('PLN')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    currency === 'PLN' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Polski Złoty (zł PLN)"
                >
                  zł PLN
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectCurrency('SEK')}
                  className={`px-1.5 py-1 rounded transition-all cursor-pointer ${
                    currency === 'SEK' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Svensk krona (kr SEK)"
                >
                  kr SEK
                </button>
              </div>

              {/* Desktop App Store Modal Button */}
              <button
                type="button"
                onClick={() => setShowDesktopStoreModal(true)}
                className="flex items-center gap-1.5 px-3 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[10px] font-mono font-bold shadow-xs transition-all cursor-pointer border border-slate-700 hover:border-indigo-400"
                title="Встановлення легкої програми на ПК (~18 МБ)"
              >
                <Monitor className="w-3.5 h-3.5 text-indigo-400" />
                <span>{globalLanguage === 'pl' ? '💻 Pulpit PC (~18 MB)' : globalLanguage === 'uk' ? '💻 Десктоп ПК (~18 МБ)' : globalLanguage === 'de' ? '💻 Desktop App (~18 MB)' : globalLanguage === 'fr' ? '💻 App Bureau (~18 MB)' : '💻 Desktop App (~18 MB)'}</span>
              </button>

              {/* Corporate Digital Office Button in Public Header */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowOfficeInfoModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-violet-700 via-indigo-700 to-purple-800 hover:from-violet-600 hover:to-purple-700 text-white rounded-lg text-[10px] font-mono font-bold shadow-md shadow-violet-900/30 transition-all cursor-pointer border border-violet-400/50"
                  title="Корпоративний Цифровий Офіс Підприємства (3 дні безкоштовно)"
                >
                  <Building2 className="w-3.5 h-3.5 text-amber-300" />
                  <span>{globalLanguage === 'pl' ? '🏢 Biuro Cyfrowe' : globalLanguage === 'en' ? '🏢 Digital Office' : '🏢 Корпоративний Цифровий Офіс'}</span>
                  <span className="bg-amber-400 text-slate-950 text-[9px] font-black px-1.5 py-0.2 rounded-full uppercase ml-0.5 animate-pulse">3 дні Free</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowOfficeInfoModal(true)}
                  className="p-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-violet-300 hover:text-white border border-violet-500/40 transition-colors cursor-pointer"
                  title="ℹ️ Про систему / Як це працює"
                >
                  <Info className="w-3.5 h-3.5 text-amber-300" />
                </button>
              </div>

              {/* Pricing Modal Button */}
              <button
                type="button"
                onClick={() => setShowPublicPricingModal(true)}
                className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-[10px] font-mono font-bold shadow-xs transition-all cursor-pointer"
                title="Відкрити повний каталог та тарифи"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
                <span>{t.pricingModalBtnText} ({currency})</span>
              </button>
            </div>
          </header>

          {/* 🌟 OUTCOME-DRIVEN HERO HEADER (CRO / PLG 2026) */}
          <div className="w-full text-center space-y-3 pt-2 pb-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-950/90 via-slate-900 to-teal-950/90 border border-emerald-500/50 shadow-lg text-[11px] font-mono font-black text-emerald-300 uppercase tracking-wider">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping shrink-0" />
              <span>
                {globalLanguage === 'uk' ? '🟢 БЕЗ БАНКІВСЬКОЇ КАРТИ • 🎁 1-Й ЗАПУСК 100% БЕЗКОШТОВНО • ⚡ РЕЗУЛЬТАТ ЗА 45 СЕКУНД' :
                 globalLanguage === 'pl' ? '🟢 BEZ KARTY PŁATNICZEJ • 🎁 1. URUCHOMIENIE GRATIS • ⚡ WYNIK W 45 SEKUND' :
                 globalLanguage === 'de' ? '🟢 KEINE KREDITKARTE ERFORDERLICH • 🎁 1. TEST 100% GRATIS • ⚡ ERGEBNIS IN 45 SEK.' :
                 globalLanguage === 'fr' ? '🟢 SANS CARTE BANCAIRE • 🎁 1ER ESSAI 100% GRATUIT • ⚡ RÉSULTAT EN 45 SECONDES' :
                 '🟢 NO CREDIT CARD REQUIRED • 🎁 1ST RUN 100% FREE • ⚡ RESULTS IN 45 SECONDS'}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight max-w-4xl mx-auto">
              {globalLanguage === 'uk' ? 'Звільніть до 15 годин щотижня завдяки миттєвій автоматизації юридичних, кошторисних та податкових операцій' :
               globalLanguage === 'pl' ? 'Zaoszczędź do 15 godzin tygodniowo dzięki błyskawicznej automatyzacji umów, kosztorysów i podatków' :
               globalLanguage === 'de' ? 'Sparen Sie bis zu 15 Stunden wöchentlich durch sofortige Automatisierung von Verträgen, Baukosten und Steuern' :
               globalLanguage === 'fr' ? 'Économisez jusqu\'à 15h par semaine grâce à l\'automatisation instantanée des contrats, devis et déclarations' :
               'Save up to 15 Hours Weekly with Instant Automation of Contracts, Estimates & Regulatory Compliance'}
            </h1>

            <p className="text-xs sm:text-sm text-slate-600 max-w-3xl mx-auto font-medium leading-relaxed">
              {globalLanguage === 'uk' ? '40+ спеціалізованих галузевих ШІ-модулів (ДСТУ 4163:2020, ДБН АВК-5, ПКУ ДПС, супутники Sentinel-2). Почніть роботу в 1 клік без пароля та банківської карти.' :
               globalLanguage === 'pl' ? '40+ specjalistycznych modułów AI (Prawo KSH, KNR SEKOCENBUD, KSeF/JPK_V7, EGiB). Rozpocznij w 1 kliknięciu bez karty i haseł.' :
               globalLanguage === 'de' ? '40+ spezialisierte KI-Module (VOB/DIN 276, XRechnung, DSGVO, Sentinel-2). Starten Sie mit 1 Klick ohne Kreditkarte.' :
               globalLanguage === 'fr' ? 'Plus de 40 modules sectoriels (Code civil, CCAG BTP, Factur-X, RGPD). Démarrez en 1 clic sans carte bancaire ni mot de passe.' :
               '40+ specialized industry AI modules (DSTU/FIDIC, Construction BOQ, Corporate Tax & Sentinel-2 GIS). Start in 1-click with zero friction.'}
            </p>
          </div>

          {/* Deep Brain OS 2026 Flagship Showcase on Public Landing Page */}
          <div className="w-full relative bg-gradient-to-r from-slate-950 via-emerald-950/85 to-slate-950 border-2 border-emerald-500/70 hover:border-emerald-400 rounded-2xl p-5 sm:p-6 shadow-2xl overflow-hidden transition-all text-white text-left">
            <div className="absolute top-0 right-0 -mr-12 -mt-12 w-80 h-80 bg-emerald-600/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 -ml-12 -mb-12 w-80 h-80 bg-teal-600/15 rounded-full blur-3xl pointer-events-none" />
            
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div className="space-y-2 max-w-3xl">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-emerald-500/30 text-emerald-200 border border-emerald-400/50 flex items-center gap-1.5 shadow-xs font-mono">
                    <Cpu className="w-3.5 h-3.5 text-amber-300" />
                    <span>🧠 {globalLanguage === 'pl' ? 'DEEP BRAIN OS • AUTONOMICZNA POWŁOKA AI' : globalLanguage === 'en' ? 'DEEP BRAIN OS • AUTONOMOUS AGENT HARNESS' : 'DEEP BRAIN OS • АВТОНОМНА ОБВ\'ЯЗКА ДЛЯ ШІ-АГЕНТІВ'}</span>
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/25 text-amber-300 border border-amber-400/50 flex items-center gap-1 font-mono animate-pulse">
                    🔥 {globalLanguage === 'pl' ? 'NOWOŚĆ 2026: 5 AGENTÓW' : globalLanguage === 'en' ? 'NEW 2026: 5-AGENT SOLVER' : 'НОВИНКА 2026: 5-АГЕНТНИЙ SOLVER'}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700 font-mono">
                    {globalLanguage === 'pl' ? 'WIECZNA PAMIĘĆ • ZERO CONTEXT ROT • 20+ SKILLS' : globalLanguage === 'en' ? 'PERSISTENT MEMORY • ZERO CONTEXT ROT • 20+ SKILLS' : 'ВІЧНА ПАМ\'ЯТЬ WIKI • ВАЛІДАЦІЯ КОДУ • 20+ НАВИЧОК • VARLOCK SHIELD'}
                  </span>
                </div>

                <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
                  <span>
                    {globalLanguage === 'pl' 
                      ? 'Deep Brain OS: Przekształć agentów AI w Senior Inżyniera z pamięcią i testami' 
                      : globalLanguage === 'en' 
                      ? 'Deep Brain OS: Turn AI Agents into a Senior Engineer with Memory & Tests' 
                      : 'Deep Brain OS: Перетворіть ШІ-агентів на Senior-інженера з вічною пам\'яттю та тестами'}
                  </span>
                </h2>

                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans font-medium">
                  {globalLanguage === 'pl' 
                    ? 'Gotowa komercyjna powłoka operacyjna dla Google Antigravity i LLM: skompilowana pamięć Wiki eliminuje degradację kontekstu, 5-agentowy potok weryfikacji kodu (Planner -> Ideator A/B -> Solver -> Testy -> Reviewer z Rollback) oraz 20+ gotowych umiejętności.'
                    : globalLanguage === 'en' 
                    ? 'Turnkey production harness for Google Antigravity & LLMs: write-time compiled LLM Wiki eliminates context rot, 5-agent ledger code verification pipeline with deterministic subprocess tests and atomic rollback, plus 20+ native engineering skills.'
                    : 'Готова інженерна обв\'язка для Google Antigravity та LLM: скомпільована довгострокова пам\'ять (LLM Wiki) назавжди усуває втрату контексту (Context Rot), 5-агентний конвеєр перевірки коду (Planner -> паралельні Ideators -> Solver -> тести в субпроцесі -> відкат Rollback Guard), захист секретів Varlock Shield та 20+ перевірених навичок.'}
                </p>

                <div className="flex items-center gap-3 pt-1 text-xs font-mono text-emerald-300">
                  <span>🛠️ Developer Kit: <strong>3 150 грн ($79)</strong></span>
                  <span className="text-slate-600">•</span>
                  <span>🚀 Cloud Server під ключ: <strong>3 550 грн/міс ($89)</strong></span>
                </div>
              </div>

              <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setDeepBrainOsInitialTab('pricing');
                    setShowDeepBrainOsModal(true);
                  }}
                  className="w-full sm:w-auto px-5 py-3.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-500 hover:from-emerald-500 hover:to-teal-400 text-white font-black text-xs sm:text-sm rounded-xl shadow-xl shadow-emerald-950/40 transition-all flex items-center justify-center gap-2 cursor-pointer border border-emerald-400/50 group hover:scale-[1.02]"
                >
                  <Sparkles className="w-4 h-4 text-amber-300 group-hover:rotate-12 transition-transform" />
                  <span>{globalLanguage === 'pl' ? '🚀 Kup licencję ($79) ➔' : globalLanguage === 'en' ? '🚀 Get License ($79) ➔' : '🚀 Придбати ліцензію ($79) ➔'}</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setDeepBrainOsInitialTab('architecture');
                    setShowDeepBrainOsModal(true);
                  }}
                  className="w-full sm:w-auto px-4 py-3.5 bg-slate-900/90 hover:bg-slate-800 text-emerald-300 hover:text-white font-bold text-xs sm:text-sm rounded-xl border border-emerald-500/40 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md"
                >
                  <Info className="w-4 h-4 text-amber-300" />
                  <span>{globalLanguage === 'pl' ? 'ℹ️ Architektura & Demo' : globalLanguage === 'en' ? 'ℹ️ Architecture & Demo' : 'ℹ️ Архітектура та Демо'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Corporate Digital Office Hero Showcase on Landing Page */}
          <div className="w-full relative bg-gradient-to-r from-slate-950 via-purple-950/90 to-slate-950 border-2 border-violet-500/60 hover:border-violet-400 rounded-2xl p-5 sm:p-6 shadow-2xl overflow-hidden transition-all text-white text-left">
            <div className="absolute top-0 right-0 -mr-12 -mt-12 w-80 h-80 bg-violet-600/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 -ml-12 -mb-12 w-80 h-80 bg-indigo-600/15 rounded-full blur-3xl pointer-events-none" />
            
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div className="space-y-2 max-w-3xl">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-violet-500/30 text-violet-200 border border-violet-400/50 flex items-center gap-1.5 shadow-xs">
                    <Building2 className="w-3.5 h-3.5 text-amber-300" />
                    <span>{globalLanguage === 'pl' ? '🏢 KORPORACYJNE BIURO CYFROWE' : globalLanguage === 'en' ? '🏢 CORPORATE DIGITAL OFFICE' : '🏢 КОРПОРАТИВНИЙ ЦИФРОВИЙ ОФІС'}</span>
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/25 text-amber-300 border border-amber-400/50 flex items-center gap-1 font-mono animate-pulse">
                    🔥 {globalLanguage === 'pl' ? '3 DNI ZA DARMO (FREE TRIAL)' : globalLanguage === 'en' ? '3 DAYS FREE TRIAL' : '3 ДНІ БЕЗКОШТОВНО (FREE TRIAL)'}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700 font-mono">
                    {globalLanguage === 'pl' ? 'ДСТУ 4163:2020 • BAZA WIEDZY • 40+ MODUŁÓW' : globalLanguage === 'en' ? 'DSTU 4163:2020 • KNOWLEDGE BASE • 40+ MODULES' : 'ДСТУ 4163:2020 • БАЗА ЗНАНЬ • 40+ МОДУЛІВ'}
                  </span>
                </div>

                <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
                  <span>{globalLanguage === 'pl' ? 'Korporacyjne Biuro Cyfrowe Przedsiębiorstwa' : globalLanguage === 'en' ? 'Corporate Digital Office for Enterprise' : 'Корпоративний Цифровий Офіс Підприємства'}</span>
                </h2>

                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans font-medium">
                  {globalLanguage === 'pl'
                    ? 'Centralny system cyfrowego biura: zintegrowana baza wiedzy, notatki analityczne, zarządzenia, umowy, akty wykonania usług wg DSTU 4163:2020, kosztorysy oddziałów i ponad 40 modułów branżowych.'
                    : globalLanguage === 'en'
                    ? 'Unified corporate digital workspace: structured knowledge repository, executive orders, contracts, official acceptance acts under DSTU 4163:2020, branch budgets, and 40+ industry automation modules.'
                    : 'Єдиний корпоративний центр цифрового діловодства та аналітики: структурована база знань, аналітичні довідки, накази, договори, розрахункові первинні акти за ДСТУ 4163:2020, кошториси філій та понад 40 спеціалізованих галузевих модулів.'}
                </p>
              </div>

              <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap shrink-0">
                <button
                  type="button"
                  onClick={() => setShowOfficeInfoModal(true)}
                  className="w-full sm:w-auto px-5 py-3.5 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-black text-xs sm:text-sm rounded-xl shadow-xl shadow-violet-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer border border-violet-400/50 group hover:scale-[1.02]"
                >
                  <Sparkles className="w-4 h-4 text-amber-300 group-hover:rotate-12 transition-transform" />
                  <span>{globalLanguage === 'pl' ? '🚀 Zobacz możliwości (3 dni Free) ➔' : globalLanguage === 'en' ? '🚀 Explore System (3 Days Free) ➔' : '🚀 Спробувати 3 дні безкоштовно ➔'}</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowOfficeInfoModal(true)}
                  className="w-full sm:w-auto px-4 py-3.5 bg-slate-900/90 hover:bg-slate-800 text-violet-300 hover:text-white font-bold text-xs sm:text-sm rounded-xl border border-violet-500/40 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md"
                >
                  <Info className="w-4 h-4 text-amber-300" />
                  <span>{globalLanguage === 'pl' ? 'ℹ️ Jak to działa' : globalLanguage === 'en' ? 'ℹ️ How it works' : 'ℹ️ Про систему / Як це працює'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Smart AI Agent Dispatcher & Navigator on Login Page */}
          <SmartAgentDispatcher 
            onLaunchAgent={handleLaunchAgentOnLogin} 
            language={globalLanguage} 
            currentUserEmail={undefined} 
          />

          {/* Operational Situational Center & Threat Radar Maps Showcase with Fixed Pricing */}
          <SituationalCenterShowcase 
            language={globalLanguage} 
            onSelectAgent={handleLaunchAgentOnLogin} 
            onOpenPricing={() => setShowPublicPricingModal(true)} 
            isLoggedIn={false}
            onNavigateToLogin={() => {
              const el = document.getElementById('login-card-section');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
          />

          {/* Interactive Proof-of-Result Showcase (Live Word/Excel/Satellite Artifacts) */}
          <InteractiveShowcasePreview 
            language={globalLanguage} 
            onSelectAgent={handleLaunchAgentOnLogin} 
          />

          {/* Autonomous Satellite Telegram Bots Showcase & 24/7 Watchlist Alerts */}
          <TelegramBotsShowcase 
            language={globalLanguage} 
          />

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xl p-4 sm:p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 w-full">
            
            {/* Left Column: Presentation */}
            <div className="space-y-5 sm:space-y-6 pr-0 md:pr-6 border-r-0 md:border-r border-slate-200/80 text-left flex flex-col justify-between max-h-[660px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="bg-[#1E3A8A] w-10 h-10 flex items-center justify-center rounded-xl shadow-md shrink-0">
                    <Sparkles className="w-5 h-5 text-amber-400 font-extrabold animate-pulse" />
                  </div>
                  <div>
                    <h2 className="text-sm font-black text-slate-800 tracking-tight uppercase">{t.brandTitle}</h2>
                    <p className="text-[9px] text-red-500 font-mono font-bold tracking-wider uppercase">{t.brandSubtitle}</p>
                  </div>
                </div>

                <p className="text-[11px] text-slate-600 font-medium italic leading-relaxed border-l-2 border-amber-500 pl-3 bg-amber-50/40 py-1.5 rounded-r">
                  {t.quote}
                </p>

                {/* 1. Блок Вигоди у цифрах (Value Metrics Strip) */}
                <div className="space-y-2">
                  <h3 className="text-xs font-black text-slate-800 uppercase tracking-wide">{t.valueMetricsTitle}</h3>
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div className="p-2.5 bg-blue-50/60 border border-blue-100 rounded-lg space-y-0.5">
                      <div className="font-bold text-blue-900 flex items-center gap-1">
                        <span>⏱️</span> <span>{t.metric1Title}</span>
                      </div>
                      <p className="text-[9.5px] text-slate-500 leading-tight">{t.metric1Desc}</p>
                    </div>
                    <div className="p-2.5 bg-emerald-50/60 border border-emerald-100 rounded-lg space-y-0.5">
                      <div className="font-bold text-emerald-900 flex items-center gap-1">
                        <span>💰</span> <span>{t.metric2Title}</span>
                      </div>
                      <p className="text-[9.5px] text-slate-500 leading-tight">{t.metric2Desc}</p>
                    </div>
                    <div className="p-2.5 bg-amber-50/60 border border-amber-100 rounded-lg space-y-0.5">
                      <div className="font-bold text-amber-900 flex items-center gap-1">
                        <span>🛡️</span> <span>{t.metric3Title}</span>
                      </div>
                      <p className="text-[9.5px] text-slate-500 leading-tight">{t.metric3Desc}</p>
                    </div>
                    <div className="p-2.5 bg-purple-50/60 border border-purple-100 rounded-lg space-y-0.5">
                      <div className="font-bold text-purple-900 flex items-center gap-1">
                        <span>📱</span> <span>{t.metric4Title}</span>
                      </div>
                      <p className="text-[9.5px] text-slate-500 leading-tight">{t.metric4Desc}</p>
                    </div>
                  </div>
                </div>

                {/* 2. Блок 6 Спеціалізованих Галузевих Хабів */}
                <div className="space-y-3">
                  <h3 className="text-xs font-black text-slate-800 uppercase tracking-wide">{t.hubsTitle}</h3>
                  
                  <div className="space-y-2">
                    <div className="p-3 bg-slate-50 hover:bg-blue-50/30 transition-all rounded-lg border border-slate-200/80 space-y-1">
                      <h4 className="text-[11px] font-bold text-slate-800 flex items-center gap-2">
                        <span>⚖️</span>
                        <span>{t.hub1Title}</span>
                      </h4>
                      <p className="text-[10.5px] text-slate-500 leading-relaxed">
                        {t.hub1Desc}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-50 hover:bg-blue-50/30 transition-all rounded-lg border border-slate-200/80 space-y-1">
                      <h4 className="text-[11px] font-bold text-slate-800 flex items-center gap-2">
                        <span>📊</span>
                        <span>{t.hub2Title}</span>
                      </h4>
                      <p className="text-[10.5px] text-slate-500 leading-relaxed">
                        {t.hub2Desc}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-50 hover:bg-blue-50/30 transition-all rounded-lg border border-slate-200/80 space-y-1">
                      <h4 className="text-[11px] font-bold text-slate-800 flex items-center gap-2">
                        <span>🏗️</span>
                        <span>{t.hub3Title}</span>
                      </h4>
                      <p className="text-[10.5px] text-slate-500 leading-relaxed">
                        {t.hub3Desc}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-50 hover:bg-blue-50/30 transition-all rounded-lg border border-slate-200/80 space-y-1">
                      <h4 className="text-[11px] font-bold text-slate-800 flex items-center gap-2">
                        <span>⚡</span>
                        <span>{t.hub4Title}</span>
                      </h4>
                      <p className="text-[10.5px] text-slate-500 leading-relaxed">
                        {t.hub4Desc}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-50 hover:bg-blue-50/30 transition-all rounded-lg border border-slate-200/80 space-y-1">
                      <h4 className="text-[11px] font-bold text-slate-800 flex items-center gap-2">
                        <span>📡</span>
                        <span>{t.hub5Title}</span>
                      </h4>
                      <p className="text-[10.5px] text-slate-500 leading-relaxed">
                        {t.hub5Desc}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-50 hover:bg-blue-50/30 transition-all rounded-lg border border-slate-200/80 space-y-1">
                      <h4 className="text-[11px] font-bold text-slate-800 flex items-center gap-2">
                        <span>🔍</span>
                        <span>{t.hub6Title}</span>
                      </h4>
                      <p className="text-[10.5px] text-slate-500 leading-relaxed">
                        {t.hub6Desc}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 2.5 Автономні Telegram-боти 24/7 */}
                <div className="p-3 bg-gradient-to-r from-cyan-950/80 via-slate-900 to-blue-950/80 rounded-xl border border-cyan-500/40 text-[10px] text-slate-200 space-y-1.5 shadow-md">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white flex items-center gap-1.5">
                      <span className="text-cyan-400">📱</span>
                      <span>{globalLanguage === 'uk' ? 'Автономні Telegram-боти (24/7)' : globalLanguage === 'pl' ? 'Autonomiczne boty Telegram 24/7' : 'Autonomous 24/7 Telegram Bots'}</span>
                    </span>
                    <span className="text-[8.5px] font-mono text-emerald-300 font-bold bg-emerald-950/90 px-1.5 py-0.5 rounded border border-emerald-500/40 animate-pulse">5 BOTS ONLINE</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 text-[9.5px] font-mono pt-1">
                    <a href="https://t.me/AgroDeepResearchHub_bot" target="_blank" rel="noopener noreferrer" className="p-1.5 bg-slate-800/80 hover:bg-slate-800 rounded border border-emerald-500/30 text-emerald-400 hover:text-emerald-300 transition-colors flex items-center justify-between">
                      <span>🌾 @AgroDeep...</span>
                      <span className="text-[8px] text-slate-400">↗</span>
                    </a>
                    <a href="https://t.me/InSARDeepResearchHub_bot" target="_blank" rel="noopener noreferrer" className="p-1.5 bg-slate-800/80 hover:bg-slate-800 rounded border border-blue-500/30 text-blue-400 hover:text-blue-300 transition-colors flex items-center justify-between">
                      <span>🏢 @InSARDeep...</span>
                      <span className="text-[8px] text-slate-400">↗</span>
                    </a>
                    <a href="https://t.me/Firedeepresearchhub_bot" target="_blank" rel="noopener noreferrer" className="p-1.5 bg-slate-800/80 hover:bg-slate-800 rounded border border-amber-500/30 text-amber-400 hover:text-amber-300 transition-colors flex items-center justify-between">
                      <span>🔥 @Firedeep...</span>
                      <span className="text-[8px] text-slate-400">↗</span>
                    </a>
                    <a href="https://t.me/Aquadeepresearchhub_bot" target="_blank" rel="noopener noreferrer" className="p-1.5 bg-slate-800/80 hover:bg-slate-800 rounded border border-cyan-500/30 text-cyan-400 hover:text-cyan-300 transition-colors flex items-center justify-between">
                      <span>💧 @Aquadeep...</span>
                      <span className="text-[8px] text-slate-400">↗</span>
                    </a>
                    <a href="https://t.me/GribnikDeppResearchHub_bot" target="_blank" rel="noopener noreferrer" className="p-1.5 bg-slate-800/80 hover:bg-slate-800 rounded border border-lime-500/30 text-lime-400 hover:text-lime-300 transition-colors flex items-center justify-between col-span-2">
                      <span>🍄 @GribnikDeppResearchHub_bot (Радар для Грибників)</span>
                      <span className="text-[8px] text-slate-400">↗</span>
                    </a>
                  </div>
                </div>

                {/* 3. Масштабованість & Безпека */}
                <div className="space-y-2 p-3 bg-slate-100/60 rounded-lg border border-slate-200 text-[10px] text-slate-600">
                  <h3 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <span>🛡️</span> <span>{t.securityTitle}</span>
                  </h3>
                  <ul className="list-disc pl-4 space-y-1">
                    <li><strong>{t.securityItem1}</strong></li>
                    <li><strong>{t.securityItem2}</strong></li>
                  </ul>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[9px] text-slate-400 font-mono mt-4">
                <span>{t.securitySystemActive}</span>
                <span>{t.version}</span>
              </div>
            </div>

            {/* Right Column: Login Card */}
            <div id="login-card-section" className="flex flex-col justify-center space-y-4 py-3 md:pl-4 text-center md:text-left scroll-mt-6">
              <div className="space-y-1.5">
                <h1 className="text-xl font-black text-slate-800 tracking-tight uppercase">
                  {t.loginCardTitlePrefix} <span className="text-red-500 font-mono">{t.loginCardTitleHighlight}</span>
                </h1>
                <p className="text-[10px] text-slate-400 font-mono tracking-widest">{t.loginCardSubtitle}</p>
              </div>

              {pendingAgentNotice && (
                <div className="p-3 bg-emerald-500/10 border-2 border-emerald-500/50 rounded-xl text-left animate-pulse">
                  <div className="flex items-center gap-2 text-emerald-800 font-extrabold text-xs uppercase tracking-wide">
                    <Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>{pendingAgentNotice}</span>
                  </div>
                </div>
              )}

              {/* In-App Browser (Telegram/Viber/Instagram) Notice */}
              {isMobileWebview && (
                <div className="p-3 bg-amber-500/15 border border-amber-500/40 rounded-xl text-left space-y-1">
                  <div className="flex items-center gap-1.5 text-amber-900 font-bold text-[11px]">
                    <span>{t.inAppBrowserNoticeTitle}</span>
                  </div>
                  <p className="text-[10px] text-amber-800 font-mono leading-relaxed">
                    {t.inAppBrowserNoticeDesc}
                  </p>
                </div>
              )}

              {/* Zero-Risk High-Contrast Badge */}
              <div className="p-2.5 bg-gradient-to-r from-emerald-950/90 via-slate-900 to-teal-950/90 border border-emerald-500/50 rounded-xl text-left flex items-center gap-2.5 shadow-md">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping shrink-0" />
                <p className="text-[10px] font-mono font-black text-emerald-300 uppercase tracking-wider">
                  {globalLanguage === 'uk'
                    ? '🟢 БЕЗ ПРИВ\'ЯЗКИ КАРТИ • 🎁 1-Й ЗАПУСК 100% БЕЗКОШТОВНО • РЕЗУЛЬТАТ ЗА 15 СЕК'
                    : globalLanguage === 'pl'
                    ? '🟢 BEZ KARTY PŁATNICZEJ • 🎁 1. URUCHOMIENIE GRATIS • WYNIK W 15 SEK'
                    : globalLanguage === 'de'
                    ? '🟢 KEINE KREDITKARTE ERFORDERLICH • 🎁 1. TEST 100% KOSTENLOS • ERGEBNIS IN 15 SEK'
                    : globalLanguage === 'fr'
                    ? '🟢 SANS CARTE BANCAIRE • 🎁 1ER TEST 100% GRATUIT • RÉSULTAT EN 15 SEC'
                    : '🟢 NO CREDIT CARD REQUIRED • 🎁 1ST RUN 100% FREE • RESULTS IN 15 SECONDS'}
                </p>
              </div>

              {/* Corporate Digital Office Notice in Login Card */}
              <div className="p-3 bg-gradient-to-r from-violet-950/80 via-purple-950/70 to-slate-900 border border-violet-500/40 rounded-xl text-left flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-violet-300 font-extrabold text-[11px] uppercase tracking-wide">
                    <Building2 className="w-3.5 h-3.5 text-amber-400" />
                    <span>🏢 {globalLanguage === 'pl' ? 'Korporacyjne Biuro Cyfrowe' : globalLanguage === 'en' ? 'Corporate Digital Office' : 'Корпоративний Цифровий Офіс'}</span>
                  </div>
                  <p className="text-[10px] text-slate-300 font-mono">
                    {globalLanguage === 'pl'
                      ? 'Baza wiedzy, obieg dokumentów DSTU 4163:2020 i ponad 40 modułów branżowych.'
                      : globalLanguage === 'en'
                      ? 'Knowledge base, DSTU 4163:2020 workflows, and 40+ industry modules.'
                      : 'База знань, діловодство за ДСТУ 4163:2020 та понад 40 галузевих модулів.'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowOfficeInfoModal(true)}
                    className="px-2.5 py-1.5 bg-violet-600 hover:bg-violet-500 text-white font-black text-[10px] rounded-lg shadow-sm transition-all flex items-center gap-1 cursor-pointer"
                  >
                    <Info className="w-3 h-3 text-amber-300" />
                    <span>{globalLanguage === 'pl' ? 'Jak to działa' : globalLanguage === 'en' ? 'How it works' : 'Як це працює'}</span>
                  </button>
                </div>
              </div>

              {/* Notice Banner for Enterprise SSO Setup if triggered */}
              {typeof window !== 'undefined' && window.location.search.includes('notice=microsoft_sso_setup') && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-left text-[11px] text-blue-900 font-mono space-y-1">
                  <p className="font-bold flex items-center gap-1.5 text-blue-800">
                    <span>🏢</span>
                    <span>Microsoft 365 / Azure AD SSO активовано</span>
                  </p>
                  <p className="text-[10px] text-blue-700 leading-relaxed">
                    Для прив'язки вашого корпоративного тенанта додайте <code>MICROSOFT_CLIENT_ID</code> на сервері або скористайтесь швидким входом через Google / Email.
                  </p>
                </div>
              )}
              {typeof window !== 'undefined' && window.location.search.includes('notice=apple_id_setup') && (
                <div className="p-3 bg-neutral-100 border border-neutral-300 rounded-xl text-left text-[11px] text-neutral-900 font-mono space-y-1">
                  <p className="font-bold flex items-center gap-1.5 text-neutral-800">
                    <span>🍏</span>
                    <span>Sign in with Apple ID активовано</span>
                  </p>
                  <p className="text-[10px] text-neutral-700 leading-relaxed">
                    Для прив'язки вашого Apple Service ID додайте <code>APPLE_CLIENT_ID</code> на сервері або скористайтесь швидким входом через Google / Email.
                  </p>
                </div>
              )}

              {/* Separate Standalone Auth Action Buttons */}
              <div className="space-y-2.5">
                {/* 1. Google 1-Click SSO Button */}
                <a
                  href={acceptedOffer ? "/auth/google" : "#"}
                  onClick={(e) => {
                    if (!acceptedOffer) {
                      setAcceptedOffer(true);
                      try { localStorage.setItem('acceptedOffer', 'true'); } catch (err) {}
                    }
                    try {
                      localStorage.setItem('last_auth_method', 'google');
                      setLastAuthMethod('google');
                    } catch (err) {}
                    trackConversion('registration_start');
                    trackEvent('login_click', 'auth', 'google');
                  }}
                  className={`flex items-center justify-center gap-3 w-full py-3.5 px-4 bg-gradient-to-r from-[#1E3A8A] via-blue-700 to-indigo-800 hover:from-blue-800 hover:to-indigo-900 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md shadow-blue-950/20 hover:shadow-lg active:scale-[0.99] cursor-pointer group ${
                    lastAuthMethod === 'google' ? 'ring-2 ring-amber-400 ring-offset-1 shadow-amber-500/30' : ''
                  }`}
                >
                  <svg className="w-4 h-4 fill-current shrink-0 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                    <path d="M12.24 10.285V13.4h6.887c-.275 1.565-1.88 4.604-6.887 4.604-4.33 0-7.86-3.577-7.86-8s3.53-8 7.86-8c2.46 0 4.105 1.025 5.047 1.926l2.427-2.334C17.955 2.192 15.34 1 12.24 1c-6.075 0-11 4.925-11 11s4.925 11 11 11c6.34 0 10.557-4.427 10.557-10.714 0-.727-.078-1.282-.172-1.714H12.24z"/>
                  </svg>
                  <span className="truncate">{t.googleLoginBtn}</span>
                  {lastAuthMethod === 'google' && (
                    <span className="ml-auto px-2 py-0.5 bg-amber-400 text-amber-950 font-black text-[9px] uppercase tracking-wider rounded shadow-xs flex items-center gap-1 shrink-0 animate-pulse">
                      <span>⭐</span>
                      <span>{t.lastUsedBadge}</span>
                    </span>
                  )}
                </a>

                {/* 2. Corporate & Mobile Ecosystem SSO (Microsoft 365 + Apple ID) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {/* Microsoft 365 */}
                  <a
                    href={acceptedOffer ? "/auth/microsoft" : "#"}
                    onClick={(e) => {
                      if (!acceptedOffer) {
                        setAcceptedOffer(true);
                        try { localStorage.setItem('acceptedOffer', 'true'); } catch (err) {}
                      }
                      try {
                        localStorage.setItem('last_auth_method', 'microsoft');
                        setLastAuthMethod('microsoft');
                      } catch (err) {}
                      trackConversion('registration_start');
                      trackEvent('login_click', 'auth', 'microsoft');
                    }}
                    className={`flex items-center justify-center gap-2.5 w-full py-3 px-3 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md active:scale-[0.99] cursor-pointer group ${
                      lastAuthMethod === 'microsoft' ? 'ring-2 ring-amber-400 ring-offset-1 shadow-amber-500/30' : ''
                    }`}
                  >
                    <svg className="w-4 h-4 shrink-0 group-hover:scale-110 transition-transform" viewBox="0 0 21 21">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                    <span className="truncate">{t.microsoftLoginBtn}</span>
                    {lastAuthMethod === 'microsoft' && (
                      <span className="ml-auto px-1.5 py-0.5 bg-amber-400 text-amber-950 font-black text-[8.5px] uppercase tracking-wider rounded shadow-xs shrink-0 animate-pulse">
                        ⭐ {t.lastUsedBadge}
                      </span>
                    )}
                  </a>

                  {/* Apple ID */}
                  <a
                    href={acceptedOffer ? "/auth/apple" : "#"}
                    onClick={(e) => {
                      if (!acceptedOffer) {
                        setAcceptedOffer(true);
                        try { localStorage.setItem('acceptedOffer', 'true'); } catch (err) {}
                      }
                      try {
                        localStorage.setItem('last_auth_method', 'apple');
                        setLastAuthMethod('apple');
                      } catch (err) {}
                      trackConversion('registration_start');
                      trackEvent('login_click', 'auth', 'apple');
                    }}
                    className={`flex items-center justify-center gap-2.5 w-full py-3 px-3 bg-black hover:bg-neutral-900 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md border border-neutral-800 active:scale-[0.99] cursor-pointer group ${
                      lastAuthMethod === 'apple' ? 'ring-2 ring-amber-400 ring-offset-1 shadow-amber-500/30' : ''
                    }`}
                  >
                    <svg className="w-4 h-4 fill-current shrink-0 group-hover:scale-110 transition-transform" viewBox="0 0 170 170">
                      <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.05-7.7-7.85-12-14.4-6.3-9.7-11.18-20.74-14.62-33.12-3.45-12.38-5.17-23.75-5.17-34.1 0-14.54 3.75-26.74 11.26-36.6 7.5-9.86 16.92-14.86 28.25-15 4.9.11 10.42 1.34 16.57 3.69 6.15 2.35 10.15 3.58 12 3.69 1.63 0 5.66-1.28 12.09-3.84 6.43-2.57 11.98-3.75 16.65-3.54 13.06.77 23.33 5.48 30.82 14.13-11.51 6.94-17.15 16.56-16.92 28.85.22 9.68 3.96 17.73 11.23 24.16 7.27 6.43 15.86 10.11 25.77 11.04-2.28 7.07-5.1 14.07-8.47 21-.32.66-.67 1.37-1.05 2.14zM119.22 33.09c0-7.29 2.58-14.18 7.74-20.68 5.16-6.5 11.56-10.74 19.2-12.72.33 1.31.49 2.52.49 3.63 0 7.28-2.67 14.28-8.01 21-5.34 6.72-11.89 10.87-19.65 12.44-.06-1.2-.17-2.43-.33-3.67h-.44z"/>
                    </svg>
                    <span className="truncate">{t.appleLoginBtn}</span>
                    {lastAuthMethod === 'apple' && (
                      <span className="ml-auto px-1.5 py-0.5 bg-amber-400 text-amber-950 font-black text-[8.5px] uppercase tracking-wider rounded shadow-xs shrink-0 animate-pulse">
                        ⭐ {t.lastUsedBadge}
                      </span>
                    )}
                  </a>
                </div>

                {/* 4. Email OTP Button / Collapsible Panel */}
                {!showEmailAuth && otpStep === 'enter_email' ? (
                  <button
                    type="button"
                    onClick={() => { setShowEmailAuth(true); setOtpError(null); }}
                    className={`flex items-center justify-center gap-2.5 w-full py-3.5 px-4 bg-white hover:bg-slate-50 text-slate-800 font-bold text-xs uppercase tracking-wider rounded-xl border-2 border-slate-200 hover:border-slate-300 shadow-sm transition-all cursor-pointer active:scale-[0.99] group ${
                      lastAuthMethod === 'email' ? 'ring-2 ring-amber-400 ring-offset-1 border-amber-300 shadow-amber-500/30' : ''
                    }`}
                  >
                    <Mail className="w-4 h-4 text-[#1E3A8A] shrink-0 group-hover:scale-110 transition-transform" />
                    <span className="truncate">{t.loginTabEmail}</span>
                    {lastAuthMethod === 'email' && (
                      <span className="px-2 py-0.5 bg-amber-400 text-amber-950 font-black text-[9px] uppercase tracking-wider rounded shadow-xs flex items-center gap-1 shrink-0 animate-pulse">
                        <span>⭐</span>
                        <span>{t.lastUsedBadge}</span>
                      </span>
                    )}
                    <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-600 ml-auto shrink-0" />
                  </button>
                ) : (
                  <div className={`space-y-3 p-4 bg-slate-50 border border-slate-200/90 rounded-xl text-left ${
                    lastAuthMethod === 'email' ? 'ring-2 ring-amber-400/80 border-amber-300' : ''
                  }`}>
                    <div className="flex items-center justify-between pb-1.5 border-b border-slate-200">
                      <div className="flex items-center gap-1.5 text-xs font-black text-slate-800 uppercase tracking-wide">
                        <Mail className="w-3.5 h-3.5 text-[#1E3A8A]" />
                        <span>{t.loginTabEmail}</span>
                        {lastAuthMethod === 'email' && (
                          <span className="px-1.5 py-0.5 bg-amber-400 text-amber-950 font-black text-[8.5px] uppercase tracking-wider rounded shadow-xs ml-1">
                            ⭐ {t.lastUsedBadge}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => { setShowEmailAuth(false); setOtpError(null); }}
                        className="text-[10.5px] text-slate-500 hover:text-slate-800 font-bold hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <ChevronUp className="w-3 h-3" />
                        <span>{globalLanguage === 'uk' ? 'Згорнути' : globalLanguage === 'pl' ? 'Zwiń' : 'Collapse'}</span>
                      </button>
                    </div>

                    {otpStep === 'enter_email' ? (
                      <form onSubmit={handleSendOtp} className="space-y-3">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                            {t.emailInputLabel}
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                            <input
                              type="email"
                              required
                              value={otpEmail}
                              onChange={(e) => setOtpEmail(e.target.value)}
                              placeholder={t.emailInputPlaceholder}
                              className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-300 rounded-lg text-xs font-mono text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent transition-all shadow-xs"
                            />
                          </div>
                        </div>

                        {otpError && (
                          <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-[11px] font-bold text-red-600 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            <span>{otpError}</span>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={otpLoading}
                          className="w-full py-3 bg-gradient-to-r from-[#1E3A8A] to-blue-700 hover:from-blue-800 hover:to-indigo-800 text-white font-bold text-xs uppercase tracking-wider rounded-lg shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                          {otpLoading ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>{t.sendingCode}</span>
                            </>
                          ) : (
                            <>
                              <Key className="w-4 h-4" />
                              <span>{t.sendCodeBtn}</span>
                            </>
                          )}
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleVerifyOtp} className="space-y-3">
                        <div className="p-2.5 bg-blue-50/70 border border-blue-200 rounded-lg space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-blue-900 font-mono">
                              {t.codeSentNotice}:
                            </span>
                            <button
                              type="button"
                              onClick={() => { setOtpStep('enter_email'); setOtpCode(''); setOtpError(null); }}
                              className="text-[10px] text-blue-700 hover:underline font-bold"
                            >
                              {t.changeEmailBtn}
                            </button>
                          </div>
                          <p className="text-xs font-mono font-bold text-slate-800 truncate">
                            📧 {otpEmail}
                          </p>
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                            {t.codeSentDesc}
                          </label>
                          <input
                            type="text"
                            required
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            placeholder={t.codeInputPlaceholder}
                            autoFocus
                            className="w-full px-3 py-3 bg-white border-2 border-[#1E3A8A] rounded-lg text-center text-xl font-mono font-black tracking-widest text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] transition-all shadow-xs"
                          />
                        </div>

                        {otpError && (
                          <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-[11px] font-bold text-red-600 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            <span>{otpError}</span>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={otpLoading || otpCode.length < 6}
                          className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                          {otpLoading ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>{t.verifyingCode}</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              <span>{t.verifyCodeBtn}</span>
                            </>
                          )}
                        </button>

                        <div className="text-center pt-1">
                          <button
                            type="button"
                            disabled={otpResendCountdown > 0 || otpLoading}
                            onClick={() => handleSendOtp()}
                            className="text-[10px] text-slate-500 hover:text-slate-800 disabled:opacity-50 font-mono cursor-pointer"
                          >
                            {otpResendCountdown > 0
                              ? (globalLanguage === 'uk' ? `Повторна відправка через ${otpResendCountdown} сек` : `Resend code in ${otpResendCountdown}s`)
                              : t.resendCodeBtn}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>

              {/* Offer Agreement Footnote */}
              <div className="flex items-start gap-2 text-left px-1">
                <input
                  type="checkbox"
                  id="offer-checkbox"
                  checked={acceptedOffer}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAcceptedOffer(checked);
                    try {
                      if (checked) {
                        localStorage.setItem('acceptedOffer', 'true');
                      } else {
                        localStorage.removeItem('acceptedOffer');
                      }
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                  className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-[#1E3A8A] focus:ring-[#1E3A8A] cursor-pointer"
                />
                <label htmlFor="offer-checkbox" className="text-[9.5px] text-slate-500 leading-normal select-none cursor-pointer">
                  {t.offerCheckboxPrefix} <button type="button" onClick={() => setShowOfferModal(true)} className="text-[#1E3A8A] hover:underline font-bold focus:outline-none">{t.offerLink}</button> {t.offerCheckboxSuffix}
                </label>
              </div>

              {/* Action Buttons: Corporate Office, Pricing & Demo Tour */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setShowOfficeInfoModal(true)}
                  className="col-span-1 sm:col-span-2 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-violet-900 via-purple-900 to-indigo-900 hover:from-violet-800 hover:to-indigo-800 text-white font-black text-xs uppercase tracking-wider rounded-xl border border-violet-400/50 shadow-md shadow-violet-950/40 transition-all cursor-pointer group"
                >
                  <Building2 className="w-4 h-4 text-amber-300 group-hover:scale-110 transition-transform" />
                  <span>{globalLanguage === 'pl' ? '🏢 Korporacyjne Biuro Cyfrowe (3 dni Free Trial)' : globalLanguage === 'en' ? '🏢 Corporate Digital Office (3 Days Free Trial)' : '🏢 Корпоративний Цифровий Офіс (3 дні Free Trial)'}</span>
                  <Info className="w-3.5 h-3.5 text-violet-300 ml-1" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowPublicPricingModal(true)}
                  className="flex items-center justify-center gap-2 py-2.5 text-[#1E3A8A] font-bold text-xs uppercase tracking-wider rounded-lg border-2 border-[#1E3A8A]/30 bg-[#1E3A8A]/5 hover:bg-[#1E3A8A]/10 hover:border-[#1E3A8A]/50 transition-all cursor-pointer shadow-sm"
                >
                  <Coins className="w-4 h-4 text-amber-500" />
                  <span>{t.pricingBtn}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowQuickDemoModal(true)}
                  className="flex items-center justify-center gap-2 py-2.5 text-indigo-700 font-bold text-xs uppercase tracking-wider rounded-lg border-2 border-indigo-500/30 bg-indigo-50 hover:bg-indigo-100/80 hover:border-indigo-500/50 transition-all cursor-pointer shadow-sm"
                >
                  <span className="text-sm">▶️</span>
                  <span>{globalLanguage === 'uk' ? '30-сек демо-тур' : globalLanguage === 'pl' ? '30-sek demo-tour' : globalLanguage === 'de' ? '30-Sek. Demo-Tour' : globalLanguage === 'fr' ? 'Visite démo 30s' : '30s Quick Tour'}</span>
                </button>
              </div>

              {/* Trust Badges Strip */}
              <div className="grid grid-cols-1 gap-1.5 text-[9.5px] font-mono text-slate-500 bg-slate-50 p-2.5 rounded-lg border border-slate-200/80 text-left">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>{t.trustBadge1}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>{t.trustBadge2}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>{t.trustBadge3}</span>
                </div>
              </div>
            </div>

          </div>
        </div>

        <PublicPricingModal 
          isOpen={showPublicPricingModal} 
          onClose={() => setShowPublicPricingModal(false)} 
          initialCurrency={currency}
          language={globalLanguage}
        />

        <QuickDemoTourModal
          isOpen={showQuickDemoModal}
          onClose={() => setShowQuickDemoModal(false)}
          language={globalLanguage}
        />

        <DesktopAppStoreModal
          isOpen={showDesktopStoreModal}
          onClose={() => setShowDesktopStoreModal(false)}
          currentLang={globalLanguage}
        />

        {showOfferModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <div className="bg-white border border-slate-200 rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <h3 className="text-xs font-bold text-slate-800 tracking-wider uppercase font-mono">{t.offerModalTitle}</h3>
                <button 
                  onClick={() => setShowOfferModal(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors text-lg font-bold p-1"
                >
                  ✕
                </button>
              </div>
              <div className="p-6 overflow-y-auto text-left text-[11px] text-slate-600 space-y-4 leading-relaxed font-sans">
                <p className="font-bold text-slate-800 text-center text-xs">ПУБЛІЧНИЙ ДОГОВІР-ОФЕРТА ПРО НАДАННЯ ПОСЛУГ КОРИСТУВАННЯ ПЛАТФОРМОЮ «DEEP RESEARCH HUB»</p>
                <p className="text-[10px] text-slate-400 text-center">Редакція від 16 липня 2026 року</p>
                
                <p>Цей документ є офіційною та публічною пропозицією (офертою) Виконавця укласти Договір про надання послуг користування Платформою «Deep Research Hub» на викладених нижче умовах. Договір адресований необмеженому колу фізичних та юридичних осіб (далі — «Користувач» або «Замовник»).</p>
                
                <p>Відповідно до статей 633, 641 та 642 Цивільного кодексу України, повним і безумовним прийняттям (акцептом) умов цієї Публічної оферти є вчинення Користувачем дій, спрямованих на реєстрацію або авторизацію на Сайті, проставлення відмітки про згоду з умовами оферти та/або здійснення оплати за доступ до послуг Платформи.</p>

                <p className="font-bold text-slate-800 uppercase mt-2 border-b border-slate-100 pb-1">1. ВИЗНАЧЕННЯ ТЕРМІНІВ</p>
                <p>• <strong>Платформа (Сайт)</strong> — сукупність програмних та апаратних засобів, розміщених у мережі Інтернет за адресою deep-research-hub (та її субдоменах), що забезпечують роботу аналітичних модулів, звітів, інтелектуальних інструментів та особистого кабінету користувача.</p>
                <p>• <strong>Спеціалізовані Модулі та Інструменти</strong> — інтелектуальні програмні модулі (Юрист, Бухгалтер, Кошторисник, Енергоменеджер, Координатор тощо), які функціонують для виконання аналітичних, юридичних, розрахункових та інших завдань за запитом Користувача.</p>
                <p>• <strong>Цифровий двійник</strong> — база знань компанії Замовника, що формується з її статутних документів, реквізитів та інших завантажених файлів з метою персоналізації відповідей аналітичних модулів.</p>

                <p className="font-bold text-slate-800 uppercase mt-2 border-b border-slate-100 pb-1">2. ПРЕДМЕТ ДОГОВОРУ</p>
                <p>2.1. Виконавець зобов’язується надавати Користувачеві доступ до функціональних можливостей Платформи «Deep Research Hub» (послуги SaaS), а Користувач зобов’язується приймати та оплачувати ці послуги відповідно до встановлених Тарифів (за винятком безкоштовного тестового ліміту).</p>

                <p className="font-bold text-slate-800 uppercase mt-2 border-b border-slate-100 pb-1">3. ПОРЯДОК НАДАННЯ ПОСЛУГ ТА АВТОРИЗАЦІЇ</p>
                <p>3.1. Для отримання доступу до Платформи Користувач здійснює авторизацію через систему Google Auth та підтверджує свою згоду з умовами цього Договору шляхом встановлення відповідного прапорця (чекбокса) перед входом.</p>

                <p className="font-bold text-slate-800 uppercase mt-2 border-b border-slate-100 pb-1">4. ОБМЕЖЕННЯ ВІДПОВІДАЛЬНОСТІ (ВІДМОВА ВІД ГАРАНТІЙ)</p>
                <p>4.1. Користувач розуміє та погоджується з тим, що послуги надаються на основі сучасних автоматизованих алгоритмів обробки даних (Generative Engine). Результати роботи спеціалізованих модулів мають виключно <strong>рекомендаційний та інформаційний характер</strong>.</p>
                <p>4.2. Платформа та цифрові модулі не є заміною професійних юридичних, бухгалтерських, фінансових чи аудиторських послуг. Виконавець не несе відповідальності за будь-які прямі чи непрямі збитки або штрафи, що виникли внаслідок використання результатів генерації.</p>

                <p className="font-bold text-slate-800 uppercase mt-2 border-b border-slate-100 pb-1">5. КОНФІДЕНЦІЙНІСТЬ ТА ЗАХИСТ ДАНИХ</p>
                <p>5.1. Завантажені Користувачем документи обробляються виключно для надання послуг (налаштування контексту для вашого акаунту) і не передаються третім особам.</p>
                <p>5.2. Авторизуючись на Сайті, Користувач надає згоду на обробку його персональних даних (Email, ім'я, аватар) відповідно до Закону України «Про захист персональних даних».</p>

                <p className="font-bold text-slate-800 uppercase mt-2 border-b border-slate-100 pb-1">6. ВАРТІСТЬ ПОСЛУГ ТА ПОРЯДОК РОЗРАХУНКІВ</p>
                <p>6.1. Вартість тарифних планів публікується в кабінеті Користувача. Оплата здійснюється через інтегровані платіжні системи (Monobank Acquiring / LiqPay тощо).</p>
                <p>6.2. Списані кошти за надані послуги поверненню не підлягають.</p>

                <p className="font-bold text-slate-800 uppercase mt-2 border-b border-slate-100 pb-1">7. РЕКВІЗИТИ ВИКОНАВЦЯ</p>
                <p>
                  Адміністрація Deep Research Hub<br />
                  E-mail: support@deep-research-hub.com
                </p>

                <div className="border-t border-slate-100 pt-3 text-[10px] text-slate-400">
                  Повний текст договору оферти з реквізитами доступний за посиланням: <a href="/offer.html" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] underline font-bold hover:text-[#1E3A8A]/80">відкрити в новій вкладці</a>.
                </div>
              </div>
              <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
                <button 
                  onClick={() => {
                    setAcceptedOffer(true);
                    try {
                      localStorage.setItem('acceptedOffer', 'true');
                    } catch (err) {
                      console.error(err);
                    }
                    setShowOfferModal(false);
                  }}
                  className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-bold text-[10px] uppercase tracking-wider rounded-lg transition-all shadow-md cursor-pointer"
                >
                  {t.offerModalAcceptBtn}
                </button>
                <button 
                  onClick={() => setShowOfferModal(false)}
                  className="px-2.5 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-[10px] uppercase tracking-wider rounded-lg transition-all cursor-pointer"
                >
                  {t.offerModalCloseBtn}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 📱 MOBILE STICKY FLOATING CTA BAR (CRO QUICK WIN Q3) */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-md border-t border-slate-700/80 p-2.5 px-4 shadow-2xl flex items-center justify-between gap-3">
          <div className="text-left">
            <p className="text-[10px] font-mono font-black text-emerald-400 uppercase tracking-wide flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping shrink-0" />
              <span>{globalLanguage === 'uk' ? '1-й запуск: 0 грн' : globalLanguage === 'pl' ? '1. test: 0 PLN' : '1st run: 100% Free'}</span>
            </p>
            <p className="text-[9px] text-slate-400 font-mono">
              {globalLanguage === 'uk' ? 'Без картки • Без пароля' : globalLanguage === 'pl' ? 'Bez karty • Bez hasła' : 'No card • Instant access'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById('login-card-section');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
              } else {
                window.location.href = '/auth/google';
              }
            }}
            className="py-2 px-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg flex items-center gap-1.5 cursor-pointer active:scale-95 transition-all shrink-0"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
            <span>{globalLanguage === 'uk' ? 'Спробувати за 1 клік →' : globalLanguage === 'pl' ? 'Wypróbuj w 1 klik →' : 'Try in 1-Click →'}</span>
          </button>
        </div>

        {/* Deep Brain OS Showcase & License Checkout Modal for Public Visitors */}
        <DeepBrainOsShowcaseModal
          isOpen={showDeepBrainOsModal}
          onClose={() => setShowDeepBrainOsModal(false)}
          language={globalLanguage}
          userEmail={authData?.user?.email || ''}
          initialTab={deepBrainOsInitialTab}
        />
      </div>
    );
  }

  if (authData.status === 'pending') {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center font-sans px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 shadow-[0_10px_30px_rgba(30,58,138,0.05)] text-center space-y-6">
          <div className="mx-auto bg-amber-500 w-14 h-14 flex items-center justify-center rounded-2xl shadow-[0_4px_12px_rgba(245,158,11,0.25)] animate-pulse">
            <RefreshCw className="w-7 h-7 text-white font-extrabold" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-black text-slate-800 tracking-tight uppercase">
              Очікування <span className="text-amber-500 font-mono">Схвалення</span>
            </h1>
            <p className="text-xs text-slate-400 font-mono">ВАШ ЗАПИТ НА ДОСТУП НАДІСЛАНО</p>
          </div>
          
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 text-left space-y-3 font-mono text-[11px] text-slate-600 leading-relaxed">
            <p className="font-bold text-amber-605">Привіт, {user?.displayName}!</p>
            <p>
              Ваш акаунт <span className="underline">{user?.email}</span> наразі очікує схвалення адміністратором екосистеми.
            </p>
            <p>
              Як тільки адміністратор підтвердить ваш доступ, ви зможете користуватися всіма можливостями аналітичних модулів. Будь ласка, зачекайте або зверніться до адміністратора.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={checkAuth}
              className="flex-1 py-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all shadow-xs cursor-pointer flex items-center justify-center gap-1.5"
            >
              <RefreshCw className="w-4 h-4" />
              Оновити статус
            </button>
            <a
              href="/auth/logout"
              className="px-4 py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 font-bold text-xs uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center"
            >
              Вийти
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (authData.status === 'rejected') {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center font-sans px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 shadow-[0_10px_30px_rgba(30,58,138,0.05)] text-center space-y-6">
          <div className="mx-auto bg-red-500 w-14 h-14 flex items-center justify-center rounded-2xl shadow-[0_4px_12px_rgba(239,68,68,0.25)]">
            <AlertCircle className="w-7 h-7 text-white font-extrabold" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-black text-slate-800 tracking-tight uppercase">
              Доступ <span className="text-red-500 font-mono">Обмежено</span>
            </h1>
            <p className="text-xs text-slate-400 font-mono">ВХІД ВІДХИЛЕНО АБО ЗАБЛОКОВАНО</p>
          </div>
          
          <div className="p-4 bg-red-50/50 rounded-lg border border-red-100 text-left space-y-3 font-mono text-[11px] text-red-800 leading-relaxed">
            <p className="font-bold">Шановний користувач,</p>
            <p>
              Адміністратор заблокував або відхилив доступ для вашого акаунту <span className="underline">{user?.email}</span>.
            </p>
            <p>
              Якщо ви вважаєте, що це помилка, будь ласка, зв'яжіться з адміністратором системи безпосередньо.
            </p>
          </div>

          <a
            href="/auth/logout"
            className="flex items-center justify-center gap-2 w-full py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 font-bold text-xs uppercase tracking-wider rounded-lg transition-all cursor-pointer"
          >
            Повернутися на сторінку входу
          </a>
        </div>
      </div>
    );
  }

  if (authData.status === 'approved' && authData.need2FA) {
    const isSetup = !authData.hasPassword;

    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center font-sans px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 shadow-[0_10px_30px_rgba(30,58,138,0.05)] space-y-6 text-center">
          
          {/* Header */}
          <div className="space-y-4">
            <div className="mx-auto bg-[#1E3A8A] w-14 h-14 flex items-center justify-center rounded-2xl shadow-[0_4px_12px_rgba(30,58,138,0.2)]">
              <ShieldCheck className="w-7 h-7 text-amber-400" />
            </div>
            <div className="space-y-1">
              <h1 className="text-lg font-black text-slate-800 tracking-tight uppercase">
                {isSetup ? (
                  <>Встановлення <span className="text-red-500 font-mono">Пароля</span></>
                ) : (
                  <>Введення <span className="text-red-500 font-mono">Пароля</span></>
                )}
              </h1>
              <p className="text-[10px] text-slate-400 font-mono">SECOND FACTOR AUTHENTICATION REQUIRED</p>
            </div>
          </div>

          {/* Verification / Setup form */}
          <form onSubmit={isSetup ? handleSetupPassword : handleVerifyPassword} className="space-y-5 text-left">
            {isSetup ? (
              // First time setup
              <div className="space-y-4 font-mono text-[11px]">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded leading-relaxed text-amber-800 space-y-1">
                  <p className="font-bold uppercase tracking-wider">Перше налаштування пароля</p>
                  <p>Адміністратор вимагає додатковий пароль для вашого акаунту. Будь ласка, створіть його зараз:</p>
                </div>

                <div className="space-y-2">
                  <label className="block text-slate-500 font-bold uppercase tracking-wider text-[10px]">Введіть новий пароль (мін. 4 символи):</label>
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoFocus
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:border-[#1E3A8A]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-slate-500 font-bold uppercase tracking-wider text-[10px]">Підтвердіть пароль:</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:border-[#1E3A8A]"
                  />
                </div>
              </div>
            ) : (
              // Just regular verification
              <div className="space-y-4 font-mono text-[11px]">
                <p className="text-slate-500 leading-relaxed">
                  Будь ласка, введіть пароль другого фактора, встановлений для вашого акаунта <span className="font-semibold underline text-slate-700">{user?.email}</span>.
                </p>

                <div className="space-y-2">
                  <label className="block text-slate-500 font-bold uppercase tracking-wider text-[10px]">Ваш пароль другого фактора:</label>
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoFocus
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:border-[#1E3A8A]"
                  />
                </div>
              </div>
            )}

            {passwordError && (
              <div className="p-2 bg-red-50 text-red-500 rounded border border-red-200 text-[10px] font-mono font-semibold flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{passwordError}</span>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={isVerifyingPassword}
                className="flex-1 py-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-all shadow-xs cursor-pointer flex items-center justify-center gap-1.5"
              >
                {isVerifyingPassword ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                {isSetup ? 'Встановити пароль' : 'Підтвердити вхід'}
              </button>
              <a
                href="/auth/logout"
                className="px-4 py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 font-bold text-xs uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center"
              >
                Скасувати
              </a>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC] text-slate-700 flex flex-col font-sans select-none w-full max-w-full overflow-x-hidden">
      
      {/* High Density Header */}
      <header className="min-h-14 py-2 border-b border-slate-200 flex items-center justify-between px-3 sm:px-6 bg-white sticky top-0 z-40 print:hidden w-full flex-wrap gap-2">
        <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => { handleNewResearch(); setActiveTab('hub'); }}>
          <div className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-950 w-9 h-9 flex items-center justify-center rounded-xl border border-blue-400/40 shadow-md shadow-blue-900/30">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <Zap className="w-3 h-3 text-amber-400 absolute -bottom-0.5 -right-0.5 animate-pulse" />
          </div>
          <div>
            <h1 className="text-xs sm:text-sm font-extrabold text-slate-900 tracking-tight uppercase flex items-center gap-1.5">
              <span>DEEP RESEARCH HUB</span>
              <span className="px-1.5 py-0.5 text-[8px] bg-emerald-100 text-emerald-800 border border-emerald-300 rounded font-mono">PRO V3</span>
            </h1>
            <p className="text-[10px] text-slate-500 font-mono font-semibold">Центр Автоматизації Рутини & Захисту Бізнесу</p>
          </div>
        </div>

        {/* 👤 Universal Top-Right Account & Balance Cockpit (Visible on ALL screens: Mobile & Desktop) */}
        {user && (
          <div className="flex items-center gap-1.5 sm:gap-2.5 ml-auto shrink-0 order-2 md:order-last">
            {/* 🪙 High-Contrast Balance & Quick Top-Up */}
            {user.balance !== undefined && (
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-100 border border-emerald-300 px-2 sm:px-2.5 py-1 rounded-xl shadow-xs">
                <Coins className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-emerald-600 shrink-0 animate-pulse" />
                <div className="flex flex-col text-left leading-tight">
                  <span className="text-[8px] text-slate-500 font-mono font-bold uppercase hidden sm:inline">Баланс</span>
                  <span className="text-[11px] sm:text-xs font-mono font-black text-emerald-900 whitespace-nowrap">
                    {formatPriceWithCurrency(user.balance, currency)}
                  </span>
                </div>
                <button 
                  type="button"
                  id="main-header-topup-btn"
                  onClick={() => setShowTopUpModal(true)}
                  className="ml-0.5 sm:ml-1 px-2 py-0.5 sm:py-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-[9px] sm:text-[10px] font-mono font-black uppercase tracking-wider transition-all flex items-center gap-0.5 shadow-sm active:scale-95 cursor-pointer"
                  title="Поповнити рахунок через Monobank (Apple Pay / Google Pay / Картка)"
                >
                  <Plus className="w-2.5 sm:w-3 h-2.5 sm:h-3 text-white font-extrabold" />
                  <span>{globalLanguage === 'pl' ? 'Doładuj' : globalLanguage === 'en' ? 'Top up' : 'Поповнити'}</span>
                </button>
              </div>
            )}

            {/* 👤 User Avatar / Name (desktop) */}
            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg">
              {user.avatar ? (
                <img src={user.avatar} alt={user.displayName} className="w-5 h-5 rounded-full border border-slate-200" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold bg-slate-200 text-slate-600">
                  {user.displayName?.charAt(0) || 'U'}
                </div>
              )}
              <span className="text-[11px] font-bold text-slate-700 max-w-[100px] truncate">{user.displayName}</span>
            </div>

            {/* 🚪 Universal Logout Button */}
            <a 
              href="/auth/logout" 
              className="px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs font-black font-mono rounded-lg transition-all flex items-center gap-1 shadow-sm active:scale-95 bg-slate-100 hover:bg-red-50 text-slate-700 hover:text-red-600 border border-slate-300 cursor-pointer shrink-0"
              title="Вийти з акаунта"
            >
              <LogOut className="w-3.5 h-3.5 text-slate-500 hover:text-red-500" />
              <span>ВИХІД</span>
            </a>
          </div>
        )}

        {/* 🔀 DUAL-PORTAL WORKSPACE SWITCHER */}
        <div className="flex items-center my-0.5">
          <WorkspaceSwitcher
            currentWorkspace={activeWorkspace}
            onSwitchWorkspace={handleSwitchWorkspace}
            language={globalLanguage}
            enterpriseName={user?.enterprise_name}
          />
        </div>

        {/* Header Navigation Options */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            {/* Jurisdiction Switcher */}
            <div className="hidden lg:flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-mono font-bold flex-wrap">
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('UA')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'UA' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Юрисдикція: Україна (ПКУ, ДСТУ, ДБН, Prozorro)"
              >
                🇺🇦 UA
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('PL')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'PL' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurysdykcja: Polska (Kodeks cywilny, KSeF/VAT-PL, CIT, TED)"
              >
                🇵🇱 PL
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('DE')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'DE' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurisdiction: Germany (BGB, HGB, XRechnung, VOB/B, LkSG)"
              >
                🇩🇪 DE
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('FR')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'FR' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurisdiction: France (Code de commerce, Factur-X, URSSAF, RE2020)"
              >
                🇫🇷 FR
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('IT')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'IT' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurisdiction: Italy (Codice Civile, SDI / FatturaPA, Codice Appalti)"
              >
                🇮🇹 IT
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('ES')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'ES' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurisdiction: Spain (Código Civil, FacturaE / Veri*Factu, LCSP)"
              >
                🇪🇸 ES
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('PT')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'PT' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurisdiction: Portugal (Código Comercial, SAF-T (PT), CCP)"
              >
                🇵🇹 PT
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('SE')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'SE' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurisdiction: Sweden (AB 04 / ABT 06, PEPPOL, Skatteverket, LOU)"
              >
                🇸🇪 SE
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('EU')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'EU' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurisdiction: European Union (EU Law, FIDIC, GDPR, TED)"
              >
                🇪🇺 EU
              </button>
              <button
                type="button"
                onClick={() => handleSelectJurisdiction('US')}
                className={`px-1.5 py-0.5 rounded-md transition-all cursor-pointer ${
                  jurisdiction === 'US' ? 'bg-amber-500 text-slate-950 shadow-xs font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Jurisdiction: United States (DGCL, FAR, IRS, OFAC, SOC 2)"
              >
                🇺🇸 US
              </button>
            </div>

            {/* Language Switcher */}
            <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-mono font-bold flex-wrap">
              <Languages className="w-3 h-3 text-indigo-600 ml-1 mr-0.5" />
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('uk')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'uk' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                UA
              </button>
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('de')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'de' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                DE
              </button>
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('fr')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'fr' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('pl')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'pl' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                PL
              </button>
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('en')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'en' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('it')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'it' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                IT
              </button>
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('es')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'es' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                ES
              </button>
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('pt')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'pt' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                PT
              </button>
              <button
                type="button"
                onClick={() => handleSwitchGlobalLanguage('sv')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  globalLanguage === 'sv' ? 'bg-indigo-600 text-white font-black' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                SV
              </button>
            </div>

            {/* Direct Currency Switcher */}
            <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-mono font-bold flex-wrap" title="Вибір валюти / Currency / Waluta">
              <Coins className="w-3 h-3 text-emerald-600 ml-1 mr-0.5" />
              <button
                type="button"
                onClick={() => handleSelectCurrency('UAH')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  currency === 'UAH' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Гривня (₴ UAH)"
              >
                ₴ UAH
              </button>
              <button
                type="button"
                onClick={() => handleSelectCurrency('USD')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  currency === 'USD' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="US Dollar ($ USD)"
              >
                $ USD
              </button>
              <button
                type="button"
                onClick={() => handleSelectCurrency('EUR')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  currency === 'EUR' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Euro (€ EUR)"
              >
                € EUR
              </button>
              <button
                type="button"
                onClick={() => handleSelectCurrency('PLN')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  currency === 'PLN' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Polski Złoty (zł PLN)"
              >
                zł PLN
              </button>
              <button
                type="button"
                onClick={() => handleSelectCurrency('SEK')}
                className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                  currency === 'SEK' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Svensk krona (kr SEK)"
              >
                kr SEK
              </button>
            </div>

            {/* Desktop App Store Modal Button */}
            <button
              type="button"
              onClick={() => setShowDesktopStoreModal(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[10px] font-mono font-bold shadow-xs transition-all cursor-pointer border border-slate-700 hover:border-indigo-400"
              title="Встановлення легкої програми на ПК (~18 МБ)"
            >
              <Monitor className="w-3.5 h-3.5 text-indigo-400" />
              <span>{globalLanguage === 'pl' ? '💻 Pulpit PC (~18 MB)' : globalLanguage === 'en' ? '💻 Desktop App (~18 MB)' : '💻 Десктоп ПК (~18 МБ)'}</span>
            </button>
          </div>
            
            {user && (
              <div className="flex items-center gap-2 border-l border-slate-200 pl-3">
                {/* Situational Center & Live Radar Maps Button */}
                <button
                  onClick={() => {
                    if (activeTab !== 'hub') {
                      handleNewResearch();
                      setUseEnterpriseContext(false);
                      setActiveTab('hub');
                    }
                    setTimeout(() => {
                      const el = document.getElementById('main-page-situational-maps');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        setActiveTab('situational-center');
                      }
                    }, 50);
                  }}
                  className={`px-2.5 py-1 rounded-md text-xs font-bold font-mono transition-all flex items-center gap-1.5 cursor-pointer shadow-xs border ${
                    activeTab === 'situational-center'
                      ? 'bg-cyan-600 text-white border-cyan-400 shadow-cyan-500/20'
                      : 'bg-slate-900 hover:bg-slate-800 text-cyan-400 border-cyan-500/30'
                  }`}
                  title="Оперативний Ситуаційний Центр & Карти Загроз (FIRMS, РЕБ, Sentinel-2)"
                >
                  <Globe className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="hidden sm:inline">
                    {globalLanguage === 'pl' ? 'Mapy i Radar' : globalLanguage === 'en' ? 'Radar Maps' : 'Карти & Радар'}
                  </span>
                </button>

                {/* Fast-Track Кодифікатор ОВТ МОУ & Brave1 Button */}
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('military-codifier');
                  }}
                  className={`px-2.5 py-1 rounded-md text-xs font-bold font-mono transition-all flex items-center gap-1.5 cursor-pointer shadow-xs border ${
                    activeTab === 'military-codifier'
                      ? 'bg-amber-600 text-white border-amber-400 shadow-amber-500/20'
                      : 'bg-slate-900 hover:bg-slate-800 text-amber-400 border-amber-500/30'
                  }`}
                  title="Fast-Track Кодифікатор ОВТ (Brave1 / МОУ за Постановою № 345)"
                >
                  <Shield className="w-3.5 h-3.5 text-amber-400" />
                  <span className="hidden sm:inline">
                    {globalLanguage === 'pl' ? 'Kodyfikator MON' : globalLanguage === 'en' ? 'MoD Codifier' : 'Кодифікатор ОВТ'}
                  </span>
                </button>

                {/* 🏛️ НЦУВКЗ-ФІНКОНТУР: Закритий Чат Головбухів */}
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('ncuvkz-fin-messenger');
                  }}
                  className={`px-2.5 py-1 rounded-md text-xs font-bold font-mono transition-all flex items-center gap-1.5 cursor-pointer shadow-xs border ${
                    activeTab === 'ncuvkz-fin-messenger'
                      ? 'bg-emerald-600 text-white border-emerald-400 shadow-emerald-500/20'
                      : 'bg-slate-900 hover:bg-slate-800 text-emerald-400 border-emerald-500/30'
                  }`}
                  title="🏛️ НЦУВКЗ-ФІНКОНТУР: Закритий відомчий комунікатор головних бухгалтерів НЦУВКЗ та 6 філій"
                >
                  <Building2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="hidden sm:inline">
                    Фінконтур НЦУВКЗ
                  </span>
                </button>

                {/* Unified Corporate Digital Office Button */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      handleNewResearch();
                      setActiveTab('enterprise-dashboard');
                      setTimeout(() => {
                        const el = document.getElementById("enterprise-brain-console-root");
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 150);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-black font-mono transition-all flex items-center gap-1.5 cursor-pointer shadow-md border ${
                      activeTab === 'enterprise-dashboard' || activeTab === 'enterprise-brain'
                        ? 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white border-violet-300 shadow-violet-500/30'
                        : 'bg-violet-950/80 hover:bg-violet-900 text-violet-200 border-violet-500/50 hover:border-violet-400'
                    }`}
                    title="Корпоративний Цифровий Офіс Підприємства (Чат, WIKI, генерація документів Word за ДСТУ 4163:2020, 40+ модулів, ЄДРПОУ, первинні акти)"
                  >
                    <Building2 className="w-4 h-4 text-amber-300" />
                    <span className="font-extrabold whitespace-nowrap">
                      {user?.enterprise_name 
                        ? `🏢 ${user.enterprise_name}`
                        : (globalLanguage === 'pl' ? '🏢 Korporacyjne Biuro' : globalLanguage === 'en' ? '🏢 Corporate Office' : '🏢 Корпоративний Цифровий Офіс')}
                    </span>
                    <span className="bg-amber-400 text-slate-950 text-[9px] font-black px-1.5 py-0.2 rounded-full uppercase ml-1 animate-pulse shrink-0">
                      3 дні Free
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowOfficeInfoModal(true)}
                    className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-violet-300 hover:text-white border border-violet-500/40 transition-colors cursor-pointer"
                    title="Опис та можливості Корпоративного Цифрового Офісу (ℹ️ Як це працює)"
                  >
                    <Info className="w-4 h-4 text-amber-300" />
                  </button>
                </div>

                {(user.role === 'admin' || ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(user?.email?.toLowerCase() || '')) && (
                  <>
                    <button
                      onClick={() => {
                        handleNewResearch();
                        setActiveTab('trembita-mesh');
                      }}
                      className={`px-2.5 py-1 rounded-md text-xs font-bold font-mono transition-all flex items-center gap-1.5 cursor-pointer shadow-xs border ${
                        activeTab === 'trembita-mesh'
                          ? 'bg-red-600 text-white border-red-500 shadow-red-500/20'
                          : 'bg-slate-900 hover:bg-slate-800 text-red-400 border-red-500/30'
                      }`}
                      title="Тактичний проєкт ППО «Трембіта-МЕШ» (Тільки для адміністраторів)"
                    >
                      <Radar className="w-3.5 h-3.5 text-red-400" />
                      <span className="hidden sm:inline">Трембіта-МЕШ</span>
                    </button>
                    <button
                      onClick={() => {
                        handleNewResearch();
                        setActiveTab('admin-assistant');
                      }}
                      className={`px-2.5 py-1 rounded-md text-xs font-bold font-mono transition-all flex items-center gap-1.5 cursor-pointer shadow-xs border ${
                        activeTab === 'admin-assistant'
                          ? 'bg-emerald-600 text-white border-emerald-500 shadow-emerald-500/20'
                          : 'bg-slate-900 hover:bg-slate-800 text-emerald-400 border-emerald-500/30'
                      }`}
                      title="ШІ-Асистент Розробника Antigravity Core (Консоль завдань, файлів та Google Drive)"
                    >
                      <Bot className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="hidden sm:inline">
                        {globalLanguage === 'pl' ? 'Asystent AI' : globalLanguage === 'en' ? 'AI Assistant' : 'ШІ-Асистент'}
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        handleNewResearch();
                        setActiveTab('settings');
                      }}
                      className={`p-1 rounded border transition-all cursor-pointer ${
                        activeTab === 'settings'
                          ? 'bg-red-50 border-red-200 text-red-500'
                          : 'border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50'
                      }`}
                      title="Налаштування API"
                    >
                      <Settings className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            )}

            {activeSession && activeSession.status === 'completed' && (
              <button
                onClick={handleNewResearch}
                className="px-3 py-1 bg-[#1E3A8A] text-white font-bold rounded text-[10px] tracking-wider transition-all hover:bg-[#1E3A8A]/90 uppercase font-mono cursor-pointer"
              >
                НОВИЙ_ЗАПИТ
              </button>
            )}
          </div>
        </header>

      {/* Main Workspace Layout */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-2 sm:p-4 md:p-6 lg:p-8 flex flex-col gap-4 sm:gap-6 md:gap-8 relative min-w-0">
        
        {/* Active Workspace */}
        <div className="w-full min-w-0 space-y-4 sm:space-y-6">
          
          {/* 1. If Error occurred before initializing state */}
          {globalError && !activeSession && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex gap-4 items-start shadow-xs">
              <AlertCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
              <div className="space-y-1.5 flex-1 min-w-0">
                <h4 className="text-xs font-bold font-mono uppercase tracking-wider text-red-600">
                  {globalLanguage === 'uk' ? 'Збій координатора пошуку' : 'Search Coordinator Failure'}
                </h4>
                <p className="text-[11px] text-red-700 leading-relaxed font-mono bg-red-50/50 p-3 rounded border border-red-100">
                  {globalError}
                </p>
                <button
                  onClick={handleNewResearch}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-[11px] font-mono font-bold uppercase rounded transition-colors cursor-pointer"
                >
                  {globalLanguage === 'uk' ? 'Почати заново' : 'Start Over'}
                </button>
              </div>
            </div>
          )}
 
          {/* Tab Navigation Switches */}
          {activeTab !== 'hub' && !activeSession && !isSearching && (
            <div className="flex border-b border-slate-200 gap-1 overflow-x-auto print:hidden font-mono text-[10px] font-bold items-center pb-2">
              <button
                onClick={() => {
                  handleNewResearch();
                  setUseEnterpriseContext(false);
                  setActiveTab('hub');
                }}
                className="py-1.5 px-3 bg-slate-900 hover:bg-slate-800 text-emerald-400 rounded border border-slate-700 transition-all cursor-pointer whitespace-nowrap uppercase flex items-center gap-1.5 font-bold shadow-xs mr-2"
              >
                <span>🏠 {globalLanguage === 'uk' ? 'На головну' : globalLanguage === 'pl' ? 'Strona główna' : 'Home'}</span>
              </button>

              {(() => {
                const centerInfo = getCenterInfoForAgent(activeTab);
                if (centerInfo && activeTab !== centerInfo.id) {
                  return (
                    <button
                      onClick={() => {
                        handleNewResearch();
                        setUseEnterpriseContext(false);
                        setActiveTab(centerInfo.id as any);
                      }}
                      className="py-1.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded border border-slate-300 transition-all cursor-pointer whitespace-nowrap uppercase flex items-center gap-1.5 font-bold shadow-xs mr-3"
                    >
                      <ArrowLeft className="w-3.5 h-3.5 text-blue-600" />
                      <span>{globalLanguage === 'uk' ? `← До ${centerInfo.nameUk}` : globalLanguage === 'pl' ? `← Wróć do ${centerInfo.nameUk}` : `← Back to ${centerInfo.nameEn}`}</span>
                    </button>
                  );
                }
                return null;
              })()}

              {(activeTab === 'research' || activeTab === 'analyzer' || activeTab === 'templates' || activeTab === 'archive' || activeTab === 'settings') && (
                <>
                  <button
                    onClick={() => {
                      handleNewResearch();
                      setActiveTab('research');
                    }}
                    className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                      activeTab === 'research'
                        ? 'border-red-500 text-red-500 font-extrabold'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    КООРДИНАТОР ПОШУКУ
                  </button>
                  <button
                    onClick={() => {
                      handleNewResearch();
                      setActiveTab('analyzer');
                    }}
                    className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                      activeTab === 'analyzer'
                        ? 'border-red-500 text-red-500 font-extrabold'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    АНАЛІЗАТОР ДАНИХ
                  </button>
                  <button
                    onClick={() => {
                      handleNewResearch();
                      setActiveTab('templates');
                    }}
                    className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                      activeTab === 'templates'
                        ? 'border-red-500 text-red-500 font-extrabold'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    КОНСТРУКТОР ШАБЛОНІВ
                  </button>
                  <button
                    onClick={() => {
                      handleNewResearch();
                      setActiveTab('archive');
                    }}
                    className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                      activeTab === 'archive'
                        ? 'border-red-500 text-red-500 font-extrabold'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    АРХІВ ДОСЛІДЖЕНЬ
                  </button>
                </>
              )}
              {activeTab === 'corrector' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('corrector');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'corrector'
                      ? 'border-teal-500 text-teal-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  КОРЕКТОР ДОКУМЕНТІВ
                </button>
              )}
              {activeTab === 'koshtorisnyk' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('koshtorisnyk');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'koshtorisnyk'
                      ? 'border-blue-500 text-blue-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  КОШТОРИСНИК
                </button>
              )}
              {activeTab === 'video-renderer' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('video-renderer');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'video-renderer'
                      ? 'border-green-500 text-green-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  МЕДІА-РЕНДЕРЕР
                </button>
              )}
               {activeTab === 'contract-auditor' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('contract-auditor');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'contract-auditor'
                      ? 'border-emerald-500 text-emerald-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  ЮРИДИЧНИЙ АУДИТОР
                </button>
              )}
              {activeTab === 'aml-auditor' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('aml-auditor');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'aml-auditor'
                      ? 'border-red-500 text-red-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  AML & Санкційний Аудитор
                </button>
              )}
              {activeTab === 'tender-analyst' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('tender-analyst');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'tender-analyst'
                      ? 'border-amber-500 text-amber-600 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Тендерний Асистент
                </button>
              )}
              {activeTab === 'legal-doc-creator' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('legal-doc-creator');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'legal-doc-creator'
                      ? 'border-purple-500 text-purple-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Конструктор документів
                </button>
              )}
              {activeTab === 'court-compliance' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('court-compliance');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'court-compliance'
                      ? 'border-red-500 text-red-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Судовий комплаєнс
                </button>
              )}
              {activeTab === 'dstu-order-constructor' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('dstu-order-constructor');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'dstu-order-constructor'
                      ? 'border-blue-500 text-blue-605 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Діловодство за ДСТУ
                </button>
              )}
              {activeTab === 'hr-compliance' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('hr-compliance');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'hr-compliance'
                      ? 'border-emerald-500 text-emerald-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Кадровий комплаєнс & HR
                </button>
              )}
              {activeTab === 'commercial-contract-auditor' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('commercial-contract-auditor');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'commercial-contract-auditor'
                      ? 'border-emerald-500 text-emerald-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Аудит комерційних договорів
                </button>
              )}
              {(activeTab === 'enterprise-dashboard' || activeTab === 'enterprise-brain') && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('enterprise-dashboard');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'enterprise-dashboard' || activeTab === 'enterprise-brain'
                      ? 'border-violet-500 text-violet-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {globalLanguage === 'pl' ? '🏢 KORPORACYJNE BIURO CYFROWE' : globalLanguage === 'en' ? '🏢 CORPORATE DIGITAL OFFICE' : '🏢 КОРПОРАТИВНИЙ ЦИФРОВИЙ ОФІС'}
                </button>
              )}
              {activeTab === 'tax-financial-analyst' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('tax-financial-analyst');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'tax-financial-analyst'
                      ? 'border-amber-500 text-amber-600 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Консультант з Податків ПКУ
                </button>
              )}
              {activeTab === 'market-intelligence' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('market-intelligence');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'market-intelligence'
                      ? 'border-red-500 text-red-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Конкурентна розвідка
                </button>
              )}
              {activeTab === 'grant-writer' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('grant-writer');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'grant-writer'
                      ? 'border-blue-500 text-blue-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Пошук та написання грантів
                </button>
              )}
              {activeTab === 'customs-compliance' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('customs-compliance');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'customs-compliance'
                      ? 'border-emerald-500 text-emerald-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Митний комплаєнс & ЗЕД
                </button>
              )}
              {activeTab === 'ip-guard' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('ip-guard');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'ip-guard'
                      ? 'border-purple-500 text-purple-650 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Реєстрація ТМ та авторського права
                </button>
              )}
              {activeTab === 'it-auditor' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('it-auditor');
                  }}
                  className={`py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                    activeTab === 'it-auditor'
                      ? 'border-blue-500 text-blue-600 font-extrabold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  IT-аудит та DevOps
                </button>
              )}
              {activeTab === 'agri-land-consultant' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('agri-land-consultant');
                  }}
                  className="py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase border-emerald-500 text-emerald-650 font-extrabold"
                >
                  Земельний & Агро Консультант
                </button>
              )}
              {activeTab === 'public-accountant-kekv' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('public-accountant-kekv');
                  }}
                  className="py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase border-blue-500 text-blue-600 font-extrabold"
                >
                  Держ-Бухгалтер & КЕКВ Аудитор
                </button>
              )}
              {activeTab === 'esg-eco-auditor' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('esg-eco-auditor');
                  }}
                  className="py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase border-teal-500 text-teal-600 font-extrabold"
                >
                  ESG & Екологічний Аудитор
                </button>
              )}
              {activeTab === 'property-valuator' && (
                <button
                  onClick={() => {
                    handleNewResearch();
                    setActiveTab('property-valuator');
                  }}
                  className="py-2 px-3.5 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase border-amber-500 text-amber-400 font-extrabold"
                >
                  Оцінювач майна & Аналітик ринку
                </button>
              )}
            </div>
          )}
 
          {/* 2. State routing panel */}
          {!activeSession ? (
            activeTab === 'situational-center' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                  <button
                    onClick={() => {
                      setUseEnterpriseContext(false);
                      setActiveTab('hub');
                    }}
                    className="py-1.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg border border-slate-300 transition-all cursor-pointer text-xs font-bold flex items-center gap-1.5 shadow-xs"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 text-blue-600" />
                    <span>{globalLanguage === 'uk' ? '← На головну до Хабу' : globalLanguage === 'pl' ? '← Wróć do Centrum' : '← Back to Hub'}</span>
                  </button>
                  <div className="flex items-center gap-2 text-xs font-mono text-cyan-600 font-bold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                    <span>{globalLanguage === 'uk' ? '📡 Супутникові дані NASA & Sentinel-2 онлайн' : '📡 Satellite feeds live'}</span>
                  </div>
                </div>
                <SituationalCenterShowcase 
                  language={globalLanguage} 
                  onSelectAgent={(agentId) => handleLaunchAgentWithContext(agentId)}
                  onOpenPricing={() => setShowPublicPricingModal(true)}
                  isLoggedIn={true}
                  currentUserEmail={user?.email}
                />
              </div>
            ) : activeTab === 'military-codifier' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                  <button
                    onClick={() => {
                      setUseEnterpriseContext(false);
                      setActiveTab('hub');
                    }}
                    className="py-1.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg border border-slate-300 transition-all cursor-pointer text-xs font-bold flex items-center gap-1.5 shadow-xs"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 text-amber-600" />
                    <span>{globalLanguage === 'uk' ? '← На головну до Хабу' : globalLanguage === 'pl' ? '← Wróć do Centrum' : '← Back to Hub'}</span>
                  </button>
                  <div className="flex items-center gap-2 text-xs font-mono text-amber-600 font-bold">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
                    <span>{globalLanguage === 'uk' ? '🏛️ МОУ & BRAVE1 CODIFIER ОНЛАЙН' : '🏛️ MOD & BRAVE1 CODIFIER ONLINE'}</span>
                  </div>
                </div>
                <MilitaryCodifierAgentView />
              </div>
            ) : activeTab === 'hub' ? (
              <HubView 
                onLaunchAgent={(target, preferredLang) => {
                  handleLaunchAgentWithContext(target, preferredLang);
                }} 
                language={globalLanguage} 
                currentUserEmail={user?.email || ''}
                hasEnterprise={!!user?.enterprise_id}
                enterpriseName={user?.enterprise_name || (user?.enterprise_id === 'c54dc9c1-00e1-4620-b922-4d6572f9662b' ? 'НЦУВКЗ' : '')}
                isEnterpriseAdmin={!!user?.is_enterprise_admin || user?.role === 'admin'}
                allowedEnterpriseAgents={Array.isArray(user?.allowed_enterprise_agents) ? user.allowed_enterprise_agents : []}
                pinnedEnterpriseAgents={Array.isArray(user?.pinned_enterprise_agents) ? user.pinned_enterprise_agents : []}
                onTogglePinAgent={handleTogglePinAgent}
                onOpenEnterpriseDashboard={() => {
                  handleNewResearch();
                  setActiveTab('enterprise-dashboard');
                  setTimeout(() => {
                    const el = document.getElementById("enterprise-brain-console-root");
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 150);
                }}
                onOpenCorporateOffice={() => {
                  handleNewResearch();
                  setActiveTab('enterprise-dashboard');
                  setTimeout(() => {
                    const el = document.getElementById("enterprise-brain-console-root");
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 150);
                }}
                onOpenCorporateOfficeInfo={() => setShowOfficeInfoModal(true)}
                onOpenPricing={() => setShowPublicPricingModal(true)}
                currency={currency}
              />
            ) : (activeTab === 'research' || activeTab === 'deep-research') ? (
              renderAgentWithSubscription('deep-research', (
                <ResearchForm 
                  onSubmit={handleStartResearch} 
                  isSearching={isSearching} 
                  templates={templates}
                  activeTemplateId={activeTemplateId}
                  onChangeActiveTemplateId={setActiveTemplateId}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'analyzer' ? (
              <DataAnalyzer
                activeTemplate={templates.find(t => t.id === activeTemplateId) || templates[0]}
                onReportGenerated={handleReportGeneratedFromData}
                language={globalLanguage}
              />
            ) : activeTab === 'templates' ? (
              <TemplateManager
                templates={templates}
                onChangeTemplates={saveTemplates}
                onReset={handleResetTemplates}
                language={globalLanguage}
              />
            ) : activeTab === 'settings' ? (
              (user?.role === 'admin' || ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(user?.email?.toLowerCase() || '')) ? (
                <SettingsView language={globalLanguage} />
              ) : (
                <div className="bg-white border border-slate-200 rounded-lg p-8 text-center space-y-4 font-mono shadow-xs">
                  <h3 className="text-sm font-bold text-red-500 uppercase">Доступ обмежено</h3>
                  <p className="text-xs text-slate-500">Тільки адміністратори можуть змінювати налаштування API.</p>
                </div>
              )
            ) : activeTab === 'corrector' ? (
              renderAgentWithSubscription('corrector', (
                <DocumentCorrector
                  language={globalLanguage}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('office-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'koshtorisnyk' ? (
              renderAgentWithSubscription('koshtorisnyk', (
                <div className="space-y-4 font-mono">
                  <div className="flex items-center justify-between gap-4 bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setUseEnterpriseContext(false);
                          setActiveTab('hub');
                        }}
                        className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
                      >
                        🏠 {globalLanguage === 'uk' ? 'На головну' : 'Home'}
                      </button>
                      <button
                        onClick={() => {
                          setUseEnterpriseContext(false);
                          setActiveTab('construction-center-hub');
                        }}
                        className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
                      >
                        ← {globalLanguage === 'uk' ? 'До Будівельного Центру' : 'Back to Construction Hub'}
                      </button>
                    </div>
                    <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-sky-500/20 text-sky-300 border border-sky-400/30 rounded-md">
                      ✨ Gemini 3.7 Flash • ДБН / РЕКН
                    </span>
                  </div>
                  <div className="w-full h-[calc(100vh-16rem)] bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs relative">
                    <iframe
                      src={`https://koshtoris.ai-assistent.online?email=${encodeURIComponent(user?.email || '')}`}
                      className="w-full h-full border-none"
                      title="Кошторисник"
                    />
                  </div>
                </div>
              ))
            ) : activeTab === 'video-renderer' ? (
              renderAgentWithSubscription('video-renderer', (
                <VideoRenderer
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('office-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                />
              ))
            ) : activeTab === 'eu-counterparty-auditor' ? (
              renderAgentWithSubscription('eu-counterparty-auditor', (
                <EuCounterpartyAuditor
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'contract-auditor' ? (
              renderAgentWithSubscription('contract-auditor', (
                <ContractAuditor
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'aml-auditor' ? (
              renderAgentWithSubscription('contract-auditor', (
                <AmlAuditor
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'tender-analyst' ? (
              renderAgentWithSubscription('contract-auditor', (
                <TenderAnalyst
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'tender-package-generator' ? (
              renderAgentWithSubscription('contract-auditor', (
                <TenderPackageGeneratorView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'legal-doc-creator' ? (
              renderAgentWithSubscription('contract-auditor', (
                <LegalDocCreator
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'legislation-tracker' ? (
              renderAgentWithSubscription('contract-auditor', (
                <LegislationTracker
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'court-compliance' ? (
              renderAgentWithSubscription('contract-auditor', (
                <CourtCompliance
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'dstu-order-constructor' ? (
              renderAgentWithSubscription('contract-auditor', (
                <EnterpriseBlankDocumentAgentView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('office-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  currentUserEmail={user?.email}
                  currency={user?.currency || 'UAH'}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'ncuvkz-universal-consultant' ? (
              renderAgentWithSubscription('contract-auditor', (
                <NcuvkzUniversalConsultantView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('enterprise-dashboard');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  currentUserEmail={user?.email}
                  currency={user?.currency || 'UAH'}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                  enterpriseId={user?.enterprise_id || "c54dc9c1-00e1-4620-b922-4d6572f9662b"}
                />
              ))
            ) : activeTab === 'ncuvkz-fin-messenger' ? (
              <NcuvkzFinMessengerView
                onBack={() => {
                  setUseEnterpriseContext(false);
                  setActiveTab('hub');
                }}
                currentUserEmail={user?.email}
              />
            ) : activeTab === 'hr-compliance' ? (
              renderAgentWithSubscription('contract-auditor', (
                <HrCompliance
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'commercial-contract-auditor' ? (
              renderAgentWithSubscription('contract-auditor', (
                <CommercialContractAuditor
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : (activeTab === 'office-portal' || activeTab === 'enterprise-brain' || activeTab === 'enterprise-dashboard') ? (
              <React.Suspense fallback={<PortalLoadingSkeleton theme="office" />}>
                <OfficePortalLayout
                  user={user}
                  language={globalLanguage}
                  onLanguageChange={(lang) => {
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else if (lang === 'en') {
                      setJurisdiction('EU');
                      setCurrency('EUR');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onPromptToChat={(promptText) => {
                    handleStartResearch(promptText, { agentId: 'treasury-findocuments-auditor' } as any);
                  }}
                  onLaunchAgent={(target) => {
                    setUseEnterpriseContext(true);
                    setActiveTab(target);
                  }}
                  onBackToMain={() => setActiveTab('hub')}
                  onSwitchToSpace={() => handleSwitchWorkspace('space')}
                  onOpenOfficeInfo={() => setShowOfficeInfoModal(true)}
                  onOpenTopUp={() => setShowTopUpModal(true)}
                  initialOfficeSubTab="dashboard"
                />
              </React.Suspense>
            ) : activeTab === 'space-portal' ? (
              <React.Suspense fallback={<PortalLoadingSkeleton theme="space" />}>
                <SpacePortalLayout
                  user={user}
                  language={globalLanguage}
                  currency={currency}
                  onLaunchAgent={(agentId) => handleLaunchAgentWithContext(agentId)}
                  onBackToMain={() => setActiveTab('hub')}
                  onSwitchToOffice={() => handleSwitchWorkspace('office')}
                  onOpenPricing={() => setShowPublicPricingModal(true)}
                  onOpenTopUp={() => setShowTopUpModal(true)}
                  onNavigateToLogin={() => { window.location.href = '/auth/google'; }}
                  initialSpaceSubTab="situational"
                />
              </React.Suspense>
            ) : activeTab === 'tax-pn-unblocker-auditor' ? (
              renderAgentWithSubscription('contract-auditor', (
                <TaxPnUnblockerAgentView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('tax-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  currency={currency}
                  currentUserEmail={user?.email}
                />
              ))
            ) : TAX_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <TaxAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else if (lang === 'en') {
                      setJurisdiction('EU');
                      setCurrency('EUR');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('tax-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'dynamic-on-the-fly-agent' && activeDynamicAgent ? (
              <DynamicOnTheFlyAgentView
                agent={activeDynamicAgent}
                language={getEffectiveAgentLanguage('dynamic-on-the-fly-agent')}
                currentUserEmail={user?.email}
                onBackToHub={() => {
                  setUseEnterpriseContext(false);
                  const backTarget = activeHubContext ? activeHubContext : 'hub';
                  setActiveTab(backTarget as any);
                }}
                onGoHome={() => {
                  setUseEnterpriseContext(false);
                  setActiveHubContext(null);
                  setAgentLanguageOverride(null);
                  setActiveTab('hub');
                }}
                hubTitle={activeHubContext ? 'Хаб' : undefined}
              />
            ) : activeTab === 'dstu-pkmu-auditor' ? (
              renderAgentWithSubscription('dstu-pkmu-auditor', (
                <DelovodstvoAuditor
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext ? activeHubContext : 'office-center-hub';
                    setActiveHubContext(null);
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : (BUSINESS_AGENT_IDS.includes(activeTab) || (agentsConfig as any[]).some((a: any) => a.id === activeTab)) ? (
              renderAgentWithSubscription('contract-auditor', (
                <BusinessAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else if (lang === 'en') {
                      setJurisdiction('EU');
                      setCurrency('EUR');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'energy-circuit-schematic-bom-designer' ? (
              renderAgentWithSubscription('contract-auditor', (
                <CircuitSchematicAgentView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('energy-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                />
              ))
            ) : ENERGY_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <EnergyAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('energy-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : AGRI_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <AgriAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('agri-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : LOGISTICS_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <LogisticsAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('logistics-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : CYBER_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <CyberAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('cyber-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                />
              ))
            ) : CONSTRUCTION_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <ConstructionAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('construction-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                />
              ))
            ) : activeTab === 'pharma-advisor' ? (
              renderAgentWithSubscription('contract-auditor', (
                <PharmaAdvisorAgentView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('pharma-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  currentUserEmail={user?.email}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                  currency={currency}
                />
              ))
            ) : PHD_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <PhdAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('phd-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                />
              ))
            ) : ((SCIENCE_AGENT_IDS.includes(activeTab) || PHARMA_AGENT_IDS.includes(activeTab)) && activeTab !== 'deep-research') ? (
              renderAgentWithSubscription('contract-auditor', (
                <ScienceAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab(PHARMA_AGENT_IDS.includes(activeTab) ? 'pharma-center-hub' : 'science-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                />
              ))
            ) : POLAND_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription(activeTab, (
                <BusinessAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else if (lang === 'en') {
                      setJurisdiction('EU');
                      setCurrency('EUR');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext('poland-center-hub');
                    setAgentLanguageOverride('pl');
                    setGlobalLanguage('pl');
                    setJurisdiction('PL');
                    setCurrency('PLN');
                    setActiveTab('poland-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
                        ) : GERMANY_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription(activeTab, (
                <BusinessAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'de' || lang === 'en' || lang === 'fr') {
                      setJurisdiction('DE');
                      setCurrency('EUR');
                    } else if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext('germany-center-hub');
                    setAgentLanguageOverride('de');
                    setGlobalLanguage('de');
                    setJurisdiction('DE');
                    setCurrency('EUR');
                    setActiveTab('germany-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : FRANCE_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription(activeTab, (
                <BusinessAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'en') {
                      setJurisdiction('FR');
                      setCurrency('EUR');
                    } else if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext('france-center-hub');
                    setAgentLanguageOverride('en');
                    setGlobalLanguage('en');
                    setJurisdiction('FR');
                    setCurrency('EUR');
                    setActiveTab('france-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : US_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription(activeTab, (
                <BusinessAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'en') {
                      setJurisdiction('US');
                      setCurrency('USD');
                    } else if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext('us-center-hub');
                    setAgentLanguageOverride('en');
                    setGlobalLanguage('en');
                    setJurisdiction('US');
                    setCurrency('USD');
                    setActiveTab('us-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : EU_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription(activeTab, (
                <BusinessAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'en') {
                      setJurisdiction('EU');
                      setCurrency('EUR');
                    } else if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext('eu-center-hub');
                    setAgentLanguageOverride('en');
                    setGlobalLanguage('en');
                    setJurisdiction('EU');
                    setCurrency('EUR');
                    setActiveTab('eu-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : CROSS_BORDER_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription(activeTab, (
                <BusinessAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext('cross-border-center-hub');
                    setActiveTab('cross-border-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'market-intelligence' ? (
              renderAgentWithSubscription('contract-auditor', (
                <MarketIntelligence
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'sales-product-optimizer' ? (
              renderAgentWithSubscription('contract-auditor', (
                <SalesProductOptimizer
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'grant-writer' ? (
              renderAgentWithSubscription('contract-auditor', (
                <GrantWriter
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'customs-compliance' ? (
              renderAgentWithSubscription('contract-auditor', (
                <CustomsCompliance
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('logistics-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'ip-guard' ? (
              renderAgentWithSubscription('contract-auditor', (
                <IpGuard
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'it-auditor' ? (
              renderAgentWithSubscription('contract-auditor', (
                <ItAuditor
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('cyber-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'startup-pitch' ? (
              renderAgentWithSubscription('contract-auditor', (
                <StartupPitchBuilder
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('business-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'news-analyst' ? (
              renderAgentWithSubscription('news-analyst', (
                <NewsAnalyst
                  user={user}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBackToHub={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('office-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                />
              ))
            ) : activeTab === 'agri-land-consultant' ? (
              renderAgentWithSubscription('contract-auditor', (
                <AgriLandConsultant
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('agri-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : (activeTab === 'public-accountant-kekv' || activeTab === 'budget-request-constructor') ? (
              renderAgentWithSubscription('contract-auditor', (
                <BudgetDraftingAgentView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('tax-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  currentUserEmail={user?.email}
                  currency={user?.currency || 'UAH'}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'esg-eco-auditor' ? (
              renderAgentWithSubscription('contract-auditor', (
                <EsgEcoAuditor
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('science-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'doc-translator' ? (
              renderAgentWithSubscription('doc-translator', (
                <DocumentTranslator
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('office-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'property-valuator' ? (
              renderAgentWithSubscription('property-valuator', (
                <PropertyValuator
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('construction-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'md-docx-converter' ? (
              renderAgentWithSubscription('md-docx-converter', (
                <MdToDocxConverter
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('office-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'satellite-eco-analyst' ? (
              renderAgentWithSubscription('contract-auditor', (
                <SatelliteEcoAnalyst
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('space-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : SPACE_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <SpaceSatAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('space-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'office-spreadsheet-agent' ? (
              renderAgentWithSubscription('office-spreadsheet-agent', (
                <OfficeSpreadsheetAgentView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('office-center-hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'meeting-transcriber' ? (
              renderAgentWithSubscription('meeting-transcriber', (
                <MeetingTranscriberView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext ? activeHubContext : 'office-center-hub';
                    setActiveHubContext(null);
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  currentUserEmail={user?.email}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'admin-center-hub' ? (
              <AdminCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currentUserEmail={user?.email}
                currency={currency}
              />
            ) : activeTab === 'trembita-mesh' ? (
              (user?.role === 'admin' || ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(user?.email?.toLowerCase() || '')) ? (
                <AdminTrembitaMeshProject
                  onBack={() => setActiveTab('admin-assistant')}
                  language={globalLanguage}
                  currentUserEmail={user?.email}
                />
              ) : (
                <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-8 text-center space-y-4 font-mono max-w-lg mx-auto my-12 text-white shadow-2xl">
                  <h3 className="text-sm font-bold text-red-400 uppercase">
                    {globalLanguage === 'pl' ? '⛔ Dostęp ograniczony' : globalLanguage === 'en' ? '⛔ Access Restricted' : '⛔ Доступ обмежено'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {globalLanguage === 'pl' 
                      ? 'Ten taktyczny projekt obrony przeciwlotniczej jest dostępny wyłącznie dla administratora systemu.' 
                      : globalLanguage === 'en' 
                      ? 'This tactical air defense project is restricted to platform administrators.' 
                      : 'Цей тактичний проєкт ППО доступний виключно для адміністратора системи.'}
                  </p>
                  <button 
                    onClick={() => setActiveTab('hub')} 
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer border border-slate-700"
                  >
                    {globalLanguage === 'pl' ? 'Powrót do Centrum' : globalLanguage === 'en' ? 'Back to Hub' : 'Повернутися до Центру'}
                  </button>
                </div>
              )
            ) : activeTab === 'admin-assistant' ? (
              (user?.role === 'admin' || ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(user?.email?.toLowerCase() || '')) ? (
                <AdminAssistantConsole
                  onBack={() => setActiveTab('admin-center-hub')}
                  language={globalLanguage}
                  currentUserEmail={user?.email}
                  onOpenTrembitaMesh={() => setActiveTab('trembita-mesh')}
                />
              ) : (
                <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-8 text-center space-y-4 font-mono max-w-lg mx-auto my-12 text-white shadow-2xl">
                  <h3 className="text-sm font-bold text-red-400 uppercase">
                    {globalLanguage === 'pl' ? '⛔ Dostęp ograniczony' : globalLanguage === 'en' ? '⛔ Access Restricted' : '⛔ Доступ обмежено'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {globalLanguage === 'pl' 
                      ? 'Ten terminal jest dostępny wyłącznie dla administratora systemu.' 
                      : globalLanguage === 'en' 
                      ? 'This terminal is restricted to platform administrators.' 
                      : 'Цей термінал доступний виключно для адміністратора системи.'}
                  </p>
                  <button 
                    onClick={() => setActiveTab('hub')} 
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer border border-slate-700"
                  >
                    {globalLanguage === 'pl' ? '← Strona główna' : globalLanguage === 'en' ? '← Home' : '← На головну'}
                  </button>
                </div>
              )
            ) : activeTab === 'phd-center-hub' ? (
              <PhdCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currentUserEmail={user?.email}
                currency={currency}
              />
            ) : activeTab === 'executive-project-orchestrator' ? (
              renderAgentWithSubscription('executive-project-orchestrator', (
                <BusinessAgentView
                  agentId="executive-project-orchestrator"
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else if (lang === 'en') {
                      setJurisdiction('EU');
                      setCurrency('EUR');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext || 'admin-center-hub';
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'global-foresight-strategist' ? (
              renderAgentWithSubscription('global-foresight-strategist', (
                <BusinessAgentView
                  agentId="global-foresight-strategist"
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                       setJurisdiction('PL');
                       setCurrency('PLN');
                    } else if (lang === 'en') {
                       setJurisdiction('EU');
                       setCurrency('EUR');
                    } else {
                       setJurisdiction('UA');
                       setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext || 'admin-center-hub';
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'nis-eco-situational-analyst' ? (
              renderAgentWithSubscription('nis-eco-situational-analyst', (
                <BusinessAgentView
                  agentId="nis-eco-situational-analyst"
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                       setJurisdiction('PL');
                       setCurrency('PLN');
                    } else if (lang === 'en') {
                       setJurisdiction('EU');
                       setCurrency('EUR');
                    } else {
                       setJurisdiction('UA');
                       setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext || 'admin-center-hub';
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'nis-eco-pro-orchestrator' ? (
              renderAgentWithSubscription('nis-eco-pro-orchestrator', (
                <BusinessAgentView
                  agentId="nis-eco-pro-orchestrator"
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                       setJurisdiction('PL');
                       setCurrency('PLN');
                    } else if (lang === 'en') {
                       setJurisdiction('EU');
                       setCurrency('EUR');
                    } else {
                       setJurisdiction('UA');
                       setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext || 'admin-center-hub';
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'gcsk-infrasound-air-defense-coordinator' ? (
              renderAgentWithSubscription('gcsk-infrasound-air-defense-coordinator', (
                <BusinessAgentView
                  agentId="gcsk-infrasound-air-defense-coordinator"
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                       setJurisdiction('PL');
                       setCurrency('PLN');
                    } else if (lang === 'en') {
                       setJurisdiction('EU');
                       setCurrency('EUR');
                    } else {
                       setJurisdiction('UA');
                       setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext || 'admin-center-hub';
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'gcsk-infrasound-spectral-auditor' ? (
              renderAgentWithSubscription('gcsk-infrasound-spectral-auditor', (
                <BusinessAgentView
                  agentId="gcsk-infrasound-spectral-auditor"
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                       setJurisdiction('PL');
                       setCurrency('PLN');
                    } else if (lang === 'en') {
                       setJurisdiction('EU');
                       setCurrency('EUR');
                    } else {
                       setJurisdiction('UA');
                       setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext || 'admin-center-hub';
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'gcsk-infrasound-blast-analyst' ? (
              renderAgentWithSubscription('gcsk-infrasound-blast-analyst', (
                <BusinessAgentView
                  agentId="gcsk-infrasound-blast-analyst"
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                       setJurisdiction('PL');
                       setCurrency('PLN');
                    } else if (lang === 'en') {
                       setJurisdiction('EU');
                       setCurrency('EUR');
                    } else {
                       setJurisdiction('UA');
                       setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext || 'admin-center-hub';
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'nonverbal-profiler' ? (
              renderAgentWithSubscription('contract-auditor', (
                <NonverbalProfilerAgentView
                  language={getEffectiveAgentLanguage(activeTab)}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    setActiveTab('admin-center-hub');
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setActiveTab('hub');
                  }}
                  currentUserEmail={user?.email}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))

            ) : activeTab === 'office-center-hub' ? (
              <OfficeCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currentUserEmail={user?.email}
                currency={currency}
              />
            ) : activeTab === 'space-center-hub' ? (
              <SpaceCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currentUserEmail={user?.email}
                currency={currency}
              />
            ) : activeTab === 'tax-center-hub' ? (
              <TaxCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currentUserEmail={user?.email}
                currency={currency}
              />
            ) : activeTab === 'business-center-hub' ? (
              <BusinessCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currentUserEmail={user?.email}
                currency={currency}
              />
            ) : activeTab === 'energy-center-hub' ? (
              <EnergyCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currentUserEmail={user?.email}
                currency={currency}
              />
            ) : activeTab === 'agri-center-hub' ? (
              <AgriCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currentUserEmail={user?.email}
                currency={currency}
              />
            ) : activeTab === 'agro-deep-research-hub' ? (
              <AgroDeepResearchHubView
                onBack={() => setActiveTab('agri-center-hub')}
                language={globalLanguage}
              />
            ) : activeTab === 'logistics-center-hub' ? (
              <LogisticsCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currency={currency}
              />
            ) : activeTab === 'cyber-center-hub' ? (
              <CyberCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currency={currency}
              />
            ) : activeTab === 'construction-center-hub' ? (
              <ConstructionCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currency={currency}
              />
            ) : activeTab === 'science-center-hub' ? (
              <ScienceCenterHubView
                onLaunchAgent={(target) => {
                  if (target === 'innovation-hub') {
                    setActiveHubContext('science-center-hub');
                  }
                  setActiveTab(target as any);
                }}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currency={currency}
              />
                        ) : activeTab === 'eco-center-hub' ? (
              <EcoCenterHubView
                onLaunchAgent={(target) => {
                  setActiveHubContext('eco-center-hub');
                  setActiveTab(target as any);
                }}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currency={currency}
                currentUserEmail={user?.email}
              />
            ) : ECO_AGENT_IDS.includes(activeTab) ? (
              renderAgentWithSubscription('contract-auditor', (
                <BusinessAgentView
                  agentId={activeTab as any}
                  language={getEffectiveAgentLanguage(activeTab)}
                  onLanguageChange={(lang) => {
                    setAgentLanguageOverride(lang);
                    setGlobalLanguage(lang);
                    if (lang === 'pl') {
                      setJurisdiction('PL');
                      setCurrency('PLN');
                    } else if (lang === 'en') {
                      setJurisdiction('EU');
                      setCurrency('EUR');
                    } else {
                      setJurisdiction('UA');
                      setCurrency('UAH');
                    }
                  }}
                  onBack={() => {
                    setUseEnterpriseContext(false);
                    const backTarget = activeHubContext || 'eco-center-hub';
                    setActiveTab(backTarget as any);
                  }}
                  onGoHome={() => {
                    setUseEnterpriseContext(false);
                    setActiveHubContext(null);
                    setAgentLanguageOverride(null);
                    setGlobalLanguage('uk');
                    setJurisdiction('UA');
                    setCurrency('UAH');
                    setActiveTab('hub');
                  }}
                  useEnterpriseContext={useEnterpriseContext}
                  enterpriseName={user?.enterprise_name}
                />
              ))
            ) : activeTab === 'pharma-center-hub' ? (
              <PharmaCenterHubView
                onLaunchAgent={(target) => setActiveTab(target as any)}
                onBack={() => setActiveTab('hub')}
                language={globalLanguage}
                currency={currency}
              />
            ) : activeTab === 'poland-center-hub' ? (
              <PolandCenterHubView
                onLaunchAgent={(target, preferredLang) => {
                  handleLaunchAgentWithContext(target, preferredLang || 'pl', 'poland-center-hub');
                }}
                onBack={() => {
                  setActiveHubContext(null);
                  setAgentLanguageOverride(null);
                  setGlobalLanguage('uk');
                  setJurisdiction('UA');
                  setCurrency('UAH');
                  setActiveTab('hub');
                }}
                language={globalLanguage}
                currency={currency}
                currentUserEmail={user?.email}
                onSelectCurrency={handleSelectCurrency}
                onSelectLanguage={(lang) => {
                  setGlobalLanguage(lang);
                  setAgentLanguageOverride(lang);
                  if (lang === 'pl') {
                    setJurisdiction('PL');
                    setCurrency('PLN');
                  } else if (lang === 'en') {
                    setJurisdiction('EU');
                    setCurrency('EUR');
                  } else {
                    setJurisdiction('UA');
                    setCurrency('UAH');
                  }
                }}
              />
                        ) : activeTab === 'germany-center-hub' ? (
              <GermanyCenterHubView
                onLaunchAgent={(target, preferredLang) => {
                  handleLaunchAgentWithContext(target, preferredLang || 'de', 'germany-center-hub');
                }}
                onBack={() => {
                  setActiveHubContext(null);
                  setAgentLanguageOverride(null);
                  setGlobalLanguage('uk');
                  setJurisdiction('UA');
                  setCurrency('UAH');
                  setActiveTab('hub');
                }}
                language={agentLanguageOverride || 'de'}
                currency={currency}
                currentUserEmail={user?.email}
                onSelectCurrency={handleSelectCurrency}
                onSelectLanguage={(lang) => {
                  setGlobalLanguage(lang);
                  setAgentLanguageOverride(lang);
                  if (lang === 'de' || lang === 'en' || lang === 'fr') {
                    setJurisdiction('DE');
                    setCurrency('EUR');
                  } else if (lang === 'pl') {
                    setJurisdiction('PL');
                    setCurrency('PLN');
                  } else {
                    setJurisdiction('UA');
                    setCurrency('UAH');
                  }
                }}
              />
            ) : activeTab === 'france-center-hub' ? (
              <FranceCenterHubView
                onLaunchAgent={(target, preferredLang) => {
                  handleLaunchAgentWithContext(target, preferredLang || 'fr', 'france-center-hub');
                }}
                onBack={() => {
                  setActiveHubContext(null);
                  setAgentLanguageOverride(null);
                  setGlobalLanguage('uk');
                  setJurisdiction('UA');
                  setCurrency('UAH');
                  setActiveTab('hub');
                }}
                language={agentLanguageOverride || 'fr'}
                currency={currency}
                currentUserEmail={user?.email}
                onSelectCurrency={handleSelectCurrency}
                onSelectLanguage={(lang) => {
                  setGlobalLanguage(lang);
                  setAgentLanguageOverride(lang);
                  if (lang === 'fr' || lang === 'de' || lang === 'en') {
                    setJurisdiction('FR');
                    setCurrency('EUR');
                  } else if (lang === 'pl') {
                    setJurisdiction('PL');
                    setCurrency('PLN');
                  } else {
                    setJurisdiction('UA');
                    setCurrency('UAH');
                  }
                }}
              />
            ) : activeTab === 'us-center-hub' ? (
              <UsCenterHubView
                onLaunchAgent={(target, preferredLang) => {
                  handleLaunchAgentWithContext(target, preferredLang || 'en', 'us-center-hub');
                }}
                onBack={() => {
                  setActiveHubContext(null);
                  setAgentLanguageOverride(null);
                  setGlobalLanguage('uk');
                  setJurisdiction('UA');
                  setCurrency('UAH');
                  setActiveTab('hub');
                }}
                language={globalLanguage}
                currency={currency}
                currentUserEmail={user?.email}
                onSelectCurrency={handleSelectCurrency}
                onSelectLanguage={(lang) => {
                  setGlobalLanguage(lang);
                  setAgentLanguageOverride(lang);
                  if (lang === 'en') {
                    setJurisdiction('US');
                    setCurrency('USD');
                  } else if (lang === 'pl') {
                    setJurisdiction('PL');
                    setCurrency('PLN');
                  } else {
                    setJurisdiction('UA');
                    setCurrency('UAH');
                  }
                }}
              />
            ) : activeTab === 'eu-center-hub' ? (
              <EuCenterHubView
                onLaunchAgent={(target, preferredLang) => {
                  handleLaunchAgentWithContext(target, preferredLang || 'en', 'eu-center-hub');
                }}
                onBack={() => {
                  setActiveHubContext(null);
                  setAgentLanguageOverride(null);
                  setGlobalLanguage('uk');
                  setJurisdiction('UA');
                  setCurrency('UAH');
                  setActiveTab('hub');
                }}
                language={globalLanguage}
                currency={currency}
                currentUserEmail={user?.email}
                onSelectCurrency={handleSelectCurrency}
                onSelectLanguage={(lang) => {
                  setGlobalLanguage(lang);
                  setAgentLanguageOverride(lang);
                  if (lang === 'en') {
                    setJurisdiction('EU');
                    setCurrency('EUR');
                  } else if (lang === 'pl') {
                    setJurisdiction('PL');
                    setCurrency('PLN');
                  } else {
                    setJurisdiction('UA');
                    setCurrency('UAH');
                  }
                }}
              />
            ) : activeTab === 'cross-border-center-hub' ? (
              <CrossBorderCenterHubView
                onLaunchAgent={(target, preferredLang) => {
                  handleLaunchAgentWithContext(target, preferredLang || globalLanguage || 'uk', 'cross-border-center-hub');
                }}
                onBack={() => {
                  setActiveHubContext(null);
                  setActiveTab('hub');
                }}
                language={globalLanguage}
                currency={currency}
                currentUserEmail={user?.email}
                onSelectCurrency={handleSelectCurrency}
                onSelectLanguage={(lang) => {
                  setGlobalLanguage(lang);
                  setAgentLanguageOverride(lang);
                }}
              />
            ) : activeTab === 'ukraine-center-hub' ? (
              <UkraineCenterHubView
                onLaunchAgent={(target, preferredLang) => {
                  handleLaunchAgentWithContext(target, preferredLang || 'uk', 'ukraine-center-hub');
                }}
                onBack={() => {
                  setActiveHubContext(null);
                  setActiveTab('hub');
                }}
                language={globalLanguage}
                currency={currency}
                currentUserEmail={user?.email}
                onSelectCurrency={handleSelectCurrency}
                onSelectLanguage={(lang) => {
                  setGlobalLanguage(lang);
                  setAgentLanguageOverride(lang);
                }}
              />
            ) : activeTab === 'folk-healer-traditions' ? (
              <FolkHealerAgentView
                language={getEffectiveAgentLanguage(activeTab)}
                onBack={() => {
                  setUseEnterpriseContext(false);
                  setActiveHubContext(null);
                  setAgentLanguageOverride(null);
                  setActiveTab('hub');
                }}
              />
            ) : activeTab === 'innovation-hub' ? (
              <InnovationHubView
                language={globalLanguage}
                onBackToHub={() => {
                  setUseEnterpriseContext(false);
                  const backTarget = activeHubContext ? activeHubContext : 'science-center-hub';
                  setActiveHubContext(null);
                  setActiveTab(backTarget as any);
                }}
              />
            ) : activeTab === 'admin-lead-harvester' ? (
              <AdminLeadHarvester
                user={user}
                language={globalLanguage}
                onBackToHub={() => {
                  setUseEnterpriseContext(false);
                  setActiveTab('hub');
                }}
              />
            ) : (
              <ArchiveView
                sessions={sessions}
                activeSessionId={activeSession?.id || null}
                onSelectSession={(id) => {
                  handleSelectSession(id);
                  setActiveTab('research');
                }}
                onDeleteSession={handleDeleteSession}
                onClearAll={handleClearAllSessions}
                language={globalLanguage}
              />
            )
          ) : activeSession.status === 'failed' ? (
            // Research failed
            <div className="bg-white border border-slate-200 rounded-lg p-8 space-y-6 shadow-xs">
              <div className="flex gap-4 items-start pb-6 border-b border-slate-100">
                <div className="p-3 bg-red-50 rounded border border-red-200 text-red-500">
                  <AlertCircle className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-bold font-mono uppercase tracking-wider text-slate-800">
                    {globalLanguage === 'uk' ? 'Помилка глибокого дослідження' : 'Deep Research Error'}
                  </h3>
                  <p className="text-[11px] text-slate-400 font-mono">
                    {globalLanguage === 'uk' ? 'Процес пошуку був перерваний сервером.' : 'The research execution process was interrupted by the server.'}
                  </p>
                </div>
              </div>
 
              <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                  {globalLanguage === 'uk' ? 'Деталі помилки:' : 'Error details:'}
                </p>
                <div className="p-4 bg-red-50/50 rounded border border-red-100 text-[11px] font-mono text-red-600 leading-relaxed whitespace-pre-wrap">
                  {activeSession.error || 'Unknown server execution error.'}
                </div>
              </div>
 
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => handleStartResearch(activeSession.query, activeSession.options)}
                  className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white text-[11px] font-bold font-mono uppercase rounded transition-all shadow-xs cursor-pointer"
                >
                  {globalLanguage === 'uk' ? 'Спробувати знову' : 'Retry Research'}
                </button>
                <button
                  onClick={handleNewResearch}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold font-mono uppercase rounded border border-slate-200 transition-all cursor-pointer"
                >
                  {globalLanguage === 'uk' ? 'Новий пошук' : 'Cancel & New Search'}
                </button>
              </div>
            </div>
          ) : activeSession.status === 'completed' ? (
            // Research finished: render beautiful report document
            <ReportView 
              session={activeSession} 
              onNewResearch={handleNewResearch} 
              onBackToHub={() => {
                handleNewResearch();
                setActiveTab('hub');
              }}
            />
          ) : (
            // Active search is running: show timeline step-by-step
            <div className="space-y-6">
              {/* Back navigation only if we had an active report before starting */}
              <button
                onClick={handleNewResearch}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {globalLanguage === 'uk' ? 'Повернутися до панелі' : 'Back to Console'}
              </button>
              <ResearchProgress session={activeSession} />
            </div>
          )}

        </div>
      </main>

      {/* Top-up Balance Modal with Dynamic Multi-Currency Selector */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white border border-slate-200 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-5">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-50 p-2.5 rounded-xl border border-emerald-100 text-emerald-600">
                  <Coins className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold font-mono uppercase tracking-wider text-slate-800">
                    {globalLanguage === 'pl' ? 'Doładowanie salda' : globalLanguage === 'en' ? 'Top up balance' : 'Поповнення балансу'}
                  </h3>
                  <p className="text-[10px] text-slate-400 font-mono">
                    {globalLanguage === 'pl' ? 'BŁYSKAWICZNA PŁATNOŚĆ ONLINE (PLN / EUR / UAH)' : globalLanguage === 'en' ? 'INSTANT ONLINE CHECKOUT (EUR / PLN / UAH)' : 'МИТТЄВЕ ПОПОВНЕННЯ РАХУНКУ ОНЛАЙН'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowTopUpModal(false)}
                className="text-slate-400 hover:text-slate-600 font-mono text-xs cursor-pointer p-1 rounded-lg hover:bg-slate-100"
              >
                ✕
              </button>
            </div>

            {/* Currency Selector Tabs */}
            <div className="space-y-1.5">
              <label className="block text-slate-500 font-bold uppercase tracking-wider text-[10px] font-mono">
                {globalLanguage === 'pl' ? 'Wybierz walutę płatności:' : globalLanguage === 'en' ? 'Select payment currency:' : 'Оберіть валюту оплати:'}
              </label>
              <div className="grid grid-cols-3 gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    handleSelectCurrency('UAH');
                    setTopUpAmount('250');
                  }}
                  className={`py-2 text-xs font-bold font-mono rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    currency === 'UAH' ? 'bg-white text-emerald-700 shadow-sm border border-slate-200/60' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span>🇺🇦 UAH</span>
                  <span className="text-[10px] text-slate-400 font-normal">(₴)</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleSelectCurrency('PLN');
                    setTopUpAmount('50');
                  }}
                  className={`py-2 text-xs font-bold font-mono rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    currency === 'PLN' ? 'bg-white text-blue-700 shadow-sm border border-slate-200/60' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span>🇵🇱 PLN</span>
                  <span className="text-[10px] text-slate-400 font-normal">(zł)</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleSelectCurrency('EUR');
                    setTopUpAmount('15');
                  }}
                  className={`py-2 text-xs font-bold font-mono rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    currency === 'EUR' ? 'bg-white text-purple-700 shadow-sm border border-slate-200/60' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <span>🇪🇺 EUR</span>
                  <span className="text-[10px] text-slate-400 font-normal">(€)</span>
                </button>
              </div>
            </div>

            {/* Quick Amount Buttons */}
            <div className="space-y-1.5">
              <label className="block text-slate-500 font-bold uppercase tracking-wider text-[10px] font-mono">
                {globalLanguage === 'pl' ? 'Szybki wybór kwoty:' : globalLanguage === 'en' ? 'Quick amount presets:' : 'Швидкий вибір суми:'}
              </label>
              <div className="flex gap-2 flex-wrap">
                {(currency === 'PLN' 
                  ? ['25', '50', '100', '250', '500']
                  : currency === 'EUR'
                  ? ['5', '10', '25', '50', '100']
                  : ['100', '250', '500', '1000', '2500']
                ).map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setTopUpAmount(amt)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold font-mono border transition-all cursor-pointer ${
                      topUpAmount === amt
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                  >
                    {currency === 'EUR' ? `€${amt}` : currency === 'PLN' ? `${amt} zł` : `${amt} ₴`}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount Input */}
            <div className="space-y-1.5">
              <label className="block text-slate-500 font-bold uppercase tracking-wider text-[10px] font-mono">
                {globalLanguage === 'pl' ? `Wpisz kwotę (${currency}):` : globalLanguage === 'en' ? `Enter custom amount (${currency}):` : `Сума поповнення (${currency}):`}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  placeholder={currency === 'EUR' ? '15' : currency === 'PLN' ? '50' : '250'}
                  disabled={topUpLoading}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-4 pr-16 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:border-emerald-500 font-mono"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-mono text-xs font-extrabold bg-slate-200 px-2 py-0.5 rounded">
                  {currency === 'EUR' ? 'EUR €' : currency === 'PLN' ? 'PLN zł' : 'UAH ₴'}
                </span>
              </div>

              {currency !== 'UAH' && (
                <div className="text-[11px] font-mono text-slate-500 flex items-center justify-between pt-1">
                  <span>{globalLanguage === 'pl' ? 'Szacowane saldo:' : globalLanguage === 'en' ? 'Estimated credit:' : 'Буде зараховано на баланс:'}</span>
                  <span className="font-bold text-emerald-600 font-mono">
                    ≈ {currency === 'PLN' 
                      ? Math.round(parseFloat(topUpAmount || '0') * 10.5) 
                      : Math.round(parseFloat(topUpAmount || '0') * 45)} грн
                  </span>
                </div>
              )}
            </div>

            <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100 text-[10.5px] font-mono text-emerald-800 space-y-1 leading-relaxed">
              <p className="font-bold">
                {globalLanguage === 'pl' ? '💡 Do czego służy saldo?' : globalLanguage === 'en' ? '💡 What is the balance used for?' : '💡 Для чого потрібен баланс?'}
              </p>
              <p>
                {globalLanguage === 'pl'
                  ? 'Środki z salda pozwalają opłacać pojedyncze zapytania do dowolnego modułu bez konieczności zakupu pełnego abonamentu.'
                  : globalLanguage === 'en'
                  ? 'Balance funds allow paying for single runs across specialized modules without committing to a full monthly subscription.'
                  : 'Кошти на балансі дозволяють сплачувати разові запуски будь-яких аналітичних модулів без потреби купувати повну місячну підписку.'}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleInitiateTopUp}
                disabled={topUpLoading || !topUpAmount || parseFloat(topUpAmount) <= 0}
                className="flex-1 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md cursor-pointer flex items-center justify-center gap-1.5 font-mono"
              >
                {topUpLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                <span>
                  {globalLanguage === 'pl'
                    ? `Doładuj (${currency === 'EUR' ? `€${topUpAmount}` : currency === 'PLN' ? `${topUpAmount} zł` : `${topUpAmount} ₴`})`
                    : globalLanguage === 'en'
                    ? `Top up now (${currency === 'EUR' ? `€${topUpAmount}` : currency === 'PLN' ? `${topUpAmount} zł` : `${topUpAmount} ₴`})`
                    : `Поповнити (${currency === 'EUR' ? `€${topUpAmount}` : currency === 'PLN' ? `${topUpAmount} zł` : `${topUpAmount} ₴`})`}
                </span>
              </button>
              <button
                onClick={() => setShowTopUpModal(false)}
                disabled={topUpLoading}
                className="px-4 py-3.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 font-bold text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer font-mono"
              >
                {globalLanguage === 'pl' ? 'Anuluj' : globalLanguage === 'en' ? 'Cancel' : 'Скасувати'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Public Pricing Modal for Authenticated Dashboard */}
      <PublicPricingModal 
        isOpen={showPublicPricingModal} 
        onClose={() => setShowPublicPricingModal(false)} 
        initialCurrency={currency}
        language={globalLanguage}
      />

      {/* Desktop App Store & Module Manager Modal */}
      <DesktopAppStoreModal
        isOpen={showDesktopStoreModal}
        onClose={() => setShowDesktopStoreModal(false)}
        currentLang={globalLanguage}
      />

      {/* Corporate Digital Office Presentation & Info Modal */}
      <CorporateOfficeInfoModal
        isOpen={showOfficeInfoModal}
        onClose={() => setShowOfficeInfoModal(false)}
        onOpenOffice={() => {
          handleNewResearch();
          setActiveTab('enterprise-dashboard');
        }}
        language={globalLanguage}
      />

      {/* Deep Brain OS Showcase & License Checkout Modal */}
      <DeepBrainOsShowcaseModal
        isOpen={showDeepBrainOsModal}
        onClose={() => setShowDeepBrainOsModal(false)}
        language={globalLanguage}
        userEmail={user?.email || ''}
        initialTab={deepBrainOsInitialTab}
      />

    </div>
  );
}
